import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Heart, Plus, Search, Filter, User, Eye, Edit3, Trash2, 
  Calendar, AlertTriangle, CheckCircle, Clock, Activity, BadgeCheck,
  Pill, FileText, Users, MapPin, Phone, Mail, 
  ChevronRight, ChevronLeft, Grid, List, MoreVertical,
  Target, ClipboardList, BarChart3, Shield, Stethoscope,
  MessageSquare, Download, TrendingUp, Settings, Edit, RefreshCw, Tag, Building2,
  DollarSign, BookOpen, UserCheck, Lock, Star, Award, TrendingDown,
  Globe, Building, Droplets, Utensils, Home, Clipboard, ClipboardCheck, AlertCircle,
  PartyPopper, Music, X, BookUser, FolderOpen, FileStack, Scale
} from 'lucide-react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { getFilteredResidents } from '../../utils/helpers';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';
import ComplianceHistoryPanel from './ComplianceHistoryPanel';
import CrmComplianceProfileSection, { ComplianceSnapshotCard } from './CrmComplianceProfileSection';
import { ResidentProfileExportModule } from './ResidentProfileModule';
import OnboardingWorkflow from './OnboardingWorkflow';
import ANACCAssessment from '../Clinical/ANACCAssessment';
import AssessmentLibraryPanel from '../Clinical/AssessmentLibraryPanel';
import AssessmentTrendCharts from '../Clinical/AssessmentTrendCharts';
import CareMinutesService from '../../services/care/CareMinutesService';
import ANACCReassessmentService from '../../services/agedcare/ANACCReassessmentService';
import MARDashboard from '../Medication/MARDashboard';
import CarePlansComponent from '../CarePlans/CarePlans';
import { sirsData, qualityIncidents, auditData, careMinutesData } from '../../data/mockData';
import { buildComplianceHistory } from '../../utils/complianceHistory';
import {
  getPersistedAssessmentCards,
  appendInstance,
  quickFormPayloadToAssessmentCard,
} from '../../utils/clinicalAssessmentInstances';
import MandatoryAssessmentCoverageGrid from '../Clinical/MandatoryAssessmentCoverageGrid';
import { logDataAccess, logDataModification } from '../../utils/auditLogger';
import { isFeatureEnabled } from '../../utils/featureFlags';
import { useOnlineStatus } from '../../components/Common/OfflineIndicator';
import LeaveMovementService from '../../services/residents/LeaveMovementService';
import ResidentProfileService from '../../services/residents/ResidentProfileService';
import ResidentService from '../../services/residents/ResidentService';
import { ACTIONS } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';
import { useConfirm } from '../Common/ConfirmDialog';
import { TableSkeleton, ResidentProfileSkeleton } from '../Common/SkeletonLoaders';
import { Tooltip, EmptyState } from '../Common/ReusableUI';

// IDDSI Helper Functions
const getIDDSIFoodLabel = (level) => {
  const labels = {
    'Level 3': 'Liquidised',
    'Level 4': 'Pureed',
    'Level 5': 'Minced & Moisture',
    'Level 6': 'Soft & Bite-Sized',
    'Level 7': 'Regular'
  };
  return labels[level] || level;
};

const getIDDSIFluidLabel = (level) => {
  const labels = {
    'Level 0': 'Thin',
    'Level 1': 'Slightly Thick',
    'Level 2': 'Mildly Thick',
    'Level 3': 'Moderately Thick',
    'Level 4': 'Extremely Thick'
  };
  return labels[level] || level;
};

// Enhanced Resident Profile Component
const EnhancedResidentProfile = ({ resident, onClose, actions }) => {
  const { state, dispatch } = useAppContext();
  const { isAgedCare, supportsANACC } = useFacilityFeatures();
  const isOnline = useOnlineStatus();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const componentMountTime = useRef(Date.now());
  
  const [activeTab, setActiveTab] = useState('profile');
  const [medicationTab, setMedicationTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingMedication, setEditingMedication] = useState(null);
  const [showRefillModal, setShowRefillModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [careMinutesData, setCareMinutesData] = useState(null);
  const [loadingCareMinutes, setLoadingCareMinutes] = useState(false);
  const [residentSIRS, setResidentSIRS] = useState([]);
  const [residentQualityIncidents, setResidentQualityIncidents] = useState([]);
  const [docCategoryFilter, setDocCategoryFilter] = useState('all');
  const [docSearch, setDocSearch] = useState('');

  // CRUD modals
  const [showAddResidentModal, setShowAddResidentModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [showAddMedModal, setShowAddMedModal] = useState(false);
  const [showDocUploadModal, setShowDocUploadModal] = useState(false);
  const [dischargeTarget, setDischargeTarget] = useState(null);
  const [editProfileData, setEditProfileData] = useState({});
  const [addResidentForm, setAddResidentForm] = useState({
    name: '', dateOfBirth: '', room: '', ward: 'ward_a', facility: 'sunshine',
    medicare: '', careLevel: 'Low', gender: '', fundingType: 'AN-ACC',
    allergies: '', dietaryRestrictions: '', emergencyContactName: '', emergencyContactPhone: '',
  });
  const [addMedForm, setAddMedForm] = useState({
    medication: '', dosage: '', frequency: '', route: 'oral', prescriber: '',
    startDate: new Date().toISOString().split('T')[0], instructions: '',
  });
  const [docUploadForm, setDocUploadForm] = useState({
    name: '', type: 'Clinical', category: 'General', description: '',
  });
  const [editMedFormData, setEditMedFormData] = useState(null);
  const [discontinueMedTarget, setDiscontinueMedTarget] = useState(null);
  const [showDiscontinueMedModal, setShowDiscontinueMedModal] = useState(false);
  const [discontinueReason, setDiscontinueReason] = useState('');
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeDate, setDischargeDate] = useState(new Date().toISOString().split('T')[0]);

  // Leave & Movement state
  const [leaveData, setLeaveData] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showBedHoldModal, setShowBedHoldModal] = useState(false);
  const [activeLeaveRecord, setActiveLeaveRecord] = useState(null);
  const [activeTransferRecord, setActiveTransferRecord] = useState(null);
  const [leaveFormData, setLeaveFormData] = useState({
    type: 'Social Leave', startDate: '', endDate: '', expectedReturnDate: '',
    destination: '', notes: '', contactWhileOnLeave: '', authorisedBy: '', activateBedHold: false,
  });
  const [transferFormData, setTransferFormData] = useState({
    dateOut: '', hospital: '', reason: '', referringDoctor: '',
    ambulanceRequired: false, familyNotified: false, expectedReturn: '',
  });
  const [returnFormData, setReturnFormData] = useState({
    returnDate: '', returnNotes: '', clinicalChangesOnReturn: '',
    dischargeSummaryReceived: false, medicationChanges: '', followUpRequired: '',
  });
  const [leaveFormErrors, setLeaveFormErrors] = useState([]);
  const [leaveStats, setLeaveStats] = useState(null);
  
  // Memoize expensive profile data generation
  const profileData = useMemo(() => {
    if (!resident) return null;
    
    // Log data access for audit trail
    logDataAccess('resident', resident.id, ['profile_data'], resident.createdBy || 'system');
    
    return ResidentProfileExportModule.generateResidentProfileData(resident);
  }, [resident]);

  const [mergedAssessments, setMergedAssessments] = useState([]);

  useEffect(() => {
    if (resident == null) return;
    const base = ResidentProfileExportModule.generateResidentProfileData(resident).assessments || [];
    const extra = getPersistedAssessmentCards(resident.id);
    const seen = new Set(base.map((a) => a.id));
    const out = [...base];
    extra.forEach((c) => {
      if (c?.id && !seen.has(c.id)) {
        out.push(c);
        seen.add(c.id);
      }
    });
    setMergedAssessments(out);
  }, [resident]);

  const handleQuickFormSaved = useCallback(
    (payload) => {
      const card = quickFormPayloadToAssessmentCard(payload, resident);
      if (!card || resident == null) return;
      appendInstance(resident.id, payload, card);
      setMergedAssessments((prev) => [...prev, card]);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[EnhancedResidentProfile] quick form persisted', { residentId: resident.id, cardId: card.id });
      }
    },
    [resident]
  );

  const careHistoryDemo = useMemo(
    () => (resident?.id != null ? buildComplianceHistory(resident.id) : null),
    [resident?.id]
  );

  const clinicalPanel = useMemo(() => {
    const c = resident?.clinical || {};
    const structured = Array.isArray(c.allergies) ? c.allergies : [];
    const allergenKeys = new Set(
      structured.map((a) => String(a?.allergen || '').toLowerCase()).filter(Boolean)
    );
    const legacyRaw = profileData?.medicalHistory?.allergies || [];
    const legacyOnly = legacyRaw.filter((item) => {
      const label = typeof item === 'string' ? item : item?.allergen || item?.name || '';
      return label && !allergenKeys.has(String(label).toLowerCase());
    });
    const weights = Array.isArray(c.weightHistory) ? [...c.weightHistory] : [];
    weights.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    let computedBmi = c.bmi;
    if (computedBmi == null && c.weightKg && c.heightCm) {
      const h = Number(c.heightCm) / 100;
      computedBmi = h > 0 ? Math.round((Number(c.weightKg) / (h * h)) * 10) / 10 : null;
    }
    return { c, structuredAllergies: structured, legacyOnlyAllergies: legacyOnly, recentWeights: weights.slice(0, 5), computedBmi };
  }, [resident, profileData?.medicalHistory?.allergies]);

  // Load care minutes, SIRS, and incidents on mount
  useEffect(() => {
    const loadData = async () => {
      if (!resident?.id) return;
      
      // Load care minutes (Aged Care only)
      if (isAgedCare) {
        setLoadingCareMinutes(true);
        try {
          const today = new Date().toISOString().split('T')[0];
          const cmResult = await CareMinutesService.getByResidentAndDate(resident.id, today);
          
          if (cmResult.success && cmResult.data) {
            setCareMinutesData(cmResult.data);
          } else {
            setCareMinutesData(null);
          }
        } catch (error) {
          console.error('Error loading care minutes:', error);
          setCareMinutesData(null);
        } finally {
          setLoadingCareMinutes(false);
        }
      }
      
      // Load SIRS incidents (Aged Care only)
      if (isAgedCare) {
        const sirs = sirsData.filter(s => s.residentId === resident.id);
        setResidentSIRS(sirs);
      }
      
      // Load quality incidents
      const incidents = qualityIncidents.filter(i => 
        i.resident.toLowerCase().includes(resident.name.toLowerCase())
      );
      setResidentQualityIncidents(incidents);

      // Initialise leave & movement data from model into persisted store
      const ld = LeaveMovementService.initialise(resident.id, resident.leaveMovements);
      setLeaveData(ld);
      setLeaveStats(LeaveMovementService.getStatistics(resident.id));
    };
    
    loadData();
  }, [resident?.id, isAgedCare]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    // eslint-disable-next-line no-console
    console.debug('[EnhancedResidentProfile] opened', { residentId: resident?.id, initialTab: activeTab });
  }, [resident?.id]);

  // Phase 0: five primary tabs — logical consolidation
  // All tabs merged from top tabs + sub-tabs into two rows
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'demographics', label: 'Demographics', icon: BadgeCheck },
    { id: 'admission', label: 'Admission', icon: Building2 },
    ...(isAgedCare ? [{ id: 'funding', label: 'Funding', icon: DollarSign }] : []),
    { id: 'clinical', label: 'Clinical', icon: Stethoscope },
    { id: 'medication', label: 'Medication', icon: Pill },
    { id: 'dietary', label: 'Dietary', icon: Utensils },
    { id: 'life-story', label: 'Personal Life', icon: PartyPopper },
    { id: 'contacts', label: 'Address & network', icon: BookUser },
    { id: 'care-plan', label: 'Care Plan & Activities', icon: ClipboardList },
    { id: 'assessments', label: 'Assessments', icon: ClipboardCheck },
    { id: 'onboarding', label: 'Onboarding', icon: ClipboardCheck },
    { id: 'crm', label: 'CRM', icon: TrendingUp },
    { id: 'quality', label: 'Quality', icon: Award },
    { id: 'leave', label: 'Leave', icon: MapPin },
    { id: 'documents', label: 'Documents', icon: FileText }
  ];

  const isTabSelected = (tabId) => {
    if (tabId === 'clinical') return activeTab === 'clinical';
    if (tabId === 'medication') return activeTab === 'medication';
    return activeTab === tabId;
  };

  const activateTab = (tabId, medicationSubTab) => {
    if (tabId === 'medication') {
      setMedicationTab(medicationSubTab || 'overview');
      setActiveTab('medication');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[EnhancedResidentProfile] tab → Medication', { sub: medicationSubTab || 'overview' });
      }
      return;
    }
    setActiveTab(tabId);
    if (process.env.NODE_ENV === 'development') {
      if (tabId === 'clinical') {
        // eslint-disable-next-line no-console
        console.debug('[EnhancedResidentProfile] tab → Clinical (diagnoses, vitals, alerts — not MAR)');
      }
      if (tabId === 'care-plan') {
        // eslint-disable-next-line no-console
        console.debug('[EnhancedResidentProfile] tab → Care Plan & Activities');
      }
      if (tabId === 'contacts') {
        // eslint-disable-next-line no-console
        console.debug('[EnhancedResidentProfile] tab → Address & network (merged address + support)');
      }
    }
  };

  // Document data for viewing
  const documentData = {
    'Comprehensive Care Plan 2024': {
      title: 'Comprehensive Care Plan 2024',
      type: 'Care Plan',
      date: '2024-01-15',
      size: '2.4 MB',
      author: 'Dr. Sarah Johnson',
      status: 'Active',
      content: {
        overview: 'Complete care plan including medical, social, and psychological needs for Eleanor Mary Thompson',
        sections: [
          {
            title: 'Medical History',
            content: 'Primary diagnosis: Dementia (Alzheimer\'s type), Secondary: Hypertension, Type 2 Diabetes, Osteoarthritis. Current medications include Donepezil 10mg daily, Metformin 500mg twice daily, Lisinopril 10mg daily, and Paracetamol 500mg as needed.'
          },
          {
            title: 'Care Goals',
            content: 'Maintain cognitive function, manage chronic conditions, ensure safety and mobility, promote social engagement, and provide family support.'
          },
          {
            title: 'Daily Care Plan',
            content: 'Morning: Medication administration, personal care, breakfast. Afternoon: Physical therapy, social activities, lunch. Evening: Medication administration, dinner, relaxation activities.'
          },
          {
            title: 'Safety Measures',
            content: 'Fall risk assessment completed, mobility aids provided, emergency response system installed, regular safety checks scheduled.'
          }
        ]
      }
    },
    'MMSE Assessment Report': {
      title: 'MMSE Assessment Report',
      type: 'Assessment',
      date: '2024-01-15',
      size: '1.8 MB',
      author: 'Dr. Michael Chen',
      status: 'Completed',
      content: {
        overview: 'Mini-Mental State Examination results and analysis for cognitive assessment',
        sections: [
          {
            title: 'Assessment Results',
            content: 'Total Score: 24/30 (Mild Cognitive Impairment). Orientation: 8/10, Registration: 3/3, Attention: 4/5, Recall: 2/3, Language: 7/9.'
          },
          {
            title: 'Cognitive Domains',
            content: 'Memory: Mild impairment in short-term recall. Language: Good comprehension, occasional word-finding difficulties. Executive Function: Some difficulty with complex tasks. Visuospatial: Intact.'
          },
          {
            title: 'Recommendations',
            content: 'Continue current medication regimen, implement memory aids, regular cognitive stimulation activities, family education on communication strategies.'
          }
        ]
      }
    },
    'Blood Test Results': {
      title: 'Blood Test Results',
      type: 'Lab Results',
      date: '2024-01-10',
      size: '1.2 MB',
      author: 'Dr. Sarah Johnson',
      status: 'Normal',
      content: {
        overview: 'Comprehensive blood panel including CBC, metabolic panel, and diabetes markers',
        sections: [
          {
            title: 'Complete Blood Count',
            content: 'Hemoglobin: 12.8 g/dL (Normal), White Blood Cells: 7.2 K/μL (Normal), Platelets: 285 K/μL (Normal), Hematocrit: 38.5% (Normal).'
          },
          {
            title: 'Metabolic Panel',
            content: 'Glucose: 98 mg/dL (Normal), Creatinine: 0.9 mg/dL (Normal), BUN: 18 mg/dL (Normal), Sodium: 140 mEq/L (Normal), Potassium: 4.2 mEq/L (Normal).'
          },
          {
            title: 'Diabetes Markers',
            content: 'HbA1c: 6.8% (Well controlled), Fasting Glucose: 95 mg/dL (Normal), Random Glucose: 110 mg/dL (Normal).'
          }
        ]
      }
    },
    'Fall Risk Assessment': {
      title: 'Fall Risk Assessment',
      type: 'Safety Assessment',
      date: '2024-01-12',
      size: '0.8 MB',
      author: 'Nurse Jennifer Wilson',
      status: 'High Risk',
      content: {
        overview: 'Comprehensive fall risk evaluation and prevention strategies',
        sections: [
          {
            title: 'Risk Factors',
            content: 'History of falls (2 in past 6 months), unsteady gait, use of walking aid, cognitive impairment, multiple medications (4+), vision problems.'
          },
          {
            title: 'Assessment Score',
            content: 'Morse Fall Scale: 85 (High Risk). Factors: History of falls (25), Secondary diagnosis (15), Ambulatory aid (30), IV/Heparin lock (20), Gait (15), Mental status (0).'
          },
          {
            title: 'Prevention Measures',
            content: 'Bed alarm activated, non-slip footwear, regular safety rounds, medication review, physical therapy referral, family education on fall prevention.'
          }
        ]
      }
    },
    'Medication Review': {
      title: 'Medication Review',
      type: 'Pharmacy Review',
      date: '2024-01-08',
      size: '1.5 MB',
      author: 'Pharmacist David Lee',
      status: 'Current',
      content: {
        overview: 'Comprehensive medication review and optimization recommendations',
        sections: [
          {
            title: 'Current Medications',
            content: 'Donepezil 10mg daily (Alzheimer\'s), Metformin 500mg twice daily (Diabetes), Lisinopril 10mg daily (Hypertension), Paracetamol 500mg PRN (Pain).'
          },
          {
            title: 'Drug Interactions',
            content: 'No significant interactions identified. Lisinopril and Metformin work synergistically for cardiovascular protection. Regular monitoring of blood pressure and blood glucose recommended.'
          },
          {
            title: 'Adherence Assessment',
            content: 'Good adherence to prescribed medications. Pill organizer provided. Family involvement in medication management. Regular pharmacy reviews scheduled.'
          }
        ]
      }
    },
    'Social Assessment': {
      title: 'Social Assessment',
      type: 'Social Work',
      date: '2024-01-05',
      size: '1.1 MB',
      author: 'Social Worker Maria Rodriguez',
      status: 'Complete',
      content: {
        overview: 'Social and family assessment for care planning and support services',
        sections: [
          {
            title: 'Family Support',
            content: 'Daughter visits 3 times per week, son visits weekly. Strong family involvement in care decisions. Family educated on dementia care strategies.'
          },
          {
            title: 'Social Activities',
            content: 'Participates in group activities 3 times per week. Enjoys music therapy and art classes. Regular social interaction with other residents.'
          },
          {
            title: 'Financial Resources',
            content: 'Private insurance coverage. Family provides additional support. No financial barriers to care identified.'
          }
        ]
      }
    }
  };

  const docCategoryBucket = (cat) => {
    const c = String(cat || '').toLowerCase();
    if (/(care|plan|clinical|assessment|medical|laboratory|lab|pharmacy|medication|safety)/.test(c)) return 'clinical';
    if (/(legal|agreement|consent|poa|power|directive|governance)/.test(c)) return 'legal';
    if (/(family|social|meeting|communication)/.test(c)) return 'family';
    return 'general';
  };

  const buildDocumentViewerPayload = (doc) => {
    if (documentData[doc.name]) return documentData[doc.name];
    return {
      title: doc.name,
      type: doc.type,
      date: doc.date,
      size: doc.size,
      author: doc.uploadedBy,
      status: doc.status,
      content: {
        overview: doc.description || 'No rich preview is stored for this file in the demo.',
        sections: [
          {
            title: 'File metadata',
            content: `Category: ${doc.category}. Reference: ${doc.id}. Uploaded by ${doc.uploadedBy}.`
          }
        ]
      }
    };
  };

  const fileLibraryDocuments = useMemo(() => profileData.documents || [], [profileData.documents]);

  const filteredFileLibrary = useMemo(() => {
    let list = [...fileLibraryDocuments];
    if (docCategoryFilter !== 'all') {
      list = list.filter((d) => docCategoryBucket(d.category) === docCategoryFilter);
    }
    const q = docSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((d) =>
        `${d.name} ${d.type} ${d.description} ${d.category}`.toLowerCase().includes(q)
      );
    }
    return list;
  }, [fileLibraryDocuments, docCategoryFilter, docSearch]);

  const carePlanGoalLines = useMemo(() => {
    const g = resident?.clinical?.goalsOfCare;
    if (!g || typeof g !== 'string') return [];
    return g
      .split(/\n+|•|;/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 10);
  }, [resident?.clinical?.goalsOfCare]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || activeTab !== 'documents') return;
    // eslint-disable-next-line no-console
    console.debug('[EnhancedResidentProfile] Documents tab', {
      libraryCount: fileLibraryDocuments.length,
      filtered: filteredFileLibrary.length,
      hasStructuredRegister: Boolean(resident?.documents && typeof resident.documents === 'object')
    });
  }, [activeTab, fileLibraryDocuments.length, filteredFileLibrary.length, resident?.documents]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || activeTab !== 'quality') return;
    // eslint-disable-next-line no-console
    console.debug('[EnhancedResidentProfile] Quality tab', {
      nqip: Boolean(resident?.qualityCompliance?.nqipData),
      sirs: residentSIRS.length,
      incidents: residentQualityIncidents.length,
      progressNotes: (resident?.qualityCompliance?.progressNotes || []).length,
      careMinutesLoaded: Boolean(careMinutesData)
    });
  }, [
    activeTab,
    resident?.qualityCompliance,
    residentSIRS.length,
    residentQualityIncidents.length,
    careMinutesData
  ]);

  // Medication action handlers
  const handleEditMedication = (medication) => {
    setEditingMedication(medication);
    setShowEditModal(true);
  };

  const handleRefillMedication = (medication) => {
    setEditingMedication(medication);
    setShowRefillModal(true);
  };

  const handleScheduleMedication = (medication) => {
    setEditingMedication(medication);
    setShowScheduleModal(true);
  };

  const handleReportMedication = (medication) => {
    setEditingMedication(medication);
    setShowReportModal(true);
  };

  // Document action handlers
  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handleDownloadDocument = (docItem) => {
    if (!resident?.id) return;
    ResidentProfileService.recordDownload(resident.id, docItem?.id || docItem?.title, 'current_user');
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[EnhancedResidentProfile] document download recorded', { title: docItem?.title || docItem?.name });
    }
  };

  const handleSaveMedication = (updatedMedication) => {
    if (!resident?.id) return;
    ResidentProfileService.initialiseMedications(resident.id, profileData.medications?.current);
    const dataToSave = editMedFormData || updatedMedication;
    const result = ResidentProfileService.saveMedication(resident.id, dataToSave, 'current_user');
    if (process.env.NODE_ENV === 'development') {
      console.debug('[EnhancedResidentProfile] medication saved', result);
    }
    setShowEditModal(false);
    setEditingMedication(null);
    setEditMedFormData(null);
  };

  const handleAddResidentSubmit = async () => {
    const result = await ResidentService.create({
      name: addResidentForm.name,
      dateOfBirth: addResidentForm.dateOfBirth,
      room: addResidentForm.room,
      ward: addResidentForm.ward,
      facility: addResidentForm.facility,
      medicare: addResidentForm.medicare,
      careLevel: addResidentForm.careLevel,
      gender: addResidentForm.gender,
      fundingType: addResidentForm.fundingType,
      allergies: addResidentForm.allergies ? addResidentForm.allergies.split(',').map(a => a.trim()) : [],
      dietaryRestrictions: addResidentForm.dietaryRestrictions ? addResidentForm.dietaryRestrictions.split(',').map(d => d.trim()) : [],
      emergencyContact: {
        name: addResidentForm.emergencyContactName,
        phone: addResidentForm.emergencyContactPhone,
      },
      photo: '👵',
      status: 'active',
    });
    if (result.success) {
      dispatch({ type: ACTIONS.ADD_RESIDENT, payload: result.data });
      setShowAddResidentModal(false);
      setAddResidentForm({ name: '', dateOfBirth: '', room: '', ward: 'ward_a', facility: 'sunshine', medicare: '', careLevel: 'Low', gender: '', fundingType: 'AN-ACC', allergies: '', dietaryRestrictions: '', emergencyContactName: '', emergencyContactPhone: '' });
      showToast('Resident admitted successfully', 'success');
    } else {
      showToast('Error: ' + (result.errors?.join(', ') || result.error), 'error');
    }
  };

  const handleDischargeResident = async () => {
    if (!dischargeTarget) return;
    const result = await ResidentService.discharge(dischargeTarget.id, dischargeReason, dischargeDate);
    if (result.success) {
      dispatch({ type: ACTIONS.REMOVE_RESIDENT, payload: dischargeTarget.id });
      setShowDischargeModal(false);
      setDischargeTarget(null);
      setDischargeReason('');
      showToast('Resident discharged successfully', 'success');
    } else {
      showToast('Error: ' + result.error, 'error');
    }
  };

  const handleEditProfileSubmit = () => {
    if (!resident?.id) return;
    Object.entries(editProfileData).forEach(([field, value]) => {
      if (value !== undefined && value !== '') {
        ResidentProfileService.saveProfileField(resident.id, field, value, 'current_user');
      }
    });
    setShowEditProfileModal(false);
    showToast('Profile updated', 'success');
  };

  const handleAddMedToResident = () => {
    if (!resident?.id) return;
    ResidentProfileService.initialiseMedications(resident.id, profileData.medications?.current);
    const result = ResidentProfileService.addMedication(resident.id, {
      medication: addMedForm.medication,
      dosage: addMedForm.dosage,
      frequency: addMedForm.frequency,
      route: addMedForm.route,
      prescriber: addMedForm.prescriber,
      startDate: addMedForm.startDate,
      instructions: addMedForm.instructions,
      status: 'active',
    }, 'current_user');
    if (result.success) {
      setShowAddMedModal(false);
      setAddMedForm({ medication: '', dosage: '', frequency: '', route: 'oral', prescriber: '', startDate: new Date().toISOString().split('T')[0], instructions: '' });
      showToast('Medication added', 'success');
    } else {
      showToast('Error: ' + result.error, 'error');
    }
  };

  const handleDiscontinueMed = () => {
    if (!resident?.id || !discontinueMedTarget) return;
    ResidentProfileService.initialiseMedications(resident.id, profileData.medications?.current);
    const result = ResidentProfileService.discontinueMedication(resident.id, discontinueMedTarget.id, discontinueReason, 'current_user');
    if (result.success) {
      setShowDiscontinueMedModal(false);
      setDiscontinueMedTarget(null);
      setDiscontinueReason('');
      showToast('Medication discontinued', 'success');
    } else {
      showToast('Error: ' + result.error, 'error');
    }
  };

  const handleDocUpload = () => {
    if (!resident?.id) return;
    const result = ResidentProfileService.addDocument(resident.id, {
      name: docUploadForm.name,
      type: docUploadForm.type,
      category: docUploadForm.category,
      description: docUploadForm.description,
    }, 'current_user');
    if (result.success) {
      setShowDocUploadModal(false);
      setDocUploadForm({ name: '', type: 'Clinical', category: 'General', description: '' });
      showToast('Document uploaded', 'success');
    } else {
      showToast('Error: ' + result.error, 'error');
    }
  };

  const handleProcessRefill = (medication) => {
    if (!resident?.id) return;
    ResidentProfileService.initialiseMedications(resident.id, profileData.medications?.current);
    const result = ResidentProfileService.processRefill(resident.id, medication.id, {
      quantity: 'Standard',
      pharmacy: medication.pharmacy || 'Default Pharmacy',
    }, 'current_user');
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[EnhancedResidentProfile] refill processed', result);
    }
    setShowRefillModal(false);
    setEditingMedication(null);
  };

  const handleScheduleMedicationTime = (medication, scheduleData) => {
    if (!resident?.id) return;
    ResidentProfileService.initialiseMedications(resident.id, profileData.medications?.current);
    const result = ResidentProfileService.updateSchedule(resident.id, medication.id, scheduleData, 'current_user');
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[EnhancedResidentProfile] schedule updated', result);
    }
    setShowScheduleModal(false);
    setEditingMedication(null);
  };

  const handleGenerateReport = (medication) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[EnhancedResidentProfile] report generated for', medication?.medication || medication?.name);
    }
    logDataModification('medication_report', medication?.id, 'GENERATE', null, { medication: medication?.medication }, 'current_user');
    setShowReportModal(false);
    setEditingMedication(null);
  };

  // ── Leave & Movement Handlers ─────────────────────────────────────────

  const refreshLeaveData = useCallback(() => {
    if (!resident?.id) return;
    setLeaveData(LeaveMovementService.getAll(resident.id));
    setLeaveStats(LeaveMovementService.getStatistics(resident.id));
  }, [resident?.id]);

  const handleRecordLeave = () => {
    setLeaveFormErrors([]);
    const result = LeaveMovementService.recordLeave(resident.id, leaveFormData, 'current_user');
    if (!result.success) {
      setLeaveFormErrors(result.errors || [result.error]);
      return;
    }
    refreshLeaveData();
    setShowLeaveModal(false);
    setLeaveFormData({
      type: 'Social Leave', startDate: '', endDate: '', expectedReturnDate: '',
      destination: '', notes: '', contactWhileOnLeave: '', authorisedBy: '', activateBedHold: false,
    });
  };

  const handleRecordTransfer = () => {
    setLeaveFormErrors([]);
    const result = LeaveMovementService.recordHospitalTransfer(resident.id, transferFormData, 'current_user');
    if (!result.success) {
      setLeaveFormErrors(result.errors || [result.error]);
      return;
    }
    refreshLeaveData();
    setShowTransferModal(false);
    setTransferFormData({
      dateOut: '', hospital: '', reason: '', referringDoctor: '',
      ambulanceRequired: false, familyNotified: false, expectedReturn: '',
    });
  };

  const handleReturnFromLeave = () => {
    if (!activeLeaveRecord) return;
    const result = LeaveMovementService.returnFromLeave(resident.id, activeLeaveRecord.id, returnFormData, 'current_user');
    if (!result.success) {
      setLeaveFormErrors([result.error]);
      return;
    }
    refreshLeaveData();
    setShowReturnModal(false);
    setActiveLeaveRecord(null);
    setReturnFormData({ returnDate: '', returnNotes: '', clinicalChangesOnReturn: '', dischargeSummaryReceived: false, medicationChanges: '', followUpRequired: '' });
  };

  const handleReturnFromHospital = () => {
    if (!activeTransferRecord) return;
    const result = LeaveMovementService.returnFromHospital(resident.id, activeTransferRecord.id, returnFormData, 'current_user');
    if (!result.success) {
      setLeaveFormErrors([result.error]);
      return;
    }
    refreshLeaveData();
    setShowReturnModal(false);
    setActiveTransferRecord(null);
    setReturnFormData({ returnDate: '', returnNotes: '', clinicalChangesOnReturn: '', dischargeSummaryReceived: false, medicationChanges: '', followUpRequired: '' });
  };

  const handleDeleteLeave = async (leaveRecordId) => {
    const confirmed = await confirm({ title: 'Delete Leave Record', message: 'Delete this leave record? This action cannot be undone.', confirmLabel: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    LeaveMovementService.deleteLeave(resident.id, leaveRecordId, 'current_user');
    refreshLeaveData();
  };

  const handleActivateBedHold = (bedHoldData) => {
    LeaveMovementService.activateBedHold(resident.id, bedHoldData, 'current_user');
    refreshLeaveData();
    setShowBedHoldModal(false);
  };

  const handleReleaseBedHold = async () => {
    const confirmed = await confirm({ title: 'Release Bed Hold', message: 'Release the bed hold?', confirmLabel: 'Release', variant: 'warning' });
    if (!confirmed) return;
    LeaveMovementService.releaseBedHold(resident.id, 'current_user');
    refreshLeaveData();
  };

  const handleUpdateEvacPriority = (priority) => {
    LeaveMovementService.updateEvacuationPriority(resident.id, priority, 'current_user');
    refreshLeaveData();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="mf-card shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header — Enhanced */}
        <div className="bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 text-white px-6 py-5 rounded-t-xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              {/* Avatar */}
              <div className="w-16 h-16 rounded-2xl bg-white/15 ring-2 ring-white/30 flex items-center justify-center flex-shrink-0 text-2xl font-bold">
                {resident.demographics?.profilePhoto
                  ? <img src={resident.demographics.profilePhoto} alt="" className="w-full h-full rounded-2xl object-cover" />
                  : (profileData.personalInfo?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-bold truncate">{profileData.personalInfo?.name || 'Unknown'}</h2>
                  <Tooltip content={
                    ({
                      'Active': 'Resident is currently in the facility receiving care',
                      'On Leave': 'Resident is temporarily away on social or recreational leave',
                      'Hospital Transfer': 'Resident has been transferred to hospital for acute care',
                      'Discharged': 'Resident has been formally discharged from the facility',
                      'Deceased': 'Resident is deceased — record retained for compliance',
                      'Respite': 'Resident is on a short-term respite care admission',
                    })[resident.admission?.residentStatus || resident.residentStatus || 'Active']
                    || 'Resident has a non-standard status — check admission details'
                  } position="bottom">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      (resident.admission?.residentStatus || resident.residentStatus || 'Active') === 'Active'
                        ? 'bg-green-500/20 text-green-200 ring-1 ring-green-400/40'
                        : 'bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40'
                    }`}>
                      {resident.admission?.residentStatus || resident.residentStatus || 'Active'}
                    </span>
                  </Tooltip>
                  {resident.clinical?.resuscitationStatus && (
                    <Tooltip content="Advance care directive — resuscitation and treatment preferences" position="bottom">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        resident.clinical.resuscitationStatus.includes('Not for') || resident.clinical.resuscitationStatus.includes('Comfort')
                          ? 'bg-red-500/30 text-red-200 ring-1 ring-red-400/40'
                          : 'bg-blue-500/20 text-blue-200 ring-1 ring-blue-400/40'
                      }`}>
                        {resident.clinical.resuscitationStatus}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-sm text-blue-100/80 flex-wrap">
                  <span>Room {resident.admission?.roomNumber || profileData.personalInfo?.room || '—'}</span>
                  <span className="opacity-40">|</span>
                  <span>{resident.admission?.wingUnit || profileData.personalInfo?.ward || ''} • {profileData.personalInfo?.facility || '—'}</span>
                  <span className="opacity-40">|</span>
                  <span>{resident.demographics?.preferredName ? `"${resident.demographics.preferredName}"` : ''} Age {profileData.personalInfo?.age || '—'}</span>
                  {resident.demographics?.interpreterRequired && (
                    <>
                      <span className="opacity-40">|</span>
                      <span className="text-amber-300 font-medium text-xs">Interpreter needed ({resident.demographics.primaryLanguage})</span>
                    </>
                  )}
                </div>
                {/* Quick clinical badges */}
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {resident.clinical?.allergies?.length > 0 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-500/25 text-red-200 ring-1 ring-red-400/30">
                      {resident.clinical.allergies.length} Allerg{resident.clinical.allergies.length > 1 ? 'ies' : 'y'}
                    </span>
                  )}
                  {resident.clinical?.fallsRiskRating && (
                    <Tooltip content="Fall risk assessment based on validated screening tool" position="bottom">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ring-1 ${
                        (typeof resident.clinical.fallsRiskRating === 'object' ? resident.clinical.fallsRiskRating.level : resident.clinical.fallsRiskRating) === 'High' ? 'bg-red-500/25 text-red-200 ring-red-400/30' :
                        (typeof resident.clinical.fallsRiskRating === 'object' ? resident.clinical.fallsRiskRating.level : resident.clinical.fallsRiskRating) === 'Medium' ? 'bg-amber-500/25 text-amber-200 ring-amber-400/30' :
                        'bg-green-500/20 text-green-200 ring-green-400/30'
                      }`}>
                        Falls: {typeof resident.clinical.fallsRiskRating === 'object' ? resident.clinical.fallsRiskRating.level : resident.clinical.fallsRiskRating}
                      </span>
                    </Tooltip>
                  )}
                  {resident.clinical?.cognitiveStatus && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-500/20 text-purple-200 ring-1 ring-purple-400/30">
                      {resident.clinical.cognitiveStatus}
                    </span>
                  )}
                  {resident.clinical?.mobilityStatus && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-200 ring-1 ring-blue-400/30">
                      {resident.clinical.mobilityStatus}
                    </span>
                  )}
                  {isAgedCare && resident.funding?.anaccClass && (
                    <Tooltip content="Australian National Aged Care Classification — determines government funding tier" position="bottom">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-200 ring-1 ring-cyan-400/30">
                        AN-ACC {resident.funding.anaccClass}
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => { setEditProfileData({}); setShowEditProfileModal(true); }}
                className="touch-target bg-white/10 text-white px-3 py-2 rounded-lg hover:bg-white/20 transition-colors inline-flex items-center text-sm font-medium ring-1 ring-white/20"
              >
                <Edit3 className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                Edit
              </button>
              <button
                type="button"
                onClick={onClose}
                className="touch-target bg-white/10 text-white p-2 rounded-lg hover:bg-white/20 transition-colors ring-1 ring-white/20"
                title="Close profile"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Two Rows (full-width grid: equal columns, ~30% wider feel via full row + padding) */}
        <div className="border-b border-gray-200 bg-white px-6 py-3">
          {(() => {
            const row1 = tabs.filter(t => ['profile', 'demographics', 'contacts', 'admission', 'funding', 'clinical', 'medication'].includes(t.id));
            const row2 = tabs.filter(t =>
              ['dietary', 'life-story', 'care-plan', 'assessments', 'crm', 'quality', 'leave', 'documents'].includes(t.id)
            );
            const tabBtn = (tab) => (
              <button
                key={tab.id}
                type="button"
                data-testid={`resident-tab-${tab.id}`}
                onClick={() => activateTab(tab.id)}
                className={`touch-target w-full min-h-[2.75rem] px-[1.3rem] py-2.5 rounded-lg font-semibold text-sm transition-all text-center leading-tight overflow-hidden ${
                  isTabSelected(tab.id)
                    ? 'bg-purple-100 text-purple-800 border-2 border-purple-300 shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100 border-2 border-transparent'
                }`}
                title={tab.label}
                style={{ zoom: 1, minWidth: 0 }}
              >
                <span style={{ zoom: 1, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tab.label}
                </span>
              </button>
            );
            return (
              <>
                <div
                  className="mb-2 grid w-full gap-2"
                  style={{ gridTemplateColumns: `repeat(${row1.length}, minmax(0, 1fr))` }}
                >
                  {row1.map(tabBtn)}
                </div>
                <div
                  className="grid w-full gap-2"
                  style={{ gridTemplateColumns: `repeat(${row2.length}, minmax(0, 1fr))` }}
                >
                  {row2.map(tabBtn)}
                </div>
              </>
            );
          })()}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Profile Tab (consolidated: Overview + Demographics) */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Clinical Alerts Banner - Always visible at top */}
              {(resident.clinical?.allergies?.length > 0 || resident.clinical?.clinicalAlerts?.length > 0 || resident.clinical?.resuscitationStatus) && (
                <div className="border-l-4 border-red-500 bg-red-50 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="mf-heading-sm text-red-800 mb-2">Clinical Alerts & Safety Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {resident.clinical?.allergies?.length > 0 && (
                          <div className="bg-white rounded p-3 border border-red-200">
                            <span className="font-bold text-red-700 block mb-1">Allergies ({resident.clinical.allergies.length})</span>
                            <div className="flex flex-wrap gap-1">
                              {resident.clinical.allergies.map((allergy, i) => (
                                <span key={i} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">
                                  {allergy.allergen} ({allergy.severity})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {resident.clinical?.clinicalAlerts?.length > 0 && (
                          <div className="bg-white rounded p-3 border border-red-200">
                            <span className="font-bold text-red-700 block mb-1">Clinical Alerts ({resident.clinical.clinicalAlerts.length})</span>
                            <div className="flex flex-wrap gap-1">
                              {resident.clinical.clinicalAlerts.map((alert, i) => (
                                <span key={i} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-semibold">
                                  {alert}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {resident.clinical?.resuscitationStatus && (
                          <div className="bg-white rounded p-3 border border-red-200 md:col-span-2">
                            <span className="font-bold text-red-700 block mb-1">Resuscitation Status</span>
                            <span className={`font-bold ${resident.clinical.resuscitationStatus.includes('Not for') || resident.clinical.resuscitationStatus.includes('Comfort measures') ? 'text-red-700' : 'text-green-700'}`}>
                              {resident.clinical.resuscitationStatus}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <ComplianceSnapshotCard
                sirsCount={residentSIRS.length}
                qualityIncidentCount={residentQualityIncidents.length}
                careMinutesCompliant={
                  !isAgedCare || !careMinutesData
                    ? true
                    : !!(careMinutesData.compliant && careMinutesData.rnCompliant)
                }
                starRating={
                  resident.starRatings?.overallRating != null
                    ? String(resident.starRatings.overallRating)
                    : '4.2'
                }
                onOpenCrm={() => {
                  setActiveTab('crm');
                  if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.debug('[EnhancedResidentProfile] ComplianceSnapshotCard → CRM');
                  }
                }}
              />

              {/* Key Info Panel — Two columns: left=demographics snapshot, right=quick-nav tiles */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Demographics Snapshot */}
                <div className="lg:col-span-2 rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Key Information</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <span className="text-gray-400 text-xs">Date of Birth</span>
                      <p className="font-semibold text-gray-800">{resident.demographics?.dateOfBirth || resident.dateOfBirth || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Gender</span>
                      <p className="font-semibold text-gray-800">{resident.demographics?.gender || '—'}{resident.demographics?.pronouns ? ` (${resident.demographics.pronouns})` : ''}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Admission Date</span>
                      <p className="font-semibold text-gray-800">{resident.admission?.admissionDate || resident.admissionDate || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Primary Diagnosis</span>
                      <p className="font-semibold text-gray-800">{resident.clinical?.primaryDiagnosis?.displayText || resident.primaryDiagnosis || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Mobility</span>
                      <p className="font-semibold text-gray-800">{resident.clinical?.mobilityStatus || resident.mobility || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-xs">Diet</span>
                      <p className="font-semibold text-gray-800">{resident.dietary?.dietType || '—'}</p>
                    </div>
                    {resident.supportNetwork?.gp && (
                      <div>
                        <span className="text-gray-400 text-xs">GP</span>
                        <p className="font-semibold text-gray-800">{resident.supportNetwork.gp.name || '—'}</p>
                      </div>
                    )}
                    {resident.supportNetwork?.primaryContact && (
                      <div>
                        <span className="text-gray-400 text-xs">Primary Contact</span>
                        <p className="font-semibold text-gray-800">{resident.supportNetwork.primaryContact.name || '—'}</p>
                        <p className="text-xs text-gray-500">{resident.supportNetwork.primaryContact.relationship || ''} {resident.supportNetwork.primaryContact.phone ? `• ${resident.supportNetwork.primaryContact.phone}` : ''}</p>
                      </div>
                    )}
                    {resident.clinical?.advanceCareDirective && (
                      <div>
                        <span className="text-gray-400 text-xs">Advance Care Directive</span>
                        <p className="font-semibold text-gray-800">{typeof resident.clinical.advanceCareDirective === 'object' ? (resident.clinical.advanceCareDirective.summary || (resident.clinical.advanceCareDirective.exists ? 'On file' : '—')) : resident.clinical.advanceCareDirective}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick-nav tiles */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'medication', sub: 'current', icon: Pill, color: 'blue', label: 'Medications', count: profileData.medications?.current?.length || 0 },
                    { id: 'assessments', icon: ClipboardList, color: 'green', label: 'Assessments', count: mergedAssessments.length },
                    { id: 'leave', icon: MapPin, color: 'cyan', label: 'Leave', count: leaveStats?.totalLeaveRecords || 0 },
                    { id: 'documents', icon: FileText, color: 'orange', label: 'Documents', count: profileData.documents?.length || 0 },
                  ].map((tile) => (
                    <button
                      key={tile.id}
                      type="button"
                      onClick={() => tile.sub ? activateTab(tile.id, tile.sub) : setActiveTab(tile.id)}
                      className={`rounded-xl border-2 border-${tile.color}-200 p-3 text-center hover:bg-${tile.color}-50 hover:border-${tile.color}-300 transition-all shadow-sm bg-white`}
                    >
                      <tile.icon className={`w-6 h-6 text-${tile.color}-600 mx-auto mb-1`} />
                      <span className="text-xs font-bold text-gray-700 block">{tile.label}</span>
                      <p className={`text-lg font-bold text-${tile.color}-600`}>{tile.count}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Care Minutes Compliance - ALWAYS SHOW FOR AGED CARE */}
              {isAgedCare && supportsANACC && (() => {
                const hasData = careMinutesData && (careMinutesData.totalMinutes > 0 || careMinutesData.rnMinutes > 0);
                const isCompliant = careMinutesData?.compliant && careMinutesData?.rnCompliant;
                const anaccClass = careMinutesData?.anaccClass || resident?.anaccClass || resident?.anacc?.class || 'N/A';

                return (
                  <div className="bg-white rounded-lg shadow-md p-6 border-2 border-blue-200" style={{ backgroundColor: '#f0f9ff' }}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        Care Minutes Compliance
                      </h3>
                      <div className="flex items-center gap-2">
                        {loadingCareMinutes ? (
                          <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">
                            Loading...
                          </span>
                        ) : hasData ? (
                          isCompliant ? (
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                              Compliant
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                              Non-Compliant
                            </span>
                          )
                        ) : (
                          <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">
                            No Data
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTab('care-minutes');
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                          title="View Care Minutes Details"
                        >
                          View Details →
                        </button>
                      </div>
                    </div>

                    {loadingCareMinutes ? (
                      <div className="text-center py-6">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                        <p className="text-sm text-gray-600">Loading care minutes...</p>
                      </div>
                    ) : hasData ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">Total Minutes</p>
                          <p className={`text-2xl font-bold ${isCompliant ? 'text-green-600' : 'text-red-600'}`}>
                            {careMinutesData.totalMinutes || 0}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Target: {careMinutesData.anaccAdjustedTarget || careMinutesData.targetMinutes || 215}</p>
                          {careMinutesData.complianceGap > 0 && (
                            <p className="text-xs text-red-600 font-medium mt-1">⚠ Gap: {careMinutesData.complianceGap} min</p>
                          )}
                        </div>
                        
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">RN Minutes</p>
                          <p className={`text-2xl font-bold ${careMinutesData.rnCompliant ? 'text-green-600' : 'text-red-600'}`}>
                            {careMinutesData.rnMinutes || 0}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Target: {careMinutesData.targetRNMinutes || 44}</p>
                          {careMinutesData.rnComplianceGap > 0 && (
                            <p className="text-xs text-red-600 font-medium mt-1">⚠ Gap: {careMinutesData.rnComplianceGap} min</p>
                          )}
                        </div>

                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">Date</p>
                          <p className="text-sm font-semibold text-gray-900">{careMinutesData.date || 'Today'}</p>
                          <p className="text-xs text-gray-500 mt-1">Today's Status</p>
                        </div>

                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 mb-1">AN-ACC Class</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {anaccClass}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Classification</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-3">No care minutes data available for today</p>
                        <button
                          onClick={async () => {
                            setLoadingCareMinutes(true);
                            try {
                              const today = new Date().toISOString().split('T')[0];
                              const cmResult = await CareMinutesService.getByResidentAndDate(resident.id, today);
                              if (cmResult.success && cmResult.data) {
                                setCareMinutesData(cmResult.data);
                              }
                            } catch (error) {
                              console.error('Error loading care minutes:', error);
                            } finally {
                              setLoadingCareMinutes(false);
                            }
                          }}
                          disabled={loadingCareMinutes}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold disabled:opacity-50"
                        >
                          {loadingCareMinutes ? 'Loading...' : 'Load Care Minutes'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Critical Compliance Alerts - Aged Care Only */}
              {isAgedCare && (() => {
                const hasActiveSIRS = residentSIRS.some(s => s.status === 'Under Investigation');
                const isCareMinutesNonCompliant = careMinutesData && (!careMinutesData.compliant || !careMinutesData.rnCompliant);
                const hasAlerts = hasActiveSIRS || isCareMinutesNonCompliant || residentQualityIncidents.length > 0;
                
                if (!hasAlerts) return null;
                
                return (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-semibold text-red-900">Critical Compliance Alerts</h3>
                    </div>
                    <div className="space-y-2">
                      {hasActiveSIRS && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-red-800">
                            Active SIRS Investigation ({residentSIRS.filter(s => s.status === 'Under Investigation').length})
                          </span>
                        </div>
                      )}
                      {isCareMinutesNonCompliant && careMinutesData && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-red-800">
                            Care Minutes Non-Compliant (Gap: {careMinutesData.complianceGap || 0} min, RN Gap: {careMinutesData.rnComplianceGap || 0} min)
                          </span>
                        </div>
                      )}
                      {residentQualityIncidents.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-red-800">
                            Quality Incidents ({residentQualityIncidents.length})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* SIRS Incidents - Aged Care Only */}
              {isAgedCare && residentSIRS.length > 0 && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <h3 className="text-lg font-semibold text-orange-900">SIRS Incidents</h3>
                    </div>
                    <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-bold rounded-full">
                      {residentSIRS.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {residentSIRS.slice(0, 3).map((incident, idx) => {
                      const daysLeft = incident.daysLeft || 0;
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm bg-white rounded p-2 border border-orange-200">
                          <span className="text-orange-800">
                            {incident.type} - {incident.status}
                          </span>
                          {daysLeft > 0 && (
                            <span className="text-xs text-orange-600 font-medium">
                              ({daysLeft} days left)
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Medication Management System Alerts */}
              <div className="bg-gradient-to-br from-white via-red-50 to-orange-50 rounded-xl border-2 border-red-200 p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Medication Management Alerts</h3>
                    <p className="text-red-600 font-medium text-sm">Drug interactions and safety warnings</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* High Priority Alert */}
                  <div className="bg-gradient-to-br from-red-100 to-pink-100 border-2 border-red-300 rounded-lg p-4 shadow-md">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-red-900 text-sm mb-1">High Priority</h4>
                        <p className="text-xs text-red-700 mb-2">Lisinopril + Metformin</p>
                        <p className="text-xs text-red-600">Monitor blood pressure and glucose levels closely</p>
                        <div className="mt-2">
                          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">Action Required</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Medium Priority Alert */}
                  <div className="bg-gradient-to-br from-yellow-100 to-orange-100 border-2 border-yellow-300 rounded-lg p-4 shadow-md">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-yellow-900 text-sm mb-1">Medium Priority</h4>
                        <p className="text-xs text-yellow-700 mb-2">Donepezil Timing</p>
                        <p className="text-xs text-yellow-600">Take with food to reduce nausea risk</p>
                        <div className="mt-2">
                          <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">Monitor</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Low Priority Alert */}
                  <div className="bg-gradient-to-br from-blue-100 to-cyan-100 border-2 border-blue-300 rounded-lg p-4 shadow-md">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-blue-900 text-sm mb-1">Low Priority</h4>
                        <p className="text-xs text-blue-700 mb-2">Paracetamol Usage</p>
                        <p className="text-xs text-blue-600">Within safe daily limits</p>
                        <div className="mt-2">
                          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">Stable</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alert Summary */}
                <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">1 High Priority</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">1 Medium Priority</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">1 Stable</span>
                      </div>
                    </div>
                    <button 
                      onClick={() => actions.setActiveTab('alerts')}
                      className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-600 text-white text-sm font-bold rounded-lg hover:from-red-600 hover:to-orange-700 transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      View All Alerts
                    </button>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-blue-600" />
                    Personal Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date of Birth:</span>
                      <span className="font-medium">{profileData.personalInfo?.dateOfBirth ? new Date(profileData.personalInfo.dateOfBirth).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gender:</span>
                      <span className="font-medium">{profileData.personalInfo?.gender || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Blood Type:</span>
                      <span className="font-medium">{profileData.personalInfo?.bloodType || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">BMI:</span>
                      <span className="font-medium">{profileData.personalInfo?.bmi || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Admission Date:</span>
                      <span className="font-medium">{profileData.personalInfo?.admissionDate ? new Date(profileData.personalInfo.admissionDate).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-green-600" />
                    Health Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Care Level:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        profileData.personalInfo?.careLevel === 'High' ? 'bg-red-100 text-red-800' :
                        profileData.personalInfo?.careLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {profileData.personalInfo?.careLevel || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mobility:</span>
                      <span className="font-medium">{profileData.personalInfo?.mobility || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cognitive Status:</span>
                      <span className="font-medium">{profileData.personalInfo?.cognitiveStatus || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Medication Compliance:</span>
                      <span className="font-medium text-green-600">{profileData.medications?.compliance?.rate || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Phone className="h-5 w-5 mr-2 text-red-600" />
                  Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-gray-900">{profileData.personalInfo?.emergencyContact?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-600">{profileData.personalInfo?.emergencyContact?.relationship || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{profileData.personalInfo?.emergencyContact?.phone || 'N/A'}</p>
                    <p className="text-sm text-gray-600">{profileData.personalInfo?.emergencyContact?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <ComplianceHistoryPanel residentId={profileData.personalInfo?.id ?? resident.id} />
            </div>
          )}

          {/* Demographics Tab */}
          {activeTab === 'demographics' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-8 rounded-2xl shadow-2xl">
                <h2 className="text-3xl font-bold">Demographics & Identity</h2>
                <p className="text-indigo-100 text-lg">Personal details, cultural profile, and government identifiers</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Personal Identity */}
                <div className="bg-white rounded-xl border-2 border-indigo-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-indigo-600" /> Personal Identity
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div><span className="text-gray-500">Title:</span> <span className="font-medium">{resident.demographics?.title || '—'}</span></div>
                      <div><span className="text-gray-500">Preferred Name:</span> <span className="font-medium text-indigo-700">{resident.demographics?.preferredName || resident.name}</span></div>
                      <div><span className="text-gray-500">Legal Name:</span> <span className="font-medium">{resident.demographics?.legalFirstName} {resident.demographics?.legalMiddleNames} {resident.demographics?.legalSurname}</span></div>
                      <div><span className="text-gray-500">Previous Surname:</span> <span className="font-medium">{resident.demographics?.previousSurname || '—'}</span></div>
                      <div><span className="text-gray-500">Date of Birth:</span> <span className="font-medium">{resident.demographics?.dateOfBirth || resident.dateOfBirth}</span></div>
                      <div><span className="text-gray-500">Age:</span> <span className="font-medium">{resident.demographics?.age || resident.age}</span></div>
                      <div><span className="text-gray-500">Gender:</span> <span className="font-medium">{resident.demographics?.gender || '—'}</span></div>
                      <div><span className="text-gray-500">Pronouns:</span> <span className="font-medium">{resident.demographics?.pronouns || '—'}</span></div>
                      <div><span className="text-gray-500">Sex at Birth:</span> <span className="font-medium">{resident.demographics?.sexAssignedAtBirth || '—'}</span></div>
                      <div><span className="text-gray-500">Marital Status:</span> <span className="font-medium">{resident.demographics?.maritalStatus || '—'}</span></div>
                    </div>
                  </div>
                </div>

                {/* Cultural & Language */}
                <div className="bg-white rounded-xl border-2 border-teal-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-teal-600" /> Cultural & Language
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div><span className="text-gray-500">Country of Birth:</span> <span className="font-medium">{resident.demographics?.countryOfBirth || '—'}</span></div>
                      <div><span className="text-gray-500">Nationality:</span> <span className="font-medium">{resident.demographics?.nationality || '—'}</span></div>
                      <div><span className="text-gray-500">Indigenous Status:</span> <span className="font-medium">{resident.demographics?.indigenousStatus || '—'}</span></div>
                      <div><span className="text-gray-500">Primary Language:</span> <span className="font-medium">{resident.demographics?.primaryLanguage || '—'}</span></div>
                      <div><span className="text-gray-500">Other Languages:</span> <span className="font-medium">{resident.demographics?.otherLanguages?.join(', ') || '—'}</span></div>
                      <div><span className="text-gray-500">Interpreter:</span> <span className={`font-medium ${resident.demographics?.interpreterRequired ? 'text-red-600' : ''}`}>{resident.demographics?.interpreterRequired ? 'Required' : 'Not required'}</span></div>
                      <div><span className="text-gray-500">Religion:</span> <span className="font-medium">{resident.demographics?.religion || '—'}</span></div>
                      <div><span className="text-gray-500">Cultural Background:</span> <span className="font-medium">{resident.demographics?.culturalBackground || '—'}</span></div>
                    </div>
                    {resident.demographics?.communicationMethods?.length > 0 && (
                      <div><span className="text-gray-500">Communication Aids:</span> <div className="flex flex-wrap gap-1 mt-1">{resident.demographics.communicationMethods.map((m, i) => <span key={i} className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs">{m}</span>)}</div></div>
                    )}
                    {resident.demographics?.communicationNotes && (
                      <div className="bg-teal-50 p-3 rounded-lg text-xs"><span className="font-medium">Notes:</span> {resident.demographics.communicationNotes}</div>
                    )}
                  </div>
                </div>

                {/* Government Identifiers */}
                <div className="bg-white rounded-xl border-2 border-blue-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" /> Government Identifiers
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div><span className="text-gray-500">Medicare:</span> <span className="font-medium">{resident.governmentIds?.medicareNumber || resident.medicare || '—'}</span></div>
                      <div><span className="text-gray-500">Medicare IRN:</span> <span className="font-medium">{resident.governmentIds?.medicareIrn || '—'}</span></div>
                      <div><span className="text-gray-500">Medicare Expiry:</span> <span className="font-medium">{resident.governmentIds?.medicareExpiry || '—'}</span></div>
                      <div><span className="text-gray-500">IHI:</span> <span className="font-medium">{resident.governmentIds?.ihi || '—'}</span></div>
                      <div><span className="text-gray-500">My Aged Care ID:</span> <span className="font-medium">{resident.governmentIds?.myAgedCareClientId || '—'}</span></div>
                      <div><span className="text-gray-500">Pension Type:</span> <span className="font-medium">{resident.governmentIds?.pensionType || '—'}</span></div>
                      <div><span className="text-gray-500">Centrelink CRN:</span> <span className="font-medium">{resident.governmentIds?.centrelinkCrn || '—'}</span></div>
                      <div><span className="text-gray-500">TFN:</span> <span className="font-medium text-gray-400">{resident.governmentIds?.tfnMasked || '***-***-***'}</span></div>
                      {resident.governmentIds?.ndisNumber && (
                        <div><span className="text-gray-500">NDIS Number:</span> <span className="font-medium text-purple-700">{resident.governmentIds.ndisNumber}</span></div>
                      )}
                    </div>
                    {resident.demographics?.veteranStatus && resident.demographics.veteranStatus !== 'Not a veteran' && (
                      <div className="mt-3 bg-green-50 p-3 rounded-lg border border-green-200">
                        <span className="font-bold text-green-800">Veteran Status:</span> {resident.demographics.veteranStatus}
                        {resident.demographics?.dvaFileNumber && <span className="ml-2 text-green-700">({resident.demographics.dvaFileNumber})</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pointer to merged Address & network tab (avoids duplicating full address + support sections) */}
                <div className="bg-white rounded-xl border-2 border-orange-200 p-6 shadow-lg lg:col-span-2">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100">
                        <BookUser className="h-5 w-5 text-orange-700" aria-hidden />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Address, phones & support network</h3>
                        <p className="mt-1 text-sm text-gray-600">
                          Resident mailing address, mobile/email, emergency reach, GP, authorised representatives, and pharmacy live in one place.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab('contacts');
                        if (process.env.NODE_ENV === 'development') {
                          // eslint-disable-next-line no-console
                          console.debug('[EnhancedResidentProfile] Demographics → Address & network tab');
                        }
                      }}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-orange-300 bg-orange-50 px-4 py-2.5 text-sm font-semibold text-orange-900 shadow-sm transition-colors hover:bg-orange-100"
                    >
                      Open Address &amp; network
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </div>

                {/* Admission & Facility */}
                <div className="bg-white rounded-xl border-2 border-purple-200 p-6 shadow-lg lg:col-span-2">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5 text-purple-600" /> Admission & Facility
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 text-sm">
                    <div><span className="text-gray-500">Admission Date:</span> <span className="font-medium">{resident.admission?.admissionDate || resident.admissionDate || '—'}</span></div>
                    <div><span className="text-gray-500">Admission Type:</span> <span className="font-medium">{resident.admission?.admissionType || '—'}</span></div>
                    <div><span className="text-gray-500">Source:</span> <span className="font-medium">{resident.admission?.admissionSource || '—'}</span></div>
                    <div><span className="text-gray-500">Referring Org:</span> <span className="font-medium">{resident.admission?.referringOrganisation || '—'}</span></div>
                    <div><span className="text-gray-500">Room:</span> <span className="font-medium">{resident.admission?.roomNumber || resident.room} ({resident.admission?.roomType || '—'})</span></div>
                    <div><span className="text-gray-500">Wing/Unit:</span> <span className="font-medium">{resident.admission?.wingUnit || resident.ward || '—'}</span></div>
                    <div><span className="text-gray-500">Status:</span> <Tooltip content={({
                      'Active': 'Currently in the facility receiving care',
                      'On Leave': 'Temporarily away on social or recreational leave',
                      'Hospital Transfer': 'Transferred to hospital for acute care',
                      'Discharged': 'Formally discharged from the facility',
                      'Deceased': 'Record retained for compliance purposes',
                      'Respite': 'Short-term respite care admission',
                    })[resident.admission?.residentStatus || 'Active'] || 'Non-standard status'} position="bottom"><span className={`font-bold cursor-help ${resident.admission?.residentStatus === 'Active' ? 'text-green-700' : 'text-orange-700'}`}>{resident.admission?.residentStatus || 'Active'}</span></Tooltip></div>
                    <div><span className="text-gray-500">Length of Stay:</span> <span className="font-medium">{resident.admission?.expectedLengthOfStay || '—'}</span></div>
                  </div>
                </div>
              </div>

              {/* Audit Info */}
              {resident.audit && (
                <div className="bg-gray-50 rounded-xl border p-4 text-xs text-gray-500">
                  <div className="flex flex-wrap gap-4">
                    <span>Created: {resident.audit.createdAt ? new Date(resident.audit.createdAt).toLocaleDateString() : '—'} by {resident.audit.createdBy || '—'}</span>
                    <span>Last Updated: {resident.audit.updatedAt ? new Date(resident.audit.updatedAt).toLocaleDateString() : '—'} by {resident.audit.updatedBy || '—'}</span>
                    <span>Version: {resident.audit.version || '—'}</span>
                    <span>Sensitivity: {resident.audit.dataSensitivityLevel || 'Standard'}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Admission & facility — expanded */}
          {activeTab === 'admission' && (() => {
            const adm = resident.admission || {};
            const admDate = adm.admissionDate || resident.admissionDate;
            const daysSinceAdmission = admDate ? Math.floor((new Date() - new Date(admDate)) / (1000 * 60 * 60 * 24)) : null;
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-violet-800 via-purple-600 to-indigo-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-violet-100/90">Placement &amp; pathway</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">Admission &amp; Facility</h2>
                <p className="mt-2 text-base text-violet-50/90">
                  Room, wing, admission pathway, discharge planning, and accommodation details for{' '}
                  <span className="font-semibold text-white">{profileData.personalInfo?.name}</span>.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-4">
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-violet-100/80">Admitted</span><span className="text-sm font-bold">{admDate || '—'}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-violet-100/80">Length of stay</span><span className="text-sm font-bold">{daysSinceAdmission != null ? `${daysSinceAdmission} days` : '—'}</span></div>
                  <Tooltip content={({
                    'Active': 'Currently in the facility receiving care',
                    'On Leave': 'Temporarily away on social or recreational leave',
                    'Hospital Transfer': 'Transferred to hospital for acute care',
                  })[adm.residentStatus || 'Active'] || 'Check admission details for more information'} position="bottom">
                    <div className="rounded-xl bg-black/20 px-4 py-3 cursor-help"><span className="block text-xs text-violet-100/80">Status</span><span className="text-sm font-bold">{adm.residentStatus || 'Active'}</span></div>
                  </Tooltip>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-violet-100/80">Room</span><span className="text-sm font-bold">{adm.roomNumber || resident.room || '—'} ({adm.roomType || '—'})</span></div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Admission Details */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Admission Details</h3>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    {[
                      ['Admission Date', admDate],
                      ['Admission Type', adm.admissionType],
                      ['Admission Source', adm.admissionSource],
                      ['Referring Organisation', adm.referringOrganisation],
                      ['Expected Length of Stay', adm.expectedLengthOfStay],
                      ['Resident Status', adm.residentStatus || 'Active'],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <dt className="text-xs text-gray-400">{label}</dt>
                        <dd className="font-semibold text-gray-800">{val || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {/* Facility Placement */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Facility Placement</h3>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    {[
                      ['Facility', profileData.personalInfo?.facility || adm.facilityId],
                      ['Wing / Unit', adm.wingUnit || resident.ward],
                      ['Room Number', adm.roomNumber || resident.room],
                      ['Bed Number', adm.bedNumber],
                      ['Room Type', adm.roomType],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <dt className="text-xs text-gray-400">{label}</dt>
                        <dd className="font-semibold text-gray-800">{val || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              {/* Discharge Information */}
              <div className="rounded-xl border bg-white p-5 shadow-sm">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Discharge &amp; Transfer Information</h3>
                {adm.dischargeDate || adm.dischargeReason || adm.dateOfDeath ? (
                  <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                    {adm.dischargeDate && <div><dt className="text-xs text-gray-400">Discharge Date</dt><dd className="font-semibold text-gray-800">{adm.dischargeDate}</dd></div>}
                    {adm.dischargeReason && <div><dt className="text-xs text-gray-400">Reason</dt><dd className="font-semibold text-gray-800">{adm.dischargeReason}</dd></div>}
                    {adm.dateOfDeath && <div><dt className="text-xs text-gray-400">Date of Death</dt><dd className="font-semibold text-gray-800">{adm.dateOfDeath}</dd></div>}
                  </dl>
                ) : (
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Resident is currently admitted. No discharge or transfer planned.</span>
                  </div>
                )}
              </div>

              {/* Leave & Movement Quick Link */}
              <div className="rounded-xl border border-cyan-200 bg-cyan-50/50 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">Leave &amp; Movements</h3>
                    <p className="text-xs text-gray-500 mt-0.5">View leave records, hospital transfers, and bed holds</p>
                  </div>
                  <button type="button" onClick={() => setActiveTab('leave')} className="px-4 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-lg hover:bg-cyan-700">View Leave Tab</button>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Address & contact network — enhanced */}
          {activeTab === 'contacts' && (() => {
            const sn = resident.supportNetwork || {};
            const ca = resident.contactAddress || {};
            const emergencyContacts = sn.emergencyContacts || [];
            const primaryContact = sn.primaryContact;
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-orange-700 via-amber-600 to-fuchsia-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-orange-100/90">Network &amp; reachability</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">Address &amp; Contact Network</h2>
                <p className="mt-2 text-base text-orange-50/90">
                  Resident contact information, emergency contacts, GP, representatives, and care team for{' '}
                  <span className="font-semibold text-white">{profileData.personalInfo?.name}</span>.
                </p>
              </div>

              {/* Primary & Emergency Contacts — prominent at top */}
              {(primaryContact || emergencyContacts.length > 0) && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50/50 p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-red-600 mb-4 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Emergency &amp; Primary Contacts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {primaryContact && (
                      <div className="rounded-lg bg-white border border-red-200 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded">PRIMARY</span>
                          <span className="font-bold text-gray-900">{primaryContact.name}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-400 text-xs block">Relationship</span><span className="font-medium">{primaryContact.relationship || '—'}</span></div>
                          <div><span className="text-gray-400 text-xs block">Phone</span><span className="font-medium">{primaryContact.phone || '—'}</span></div>
                          {primaryContact.email && <div className="col-span-2"><span className="text-gray-400 text-xs block">Email</span><span className="font-medium">{primaryContact.email}</span></div>}
                        </div>
                      </div>
                    )}
                    {emergencyContacts.map((ec, i) => (
                      <div key={i} className="rounded-lg bg-white border border-orange-200 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded">EMERGENCY {i + 1}</span>
                          <span className="font-bold text-gray-900">{ec.name}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-gray-400 text-xs block">Relationship</span><span className="font-medium">{ec.relationship || '—'}</span></div>
                          <div><span className="text-gray-400 text-xs block">Phone</span><span className="font-medium">{ec.phone || '—'}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Resident Address & Direct Contact */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-500" /> Resident Address
                  </h3>
                  {ca.homeAddress ? (
                    <div className="rounded-lg bg-orange-50 border border-orange-200 p-4 mb-4">
                      <p className="font-semibold text-gray-800 text-sm">{ca.homeAddress.street}</p>
                      <p className="text-sm text-gray-600">{ca.homeAddress.suburb}, {ca.homeAddress.state} {ca.homeAddress.postcode}</p>
                      {ca.homeAddress.country && <p className="text-xs text-gray-500">{ca.homeAddress.country}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">No address on file</p>
                  )}
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div><dt className="text-xs text-gray-400">Mobile</dt><dd className="font-semibold">{ca.phoneMobile || '—'}</dd></div>
                    <div><dt className="text-xs text-gray-400">Home Phone</dt><dd className="font-semibold">{ca.phoneHome || '—'}</dd></div>
                    <div className="col-span-2"><dt className="text-xs text-gray-400">Email</dt><dd className="font-semibold">{ca.email || '—'}</dd></div>
                  </dl>
                </div>

                {/* GP */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-purple-500" /> General Practitioner
                  </h3>
                  {sn.gp?.name ? (
                    <div className="rounded-lg bg-purple-50 border border-purple-200 p-4">
                      <p className="font-bold text-purple-900">{sn.gp.name}</p>
                      <dl className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <div><dt className="text-xs text-gray-400">Practice</dt><dd className="font-medium">{sn.gp.practice || '—'}</dd></div>
                        <div><dt className="text-xs text-gray-400">Phone</dt><dd className="font-medium">{sn.gp.phone || '—'}</dd></div>
                        {sn.gp.email && <div className="col-span-2"><dt className="text-xs text-gray-400">Email</dt><dd className="font-medium">{sn.gp.email}</dd></div>}
                      </dl>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No GP assigned</p>
                  )}
                </div>
              </div>

              {/* Authorised Representatives / POA */}
              {(sn.authorisedRepresentatives?.length > 0 || sn.poaMedical || sn.poaFinancial) && (
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Authorised Representatives &amp; Powers of Attorney</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {sn.poaMedical && (
                      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                        <span className="text-[10px] font-bold uppercase text-blue-600 block mb-1">POA — Medical</span>
                        <p className="font-semibold text-sm">{typeof sn.poaMedical === 'object' ? (sn.poaMedical.person || sn.poaMedical.name || '—') : sn.poaMedical}</p>
                        {typeof sn.poaMedical === 'object' && sn.poaMedical.documentId && <p className="text-xs text-gray-500">Doc: {sn.poaMedical.documentId}</p>}
                      </div>
                    )}
                    {sn.poaFinancial && (
                      <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                        <span className="text-[10px] font-bold uppercase text-green-600 block mb-1">POA — Financial</span>
                        <p className="font-semibold text-sm">{typeof sn.poaFinancial === 'object' ? (sn.poaFinancial.person || sn.poaFinancial.name || '—') : sn.poaFinancial}</p>
                        {typeof sn.poaFinancial === 'object' && sn.poaFinancial.documentId && <p className="text-xs text-gray-500">Doc: {sn.poaFinancial.documentId}</p>}
                      </div>
                    )}
                    {(sn.authorisedRepresentatives || []).map((rep, i) => (
                      <div key={i} className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                        <span className="text-[10px] font-bold uppercase text-indigo-600 block mb-1">{rep.type || 'Representative'}</span>
                        <p className="font-semibold text-sm">{rep.name}</p>
                        {rep.relationship && <p className="text-xs text-gray-500">{rep.relationship}</p>}
                        {rep.phone && <p className="text-xs text-gray-500">{rep.phone}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialists & Allied Health + Pharmacy */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {sn.specialists?.length > 0 && (
                  <div className="rounded-xl border bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Specialists</h3>
                    <div className="space-y-2">
                      {sn.specialists.map((spec, i) => (
                        <div key={i} className="rounded-lg bg-teal-50 border border-teal-200 p-3 flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-sm text-gray-900">{spec.name}</p>
                            <p className="text-xs text-teal-700">{spec.specialty}{spec.practice ? ` • ${spec.practice}` : ''}</p>
                          </div>
                          {spec.phone && <span className="text-xs text-gray-500">{spec.phone}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {sn.preferredPharmacy && (
                  <div className="rounded-xl border bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Preferred Pharmacy</h3>
                    <div className="rounded-lg bg-pink-50 border border-pink-200 p-4">
                      <p className="font-bold text-pink-900">{typeof sn.preferredPharmacy === 'object' ? (sn.preferredPharmacy.name || '—') : sn.preferredPharmacy}</p>
                      {sn.preferredPharmacy.phone && <p className="text-sm text-gray-600 mt-1">{sn.preferredPharmacy.phone}</p>}
                      {sn.preferredPharmacy.address && <p className="text-sm text-gray-600">{sn.preferredPharmacy.address}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Medical Tab — bound to resident.clinical structured data */}
          {activeTab === 'medical' && (() => {
            const c = resident.clinical || {};
            const allergies = Array.isArray(c.allergies) ? c.allergies : [];
            const secondary = Array.isArray(c.secondaryDiagnoses) ? c.secondaryDiagnoses : [];
            const immunisations = Array.isArray(c.immunisations) ? c.immunisations : [];
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-red-800 via-rose-600 to-pink-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-red-100/90">Health record</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">Medical History</h2>
                <p className="mt-2 text-base text-red-50/90">Diagnoses, allergies, and immunisation record for <span className="font-semibold text-white">{profileData.personalInfo?.name}</span>.</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-4">
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-red-100/80">Primary Dx</span><span className="text-sm font-bold">{c.primaryDiagnosis?.displayText || '—'}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-red-100/80">Secondary</span><span className="text-sm font-bold">{secondary.length} condition{secondary.length !== 1 ? 's' : ''}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-red-100/80">Allergies</span><span className="text-sm font-bold">{allergies.length} recorded</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-red-100/80">Immunisations</span><span className="text-sm font-bold">{immunisations.length} on file</span></div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Primary & Secondary Diagnoses */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Diagnoses</h3>
                  {c.primaryDiagnosis?.displayText && (
                    <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                        <span className="font-bold text-gray-900 text-sm">{c.primaryDiagnosis.displayText}</span>
                      </div>
                      {c.primaryDiagnosis.code && <p className="text-xs text-gray-500 mt-1 ml-4">ICD: {c.primaryDiagnosis.code}</p>}
                      <span className="ml-4 inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">Primary</span>
                    </div>
                  )}
                  {secondary.length > 0 ? (
                    <div className="space-y-2">
                      {secondary.map((dx, i) => (
                        <div key={i} className="rounded-lg bg-gray-50 border p-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-orange-400" />
                            <span className="font-semibold text-gray-800 text-sm">{dx.displayText || dx}</span>
                          </div>
                          {dx.code && <p className="text-xs text-gray-500 mt-0.5 ml-4">ICD: {dx.code}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No secondary diagnoses recorded.</p>
                  )}
                </div>

                {/* Allergies */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Allergies &amp; Sensitivities</h3>
                  {allergies.length > 0 ? (
                    <div className="space-y-2">
                      {allergies.map((a, i) => (
                        <div key={i} className={`rounded-lg p-3 border ${
                          a.severity === 'Severe' ? 'bg-red-50 border-red-200' :
                          a.severity === 'Moderate' ? 'bg-orange-50 border-orange-200' :
                          'bg-yellow-50 border-yellow-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-gray-900 text-sm">{a.allergen}</span>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                              a.severity === 'Severe' ? 'bg-red-200 text-red-800' :
                              a.severity === 'Moderate' ? 'bg-orange-200 text-orange-800' :
                              'bg-yellow-200 text-yellow-800'
                            }`}>{a.severity || 'Unknown'}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{a.reaction || '—'}</p>
                          <div className="flex gap-2 mt-1.5 flex-wrap">
                            {a.type && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{a.type}</span>}
                            {a.dateIdentified && <span className="text-[10px] text-gray-400">Since {a.dateIdentified}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border-2 border-dashed border-green-200 bg-green-50 p-6 text-center">
                      <CheckCircle className="mx-auto h-8 w-8 text-green-400 mb-2" />
                      <p className="text-sm font-medium text-green-700">No known allergies</p>
                    </div>
                  )}
                </div>

                {/* Immunisations */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Immunisations</h3>
                  {immunisations.length > 0 ? (
                    <div className="space-y-2">
                      {immunisations.map((imm, i) => (
                        <div key={i} className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-800 text-sm">{imm.name || imm}</span>
                            {imm.dueDate && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                new Date(imm.dueDate) < new Date() ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}>{new Date(imm.dueDate) < new Date() ? 'Overdue' : `Due ${imm.dueDate}`}</span>
                            )}
                          </div>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            {imm.dateGiven && <span>Given: {imm.dateGiven}</span>}
                            {imm.batchNumber && <span>Batch: {imm.batchNumber}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No immunisation records on file.</p>
                  )}
                </div>
              </div>

              {/* Clinical Alerts & Advance Care */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {(c.clinicalAlerts?.length > 0 || c.infectionStatus) && (
                  <div className="rounded-xl border border-red-200 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-red-500 mb-3">Clinical Alerts</h3>
                    {c.clinicalAlerts?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {c.clinicalAlerts.map((alert, i) => (
                          <span key={i} className="px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">{alert}</span>
                        ))}
                      </div>
                    )}
                    {c.infectionStatus && (
                      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm">
                        <span className="font-semibold text-amber-800">Infection status:</span>{' '}
                        {typeof c.infectionStatus === 'object'
                          ? [
                              c.infectionStatus.isolationRequired && 'Isolation required',
                              c.infectionStatus.mrsa && 'MRSA+',
                              c.infectionStatus.vre && 'VRE+',
                              c.infectionStatus.cdiff && 'C.diff+',
                              c.infectionStatus.currentInfections?.length > 0 && c.infectionStatus.currentInfections.join(', '),
                            ].filter(Boolean).join(', ') || 'No active infections'
                          : c.infectionStatus}
                      </div>
                    )}
                  </div>
                )}
                {(c.advanceCareDirective || c.resuscitationStatus || c.goalsOfCare) && (
                  <div className="rounded-xl border border-purple-200 bg-white p-5 shadow-sm">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-purple-500 mb-3">Advance Care</h3>
                    {c.advanceCareDirective && <div className="text-sm mb-2"><span className="text-gray-500">ACD:</span> <span className="font-medium">{typeof c.advanceCareDirective === 'object' ? (c.advanceCareDirective.summary || (c.advanceCareDirective.exists ? 'On file' : '—')) : c.advanceCareDirective}</span></div>}
                    {c.resuscitationStatus && (
                      <div className="text-sm mb-2">
                        <span className="text-gray-500">Resuscitation:</span>{' '}
                        <span className={`font-bold ${c.resuscitationStatus.includes('Not for') || c.resuscitationStatus.includes('Comfort') ? 'text-red-700' : 'text-green-700'}`}>{c.resuscitationStatus}</span>
                      </div>
                    )}
                    {c.goalsOfCare && <div className="text-sm"><span className="text-gray-500">Goals:</span> <span className="font-medium">{typeof c.goalsOfCare === 'string' ? c.goalsOfCare.slice(0, 150) : '—'}{typeof c.goalsOfCare === 'string' && c.goalsOfCare.length > 150 ? '…' : ''}</span></div>}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Clinical — diagnoses, vitals, allergies, advance care (not MAR / dispensing) */}
          {activeTab === 'clinical' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-rose-600 via-violet-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                      <Stethoscope className="w-7 h-7 shrink-0 opacity-95" aria-hidden />
                      Clinical profile
                    </h2>
                    <p className="opacity-90 max-w-2xl text-sm sm:text-base">
                      Diagnoses, allergies, vitals, mobility, infection, and advance care. Orders, administration, and the MAR live under the Medication tab.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => activateTab('medication', 'overview')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white font-medium transition-colors"
                    >
                      <Pill className="w-5 h-5 shrink-0" aria-hidden />
                      Medication overview
                    </button>
                    <button
                      type="button"
                      onClick={() => activateTab('medication', 'mar')}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-indigo-700 hover:bg-indigo-50 font-semibold transition-colors shadow-md"
                    >
                      <ClipboardList className="w-5 h-5 shrink-0" aria-hidden />
                      Open MAR
                    </button>
                  </div>
                </div>
              </div>

              {(() => {
                const c = clinicalPanel.c;
                const primaryFromProfile = profileData?.medicalHistory?.primaryDiagnosis;
                const primaryList = Array.isArray(primaryFromProfile)
                  ? primaryFromProfile
                  : primaryFromProfile
                    ? [primaryFromProfile]
                    : [];
                const hasStructuredPrimary = Boolean(c.primaryDiagnosis?.displayText || c.primaryDiagnosis?.code);
                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <button
                        type="button"
                        onClick={() => activateTab('medication', 'current')}
                        className="text-left bg-white border-2 border-emerald-200 rounded-xl p-4 shadow-sm hover:border-emerald-400 hover:shadow transition-all"
                      >
                        <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Active medications</p>
                        <p className="text-2xl font-bold text-emerald-700 mt-1">{profileData.medications?.current?.length ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Open Medication tab</p>
                      </button>
                      <div className="bg-white border-2 border-orange-200 rounded-xl p-4 shadow-sm">
                        <p className="text-xs font-semibold text-orange-800 uppercase tracking-wide">Allergens on file</p>
                        <p className="text-2xl font-bold text-orange-700 mt-1">
                          {clinicalPanel.structuredAllergies.length + clinicalPanel.legacyOnlyAllergies.length}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Structured + legacy notes</p>
                      </div>
                      <div className="bg-white border-2 border-amber-200 rounded-xl p-4 shadow-sm">
                        <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Falls risk</p>
                        <p className="text-lg font-bold text-amber-800 mt-1">{c.fallsRiskRating?.level || '—'}</p>
                        {c.fallsRiskRating?.score != null && (
                          <p className="text-xs text-gray-500 mt-1">Score {c.fallsRiskRating.score}</p>
                        )}
                      </div>
                      <div className="bg-white border-2 border-slate-200 rounded-xl p-4 shadow-sm">
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Resuscitation</p>
                        <p className="text-sm font-bold text-slate-900 mt-1 leading-snug">{c.resuscitationStatus || '—'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-violet-600" aria-hidden />
                          Diagnoses
                        </h3>
                        <div className="space-y-3">
                          {hasStructuredPrimary ? (
                            <div className="p-4 rounded-xl bg-violet-50 border border-violet-200">
                              <p className="text-xs font-semibold text-violet-800 uppercase">Primary</p>
                              <p className="font-semibold text-gray-900 mt-1">{c.primaryDiagnosis.displayText}</p>
                              {c.primaryDiagnosis.code && (
                                <p className="text-sm text-gray-600 font-mono mt-0.5">{c.primaryDiagnosis.code}</p>
                              )}
                            </div>
                          ) : primaryList.length > 0 ? (
                            <div className="p-4 rounded-xl bg-violet-50 border border-violet-200">
                              <p className="text-xs font-semibold text-violet-800 uppercase">Primary (summary)</p>
                              <ul className="mt-2 space-y-1 text-gray-900">
                                {primaryList.map((d, i) => (
                                  <li key={i} className="text-sm">{typeof d === 'string' ? d : d?.displayText || d?.condition || JSON.stringify(d)}</li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No primary diagnosis recorded in the clinical record.</p>
                          )}
                          {(c.secondaryDiagnoses || []).length > 0 ? (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Secondary</p>
                              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                {c.secondaryDiagnoses.map((d, i) => (
                                  <li key={i} className="text-sm border border-gray-100 rounded-lg p-3 bg-gray-50">
                                    <span className="font-medium text-gray-900">{d.displayText}</span>
                                    {d.code && <span className="text-gray-500 font-mono ml-2">{d.code}</span>}
                                    {d.onsetDate && (
                                      <span className="block text-xs text-gray-500 mt-1">Onset {d.onsetDate}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-orange-600" aria-hidden />
                          Allergies &amp; alerts
                        </h3>
                        {(c.clinicalAlerts || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {(c.clinicalAlerts || []).map((alert, i) => (
                              <span
                                key={i}
                                className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-200"
                              >
                                {alert}
                              </span>
                            ))}
                          </div>
                        )}
                        {clinicalPanel.structuredAllergies.length === 0 && clinicalPanel.legacyOnlyAllergies.length === 0 ? (
                          <p className="text-sm text-gray-500">No allergies documented.</p>
                        ) : (
                          <ul className="space-y-3">
                            {clinicalPanel.structuredAllergies.map((a, i) => (
                              <li
                                key={`s-${i}`}
                                className="p-3 rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-red-50"
                              >
                                <p className="font-semibold text-gray-900">{a.allergen}</p>
                                <p className="text-xs text-gray-600 mt-1">
                                  {[a.type, a.severity, a.reaction].filter(Boolean).join(' · ') || 'Details not specified'}
                                </p>
                                {a.dateIdentified && (
                                  <p className="text-xs text-gray-500 mt-1">Identified {a.dateIdentified}</p>
                                )}
                              </li>
                            ))}
                            {clinicalPanel.legacyOnlyAllergies.map((item, i) => (
                              <li
                                key={`l-${i}`}
                                className="p-3 rounded-xl border border-dashed border-orange-300 bg-orange-50/80"
                              >
                                <p className="text-xs font-semibold text-orange-800 uppercase mb-1">Also noted (legacy summary)</p>
                                <p className="font-medium text-gray-900">{typeof item === 'string' ? item : item?.allergen || item?.name}</p>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Heart className="w-5 h-5 text-rose-600" aria-hidden />
                        Vitals &amp; status
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                          <p className="text-xs text-gray-500 uppercase font-semibold">Weight</p>
                          <p className="text-xl font-bold text-gray-900 mt-1">{c.weightKg != null ? `${c.weightKg} kg` : '—'}</p>
                        </div>
                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                          <p className="text-xs text-gray-500 uppercase font-semibold">Height</p>
                          <p className="text-xl font-bold text-gray-900 mt-1">{c.heightCm != null ? `${c.heightCm} cm` : '—'}</p>
                        </div>
                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                          <p className="text-xs text-gray-500 uppercase font-semibold">BMI</p>
                          <p className="text-xl font-bold text-gray-900 mt-1">{clinicalPanel.computedBmi != null ? clinicalPanel.computedBmi : '—'}</p>
                        </div>
                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                          <p className="text-xs text-gray-500 uppercase font-semibold">Blood type</p>
                          <p className="text-xl font-bold text-gray-900 mt-1">{c.bloodType || profileData.personalInfo?.bloodType || '—'}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                          <p className="text-xs text-blue-800 uppercase font-semibold">Pain</p>
                          <p className="text-lg font-bold text-blue-900 mt-1">
                            {c.painLevel?.currentScore != null ? `${c.painLevel.currentScore} (${c.painLevel.toolUsed || 'scale'})` : '—'}
                          </p>
                          {c.painLevel?.lastAssessed && (
                            <p className="text-xs text-blue-700 mt-1">
                              Last {new Date(c.painLevel.lastAssessed).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl bg-teal-50 border border-teal-100 p-4">
                          <p className="text-xs text-teal-800 uppercase font-semibold">Mobility</p>
                          <p className="text-sm font-bold text-teal-900 mt-1">{c.mobilityStatus || '—'}</p>
                        </div>
                        <div className="rounded-xl bg-purple-50 border border-purple-100 p-4">
                          <p className="text-xs text-purple-800 uppercase font-semibold">Cognition</p>
                          <p className="text-sm font-bold text-purple-900 mt-1">{c.cognitiveStatus || '—'}</p>
                          {c.dementiaDiagnosis?.diagnosed && (
                            <p className="text-xs text-purple-700 mt-1">Dementia: {c.dementiaDiagnosis.type || 'Documented'}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Continence &amp; pressure</h3>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Bladder:</span> {c.continenceStatus?.bladder || '—'}
                        </p>
                        <p className="text-sm text-gray-700 mt-2">
                          <span className="font-semibold">Bowel:</span> {c.continenceStatus?.bowel || '—'}
                        </p>
                        {(c.continenceStatus?.aids || []).length > 0 && (
                          <p className="text-sm text-gray-600 mt-2">Aids: {(c.continenceStatus.aids || []).join(', ')}</p>
                        )}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">Pressure injury</p>
                          <p className="text-sm text-gray-700 mt-1">
                            {c.pressureInjuryStatus?.present
                              ? `Present — stage ${c.pressureInjuryStatus.stage || '?'} (${(c.pressureInjuryStatus.locations || []).join(', ') || 'sites not listed'})`
                              : 'None recorded'}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Infection &amp; isolation</h3>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Isolation required:</span>{' '}
                          {c.infectionStatus?.isolationRequired ? 'Yes' : 'No'}
                        </p>
                        <p className="text-sm text-gray-700 mt-2">
                          Flags: MRSA {c.infectionStatus?.mrsa ? 'Yes' : 'No'} · VRE {c.infectionStatus?.vre ? 'Yes' : 'No'} · C. diff{' '}
                          {c.infectionStatus?.cdiff ? 'Yes' : 'No'}
                        </p>
                        {(c.infectionStatus?.currentInfections || []).length > 0 && (
                          <ul className="mt-3 text-sm text-gray-800 list-disc list-inside">
                            {c.infectionStatus.currentInfections.map((x, i) => (
                              <li key={i}>{typeof x === 'string' ? x : x?.name || x?.label || JSON.stringify(x)}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-indigo-600" aria-hidden />
                          Advance care &amp; goals
                        </h3>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Advance care directive:</span>{' '}
                          {c.advanceCareDirective?.exists ? 'On file' : 'Not recorded'}
                        </p>
                        {c.advanceCareDirective?.reviewDate && (
                          <p className="text-xs text-gray-500 mt-1">Review due {c.advanceCareDirective.reviewDate}</p>
                        )}
                        {c.advanceCareDirective?.summary && (
                          <p className="text-sm text-gray-700 mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">{c.advanceCareDirective.summary}</p>
                        )}
                        {c.goalsOfCare && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Goals of care</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.goalsOfCare}</p>
                          </div>
                        )}
                      </div>
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-emerald-600" aria-hidden />
                          Immunisations
                        </h3>
                        {(c.immunisations || []).length === 0 ? (
                          <p className="text-sm text-gray-500">No immunisations listed on the clinical record.</p>
                        ) : (
                          <ul className="space-y-2 max-h-56 overflow-y-auto">
                            {(c.immunisations || []).map((im, i) => (
                              <li key={i} className="text-sm border border-gray-100 rounded-lg p-3 bg-emerald-50/50">
                                <span className="font-semibold text-gray-900">{im.vaccine}</span>
                                {im.dateGiven && <span className="text-gray-600 ml-2">Given {im.dateGiven}</span>}
                                {im.nextDue && <span className="block text-xs text-emerald-800 mt-1">Next due {im.nextDue}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {clinicalPanel.recentWeights.length > 0 && (
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Recent weights</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b">
                                <th className="py-2 pr-4">Date</th>
                                <th className="py-2 pr-4">Weight (kg)</th>
                                <th className="py-2">Measured by</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clinicalPanel.recentWeights.map((w, i) => (
                                <tr key={i} className="border-b border-gray-50">
                                  <td className="py-2 pr-4">{w.date || '—'}</td>
                                  <td className="py-2 pr-4 font-medium">{w.weightKg != null ? w.weightKg : '—'}</td>
                                  <td className="py-2 text-gray-600">{w.measuredBy || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {(c.restrictivePractices || []).length > 0 && (
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
                        <h3 className="text-lg font-bold text-amber-900 mb-2">Restrictive practices</h3>
                        <ul className="text-sm text-amber-950 space-y-2">
                          {c.restrictivePractices.map((rp, i) => (
                            <li key={i} className="font-medium">
                              {rp.type || 'Practice'} — review {rp.reviewDate || 'TBC'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Medication — orders, MAR, schedule, interactions, compliance */}
          {activeTab === 'medication' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold mb-2">Medication &amp; administration</h2>
                <p className="opacity-90">
                  Charts, schedules, MAR, and compliance for {profileData.personalInfo.name}. Clinical diagnoses and vitals are under the Clinical tab.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="border-b border-gray-200">
                  <nav className="flex flex-wrap space-x-8 px-6">
                    {[
                      { id: 'overview', label: 'Overview', icon: '📊' },
                      { id: 'current', label: 'Current Meds', icon: '💊' },
                      { id: 'mar', label: 'MAR Records', icon: '📋' },
                      { id: 'schedule', label: 'Schedule', icon: '⏰' },
                      { id: 'history', label: 'History', icon: '📋' },
                      { id: 'interactions', label: 'Interactions', icon: '⚠️' },
                      { id: 'compliance', label: 'Compliance', icon: '✅' }
                    ].map(tabItem => (
                      <button
                        key={tabItem.id}
                        onClick={() => setMedicationTab(tabItem.id)}
                        className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                          medicationTab === tabItem.id
                            ? 'border-green-500 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-lg">{tabItem.icon}</span>
                        <span>{tabItem.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                <div className="p-6">
                  {/* Medication Overview */}
                  {medicationTab === 'overview' && (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border-2 border-green-200 shadow-lg">
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                              <Pill className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-green-700">{profileData.medications?.current?.length || 0}</div>
                              <div className="text-sm text-green-600">Active Medications</div>
                            </div>
                          </div>
                          <p className="text-sm text-green-700 font-medium">Currently prescribed</p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-2xl p-6 border-2 border-blue-200 shadow-lg">
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                              <CheckCircle className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-blue-700">94%</div>
                              <div className="text-sm text-blue-600">Compliance Rate</div>
                            </div>
                          </div>
                          <p className="text-sm text-blue-700 font-medium">Excellent adherence</p>
                        </div>

                        <div className="bg-gradient-to-br from-orange-50 to-amber-100 rounded-2xl p-6 border-2 border-orange-200 shadow-lg">
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                              <AlertTriangle className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-orange-700">{profileData.medications?.allergies?.length || 0}</div>
                              <div className="text-sm text-orange-600">Known Allergies</div>
                            </div>
                          </div>
                          <p className="text-sm text-orange-700 font-medium">Monitor closely</p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                              <Clock className="w-6 h-6 text-white" />
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-purple-700">3x</div>
                              <div className="text-sm text-purple-600">Daily Doses</div>
                            </div>
                          </div>
                          <p className="text-sm text-purple-700 font-medium">Regular schedule</p>
                        </div>
                      </div>

                      {/* Recent Medication Activity */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <Activity className="w-6 h-6 text-green-600" />
                          Recent Medication Activity
                        </h3>
                        <div className="space-y-4">
                          {[
                            { action: 'Metformin 500mg taken', time: '2 hours ago', status: 'completed', icon: CheckCircle },
                            { action: 'Donepezil 10mg taken', time: '6 hours ago', status: 'completed', icon: CheckCircle },
                            { action: 'Blood glucose checked', time: '8 hours ago', status: 'completed', icon: Activity },
                            { action: 'Medication review scheduled', time: 'Tomorrow', status: 'pending', icon: Calendar }
                          ].map((activity, index) => (
                            <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                activity.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                              }`}>
                                <activity.icon className={`w-5 h-5 ${
                                  activity.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                                }`} />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{activity.action}</p>
                                <p className="text-sm text-gray-500">{activity.time}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                activity.status === 'completed' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {activity.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <Settings className="w-6 h-6 text-blue-600" />
                          Quick Actions
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <button 
                            type="button"
                            onClick={() => setShowAddMedModal(true)}
                            className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                          >
                            <Plus className="w-5 h-5" />
                            <span className="font-medium">Add Medication</span>
                          </button>
                          <button 
                            type="button"
                            onClick={() => activateTab('medication', 'current')}
                            className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                          >
                            <Edit className="w-5 h-5" />
                            <span className="font-medium">Update Dosage</span>
                          </button>
                          <button 
                            onClick={() => actions.setActiveTab('reports')}
                            className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-xl hover:from-purple-600 hover:to-violet-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                          >
                            <FileText className="w-5 h-5" />
                            <span className="font-medium">Generate Report</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MAR Records */}
                  {medicationTab === 'mar' && (
                    <MARDashboard 
                      participantId={resident.id}
                      medication={null}
                    />
                  )}

                  {/* Current Medications */}
                  {medicationTab === 'current' && (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <Pill className="w-6 h-6 text-green-600" />
                        Current Medications
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {(profileData.medications?.current || []).map((med, index) => (
                          <div 
                            key={index} 
                            className="group relative bg-gradient-to-br from-white via-green-50 to-emerald-50 rounded-2xl border-2 border-green-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:border-green-300"
                            style={{
                              animationDelay: `${index * 150}ms`,
                              animation: 'fadeInUp 0.8s ease-out forwards'
                            }}
                          >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-6">
                              <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                                  <Pill className="w-8 h-8 text-white" />
                                </div>
                            <div>
                                  <h4 className="text-2xl font-bold text-gray-900 mb-1">{med.medication}</h4>
                                  <p className="text-sm text-green-600 font-medium">{med.dosage}</p>
                                  <p className="text-sm text-gray-500">Prescribed by {med.doctor}</p>
                            </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <span className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-bold rounded-full shadow-lg">
                              {med.status}
                            </span>
                                <div className="text-center">
                                  <div className="text-lg font-bold text-green-600">{med.adherenceRate}%</div>
                                  <div className="text-xs text-gray-500">Adherence</div>
                          </div>
                              </div>
                            </div>

                            {/* Medication Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm">
                                <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  Medication Info
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                              <span className="text-gray-600">Start Date:</span>
                                    <span className="font-medium">{new Date(med.startDate).toLocaleDateString()}</span>
                            </div>
                                  <div className="flex justify-between">
                              <span className="text-gray-600">Next Review:</span>
                                    <span className="font-medium">{new Date(med.nextReview).toLocaleDateString()}</span>
                            </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Refills Left:</span>
                                    <span className="font-medium text-green-600">{med.refillsLeft}/{med.totalRefills}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
                                <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  Instructions & Effects
                                </h5>
                                <div className="space-y-2 text-sm">
                            <div>
                                    <span className="text-gray-600 font-medium">Instructions:</span>
                                    <p className="text-gray-700 mt-1">{med.instructions}</p>
                            </div>
                            <div>
                                    <span className="text-gray-600 font-medium">Side Effects:</span>
                                    <p className="text-gray-700 mt-1">{med.sideEffects}</p>
                            </div>
                          </div>
                              </div>
                            </div>

                            {/* Medication Management Controls */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border-2 border-blue-200">
                              <h5 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Settings className="w-5 h-5 text-blue-600" />
                                Doctor Controls
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Auto Refill:</span>
                                    <button 
                                      onClick={() => {
                                        // Toggle auto refill
                                        console.log('Toggle auto refill for', med.name);
                                      }}
                                      className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                                        med.autoRefill 
                                          ? 'bg-green-500 text-white' 
                                          : 'bg-gray-300 text-gray-600'
                                      }`}
                                    >
                                      {med.autoRefill ? 'ON' : 'OFF'}
                                    </button>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Reminders:</span>
                                    <button 
                                      onClick={() => {
                                        // Toggle reminders
                                        console.log('Toggle reminders for', med.name);
                                      }}
                                      className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                                        med.reminderEnabled 
                                          ? 'bg-blue-500 text-white' 
                                          : 'bg-gray-300 text-gray-600'
                                      }`}
                                    >
                                      {med.reminderEnabled ? 'ON' : 'OFF'}
                                    </button>
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Next Dose:</span>
                                    <span className="text-sm font-bold text-blue-600">{med.nextDose}</span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">Last Taken:</span>
                                    <span className="text-sm font-bold text-green-600">{med.lastTaken}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Medication History & Interactions */}
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="bg-white rounded-xl p-4 border border-yellow-100 shadow-sm">
                                <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                  Drug Interactions
                                </h5>
                                <div className="space-y-2">
                                  {med.interactions.map((interaction, idx) => (
                                    <div key={idx} className="text-sm text-gray-700 bg-yellow-50 p-2 rounded-lg">
                                      ⚠️ {interaction}
                        </div>
                      ))}
                                </div>
                              </div>

                              <div className="bg-white rounded-xl p-4 border border-red-100 shadow-sm">
                                <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                  Contraindications
                                </h5>
                                <div className="space-y-2">
                                  {med.contraindications.map((contraindication, idx) => (
                                    <div key={idx} className="text-sm text-gray-700 bg-red-50 p-2 rounded-lg">
                                      🚫 {contraindication}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons - Bottom of Tile */}
                            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                              <button 
                                onClick={() => handleEditMedication(med)}
                                className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-3 px-4 rounded-xl hover:from-blue-600 hover:to-cyan-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                              <button 
                                onClick={() => handleRefillMedication(med)}
                                className="bg-gradient-to-r from-purple-500 to-violet-600 text-white py-3 px-4 rounded-xl hover:from-purple-600 hover:to-violet-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Refill
                              </button>
                              <button 
                                onClick={() => handleScheduleMedication(med)}
                                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                              >
                                <Clock className="w-4 h-4" />
                                Schedule
                              </button>
                              <button 
                                onClick={() => handleReportMedication(med)}
                                className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-4 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                              >
                                <AlertTriangle className="w-4 h-4" />
                                Report
                              </button>
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() => { setDiscontinueMedTarget(med); setShowDiscontinueMedModal(true); }}
                                className="w-full py-2 px-4 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Discontinue Medication
                              </button>
                            </div>

                            {/* Hover Effect Overlay */}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-500/0 to-emerald-500/0 group-hover:from-green-500/5 group-hover:to-emerald-500/5 transition-all duration-500 pointer-events-none"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Medication Schedule */}
                  {medicationTab === 'schedule' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Medication Schedule</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">Morning (8:00 AM)</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Metformin 500mg</span>
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Lisinopril 10mg</span>
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </div>
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-semibold text-green-900 mb-2">Afternoon (2:00 PM)</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Metformin 500mg</span>
                              <Clock className="w-4 h-4 text-yellow-600" />
                            </div>
                          </div>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="font-semibold text-purple-900 mb-2">Evening (8:00 PM)</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Vitamin D3</span>
                              <Clock className="w-4 h-4 text-yellow-600" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Medication History */}
                  {medicationTab === 'history' && (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <FileText className="w-6 h-6 text-purple-600" />
                        Medication History & Refills
                      </h3>
                      
                      {/* Refill History */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <RefreshCw className="w-5 h-5 text-blue-600" />
                          Refill History
                        </h4>
                        <div className="space-y-4">
                          {[
                            { medication: 'Donepezil 10mg', date: '2024-01-15', quantity: 30, doctor: 'Dr. Robert Smith', status: 'Completed', cost: '$12.50' },
                            { medication: 'Metformin 500mg', date: '2024-01-10', quantity: 60, doctor: 'Dr. Sarah Johnson', status: 'Completed', cost: '$8.75' },
                            { medication: 'Lisinopril 10mg', date: '2024-01-05', quantity: 30, doctor: 'Dr. Michael Chen', status: 'Completed', cost: '$7.50' },
                            { medication: 'Donepezil 10mg', date: '2023-12-15', quantity: 30, doctor: 'Dr. Robert Smith', status: 'Completed', cost: '$12.50' },
                            { medication: 'Metformin 500mg', date: '2023-12-10', quantity: 60, doctor: 'Dr. Sarah Johnson', status: 'Completed', cost: '$8.75' }
                          ].map((refill, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                                  <Pill className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <h5 className="font-bold text-gray-900">{refill.medication}</h5>
                                  <p className="text-sm text-gray-600">{refill.quantity} tablets • {refill.date}</p>
                                  <p className="text-xs text-gray-500">Prescribed by {refill.doctor}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-bold rounded-full">
                                  {refill.status}
                                </span>
                                <p className="text-sm font-bold text-gray-900 mt-1">{refill.cost}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Medication Changes */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <Edit className="w-5 h-5 text-orange-600" />
                          Recent Changes
                        </h4>
                        <div className="space-y-4">
                          {[
                            { action: 'Dosage increased', medication: 'Metformin', from: '250mg', to: '500mg', date: '2024-01-20', doctor: 'Dr. Sarah Johnson' },
                            { action: 'New medication added', medication: 'Lisinopril 10mg', from: 'N/A', to: '10mg daily', date: '2023-08-15', doctor: 'Dr. Michael Chen' },
                            { action: 'Schedule adjusted', medication: 'Donepezil', from: 'Morning', to: 'Bedtime', date: '2023-06-01', doctor: 'Dr. Robert Smith' },
                            { action: 'Medication discontinued', medication: 'Ibuprofen 400mg', from: '400mg PRN', to: 'Discontinued', date: '2023-10-15', doctor: 'Dr. Michael Chen' }
                          ].map((change, index) => (
                            <div key={index} className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                                <Edit className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <h5 className="font-bold text-gray-900">{change.action}</h5>
                                <p className="text-sm text-gray-600">{change.medication}: {change.from} → {change.to}</p>
                                <p className="text-xs text-gray-500">{change.date} • {change.doctor}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Upcoming Refills */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h4 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-green-600" />
                          Upcoming Refills
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                            { medication: 'Donepezil 10mg', dueDate: '2024-02-15', daysLeft: 22, repeatsLeft: 2, autoRefill: true },
                            { medication: 'Metformin 500mg', dueDate: '2024-02-10', daysLeft: 17, repeatsLeft: 1, autoRefill: true },
                            { medication: 'Lisinopril 10mg', dueDate: '2024-02-05', daysLeft: 12, repeatsLeft: 4, autoRefill: false }
                          ].map((refill, index) => (
                            <div key={index} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-bold text-gray-900">{refill.medication}</h5>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  refill.autoRefill ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                                }`}>
                                  {refill.autoRefill ? 'AUTO' : 'MANUAL'}
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Due Date:</span>
                                  <span className="font-bold text-green-600">{refill.dueDate}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Days Left:</span>
                                  <span className="font-bold text-blue-600">{refill.daysLeft} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Repeats Left:</span>
                                  <span className="font-bold text-purple-600">{refill.repeatsLeft}</span>
                                </div>
                              </div>
                              <button 
                                onClick={() => {
                                  console.log('Order refill for', refill.medication);
                                }}
                                className="w-full mt-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2 px-4 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 font-semibold"
                              >
                                Order Refill
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Drug Interactions */}
                  {medicationTab === 'interactions' && (
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                        Drug Interactions & Safety
                      </h3>
                      
                      {/* High Risk Interactions */}
                      <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border-2 border-red-200 p-6 shadow-lg">
                        <h4 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5" />
                          High Risk Interactions
                        </h4>
                        <div className="space-y-4">
                          {[
                            { 
                              medications: 'Donepezil + Anticholinergic drugs', 
                              severity: 'High', 
                              effect: 'Reduced effectiveness of Donepezil',
                              recommendation: 'Monitor cognitive function closely',
                              status: 'Active'
                            },
                            { 
                              medications: 'Metformin + Contrast agents', 
                              severity: 'High', 
                              effect: 'Risk of lactic acidosis',
                              recommendation: 'Discontinue 48 hours before contrast studies',
                              status: 'Potential'
                            }
                          ].map((interaction, index) => (
                            <div key={index} className="bg-white rounded-xl p-4 border border-red-200">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-bold text-gray-900">{interaction.medications}</h5>
                                <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full">
                                  {interaction.severity} RISK
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-2"><strong>Effect:</strong> {interaction.effect}</p>
                              <p className="text-sm text-gray-700 mb-2"><strong>Recommendation:</strong> {interaction.recommendation}</p>
                              <div className="flex justify-between items-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  interaction.status === 'Active' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {interaction.status}
                                </span>
                                <button 
                                  onClick={() => {
                                    console.log('View details for', alert.type);
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  View Details →
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Allergies */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg">
                        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-orange-600" />
                          Known Allergies
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {(profileData.medicalHistory?.allergies || []).map((allergy, index) => (
                            <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
                              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h5 className="font-bold text-gray-900">{allergy}</h5>
                                <p className="text-sm text-orange-700">Severe allergic reaction</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Allergies */}
                  {medicationTab === 'allergies' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Known Allergies</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(profileData.medicalHistory?.allergies || []).map((allergy, index) => (
                          <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center">
                              <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
                              <div>
                                <h4 className="font-semibold text-red-900">{allergy}</h4>
                                <p className="text-sm text-red-700">Severe allergic reaction</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Compliance */}
                  {medicationTab === 'compliance' && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold">Medication Compliance</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-green-600">{profileData.medications.compliance.rate}%</div>
                            <div className="text-sm text-green-600">Overall Compliance</div>
                          </div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-blue-600">{profileData.medications.compliance.daysThisMonth}</div>
                            <div className="text-sm text-blue-600">Days This Month</div>
                          </div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="text-center">
                            <div className="text-3xl font-bold text-orange-600">{profileData.medications.compliance.missedDoses}</div>
                            <div className="text-sm text-orange-600">Missed Doses</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other medication tabs */}
                  {!['current', 'mar', 'schedule', 'allergies', 'compliance'].includes(medicationTab) && (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-4">💊</div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">{medicationTab.charAt(0).toUpperCase() + medicationTab.slice(1)}</h3>
                      <p className="text-gray-500">Detailed {medicationTab} information and management</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Care Plan & Activities — Full 18-section care plan system */}
          {activeTab === 'care-plan' && (
            <CarePlansComponent residentId={String(resident.id)} />
          )}
          {/* LEGACY_CARE_PLAN_REMOVED — replaced by CarePlansComponent above */}
          {false && (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-teal-800 via-teal-600 to-violet-700 p-8 text-white shadow-2xl">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-widest text-teal-100/90">Person-centred care</p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight">Care plan &amp; activities</h2>
                    <p className="mt-3 text-base leading-relaxed text-teal-50/95">
                      Measurable goals, review discipline, activity matching, and allied health — for{' '}
                      <span className="font-semibold text-white">{profileData.personalInfo.name}</span>.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => activateTab('assessments')}
                      className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/35 backdrop-blur hover:bg-white/25"
                    >
                      Assessments
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('clinical')}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-teal-900 shadow-md hover:bg-teal-50"
                    >
                      Clinical profile
                    </button>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl bg-black/20 px-4 py-3">
                    <span className="block text-xs font-semibold uppercase text-teal-100/80">Next review target</span>
                    <span className="text-lg font-bold">{resident.assessments?.carePlanReviewSchedule || 'Set from care plan'}</span>
                  </div>
                  <div className="rounded-xl bg-black/20 px-4 py-3">
                    <span className="block text-xs font-semibold uppercase text-teal-100/80">Summary care plan</span>
                    <span className="text-lg font-bold">{resident.assessments?.summaryCarePlan ? 'On file' : 'Pending issue'}</span>
                  </div>
                  <div className="rounded-xl bg-black/20 px-4 py-3">
                    <span className="block text-xs font-semibold uppercase text-teal-100/80">Booked sessions</span>
                    <span className="text-lg font-bold">{(profileData.activities?.scheduled || []).length} upcoming</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-4 shadow-sm ring-1 ring-teal-100">
                  <div className="flex items-center gap-2 text-teal-900">
                    <ClipboardCheck className="h-5 w-5 shrink-0 text-teal-600" />
                    <span className="text-xs font-bold uppercase tracking-wide">Goals</span>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-teal-800">{resident.clinical?.goalsOfCare ? 'Active' : 'Draft'}</p>
                  <p className="text-xs text-teal-700/80">Linked to assessments</p>
                </div>
                <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm ring-1 ring-violet-100">
                  <div className="flex items-center gap-2 text-violet-900">
                    <Calendar className="h-5 w-5 shrink-0 text-violet-600" />
                    <span className="text-xs font-bold uppercase tracking-wide">Review</span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm font-bold text-violet-900">{resident.assessments?.carePlanReviewSchedule || '—'}</p>
                  <p className="text-xs text-violet-700/80">Min. 8-weekly</p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm ring-1 ring-blue-100">
                  <div className="flex items-center gap-2 text-blue-900">
                    <Users className="h-5 w-5 shrink-0 text-blue-600" />
                    <span className="text-xs font-bold uppercase tracking-wide">Team</span>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-blue-800">{(resident.supportNetwork?.gp ? 1 : 0) + (resident.supportNetwork?.specialists?.length || 0) + (resident.supportNetwork?.alliedHealth?.length || 0)}</p>
                  <p className="text-xs text-blue-700/80">Named care roles</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm ring-1 ring-amber-100">
                  <div className="flex items-center gap-2 text-amber-900">
                    <Activity className="h-5 w-5 shrink-0 text-amber-600" />
                    <span className="text-xs font-bold uppercase tracking-wide">Interests</span>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-amber-800">{resident.demographics?.hobbiesInterests?.length || 0}</p>
                  <p className="text-xs text-amber-800/80">For activity matching</p>
                </div>
              </div>

              {careHistoryDemo?.carePlanVersions?.length ? (
                <div className="rounded-2xl border border-violet-200 bg-white p-6 shadow-md">
                  <div className="mb-4 flex flex-col gap-1 border-b border-violet-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                      <FileStack className="h-5 w-5 text-violet-600" />
                      Care plan version history
                    </h3>
                    <span className="text-xs font-medium text-gray-500">Demo timeline + compliance register slice</span>
                  </div>
                  <ul className="space-y-3">
                    {careHistoryDemo.carePlanVersions.map((v) => (
                      <li
                        key={v.version}
                        className="flex flex-col gap-1 rounded-xl border border-violet-100 bg-violet-50/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div>
                          <span className="font-mono text-sm font-bold text-violet-900">{v.version}</span>
                          <p className="text-sm text-gray-800 mt-0.5">{v.summary}</p>
                          <p className="text-xs text-gray-500">Author: {v.author}</p>
                        </div>
                        <span className="text-xs font-semibold text-violet-700 whitespace-nowrap">Effective {v.effectiveDate}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs text-gray-500">
                    Scheduled review target:{' '}
                    <span className="font-semibold text-gray-700">{resident.assessments?.carePlanReviewSchedule || '—'}</span>
                    . Full diff / task notifications remain backend-linked in production.
                  </p>
                </div>
              ) : null}

              <div className="rounded-2xl border border-teal-200 bg-white p-6 shadow-md">
                <div className="mb-4 flex flex-col gap-2 border-b border-teal-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                    <Target className="h-5 w-5 text-teal-600" />
                    Goals &amp; outcome statements
                  </h3>
                  <span className="text-xs font-medium text-gray-500">Parsed into cards when newline / bullet separated</span>
                </div>
                {carePlanGoalLines.length > 0 ? (
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {carePlanGoalLines.map((line, i) => (
                      <li
                        key={i}
                        className="flex gap-3 rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50/90 to-white p-4 shadow-sm"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white">
                          {i + 1}
                        </span>
                        <span className="text-sm font-medium leading-snug text-gray-800">{line}</span>
                      </li>
                    ))}
                  </ul>
                ) : resident.clinical?.goalsOfCare ? (
                  <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-5 text-sm leading-relaxed text-gray-800 whitespace-pre-line">
                    {resident.clinical.goalsOfCare}
                  </div>
                ) : (
                  <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-12 text-center text-gray-500">
                    <ClipboardCheck className="mx-auto mb-3 h-14 w-14 text-gray-300" />
                    <p className="text-lg font-semibold text-gray-700">No goals captured yet</p>
                    <p className="mt-1 text-sm text-gray-500">Complete assessments, then author goals (production: workflow + sign-off).</p>
                    <button
                      type="button"
                      onClick={() => activateTab('assessments')}
                      className="mt-4 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-teal-700"
                    >
                      Go to assessments
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-violet-200 bg-white p-6 shadow-md">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
                  <Calendar className="h-5 w-5 text-violet-600" />
                  Upcoming activities (roster sample)
                </h3>
                {(profileData.activities?.scheduled || []).length > 0 ? (
                  <div className="space-y-3">
                    {(profileData.activities.scheduled || []).slice(0, 6).map((a) => (
                      <div
                        key={a.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-violet-100 bg-violet-50/50 px-4 py-3"
                      >
                        <div>
                          <p className="font-semibold text-gray-900">{a.name}</p>
                          <p className="text-xs text-gray-600">
                            {a.date} · {a.time} · {a.location} · {a.facilitator}
                          </p>
                          <p className="mt-1 text-xs text-violet-700">{a.description}</p>
                        </div>
                        <span className="rounded-full bg-violet-200/80 px-3 py-1 text-xs font-bold text-violet-900">{a.status}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No scheduled rows in demo data for this resident.</p>
                )}
              </div>

              {/* Activities & Wellbeing Section */}
              <div className="bg-white rounded-xl border-2 border-purple-200 p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <PartyPopper className="w-5 h-5 text-purple-600" />
                  Activities & Social Wellbeing
                </h3>
                
                {/* Hobbies & Interests */}
                {resident.demographics?.hobbiesInterests?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm">Registered Interests</h4>
                    <div className="flex flex-wrap gap-2">
                      {resident.demographics.hobbiesInterests.map((interest, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium border border-purple-300">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Music Preferences */}
                {resident.demographics?.musicPreferences && (
                  <div className="mb-6 bg-pink-50 p-4 rounded-lg border border-pink-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Music className="w-4 h-4 text-pink-600" />
                      <span className="font-semibold text-pink-900 text-sm">Music Preferences</span>
                    </div>
                    <p className="text-pink-800 text-sm">{resident.demographics.musicPreferences}</p>
                  </div>
                )}
                
                {/* Daily Routine Preferences */}
                {resident.demographics?.dailyRoutinePreferences && (
                  <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-900 text-sm">Daily Routine Preferences</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {resident.demographics.dailyRoutinePreferences.wakeTime && (
                        <div>
                          <span className="text-gray-500 block text-xs">Wake Time</span>
                          <span className="font-semibold text-blue-800">{resident.demographics.dailyRoutinePreferences.wakeTime}</span>
                        </div>
                      )}
                      {resident.demographics.dailyRoutinePreferences.showerPreference && (
                        <div>
                          <span className="text-gray-500 block text-xs">Shower</span>
                          <span className="font-semibold text-blue-800">{resident.demographics.dailyRoutinePreferences.showerPreference}</span>
                        </div>
                      )}
                      {resident.demographics.dailyRoutinePreferences.mealPreferences && (
                        <div>
                          <span className="text-gray-500 block text-xs">Meals</span>
                          <span className="font-semibold text-blue-800">{resident.demographics.dailyRoutinePreferences.mealPreferences}</span>
                        </div>
                      )}
                      {resident.demographics.dailyRoutinePreferences.bedtime && (
                        <div>
                          <span className="text-gray-500 block text-xs">Bedtime</span>
                          <span className="font-semibold text-blue-800">{resident.demographics.dailyRoutinePreferences.bedtime}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Comfort Items */}
                {resident.demographics?.comfortItems && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-4 h-4 text-yellow-600" />
                      <span className="font-semibold text-yellow-900 text-sm">Comfort Items</span>
                    </div>
                    <p className="text-yellow-800 text-sm">{resident.demographics.comfortItems}</p>
                  </div>
                )}
              </div>

              {/* Interventions & Support */}
              <div className="bg-white rounded-xl border-2 border-indigo-200 p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-indigo-600" />
                  Active Interventions & Support
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Allied Health */}
                  {resident.supportNetwork?.alliedHealth?.length > 0 && (
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-3 text-sm">Allied Health Services</h4>
                      <div className="space-y-2">
                        {resident.supportNetwork.alliedHealth.map((ah, idx) => (
                          <div key={idx} className="bg-white p-3 rounded border border-indigo-200 text-sm">
                            <div className="font-semibold text-indigo-900">{ah.discipline}</div>
                            <div className="text-xs text-gray-600">{ah.name} • {ah.practice}</div>
                            {ah.phone && <div className="text-xs text-gray-500">📞 {ah.phone}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Care Team — data-driven */}
                  <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                    <h4 className="font-semibold text-teal-900 mb-3 text-sm">Care Team</h4>
                    <div className="space-y-2 text-sm">
                      {resident.supportNetwork?.gp?.name && (
                        <div className="bg-white p-3 rounded border border-teal-200">
                          <span className="text-[10px] font-bold uppercase text-teal-600">GP</span>
                          <div className="font-semibold text-teal-900">{resident.supportNetwork.gp.name}</div>
                          {resident.supportNetwork.gp.practice && <div className="text-xs text-gray-500">{resident.supportNetwork.gp.practice}</div>}
                        </div>
                      )}
                      {(resident.supportNetwork?.specialists || []).slice(0, 3).map((spec, i) => (
                        <div key={i} className="bg-white p-3 rounded border border-teal-200">
                          <span className="text-[10px] font-bold uppercase text-teal-600">{spec.specialty || 'Specialist'}</span>
                          <div className="font-semibold text-teal-900">{spec.name}</div>
                        </div>
                      ))}
                      {(resident.supportNetwork?.alliedHealth || []).slice(0, 2).map((ah, i) => (
                        <div key={i} className="bg-white p-3 rounded border border-teal-200">
                          <span className="text-[10px] font-bold uppercase text-teal-600">{ah.discipline || 'Allied Health'}</span>
                          <div className="font-semibold text-teal-900">{ah.name}</div>
                        </div>
                      ))}
                      {!resident.supportNetwork?.gp?.name && !resident.supportNetwork?.specialists?.length && (
                        <p className="text-gray-500 text-xs">No care team members on file. Add via the <button type="button" className="underline text-teal-600" onClick={() => setActiveTab('contacts')}>Contacts</button> tab.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Behaviour Support Plan (if applicable) */}
              {resident.assessments?.behaviourSupportPlan && (
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl border-2 border-orange-300 p-6 shadow-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    <h3 className="text-lg font-bold text-orange-900">Behaviour Support Plan</h3>
                    <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-bold rounded-full">Required</span>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-orange-200">
                    <p className="text-gray-800 leading-relaxed">{resident.assessments.behaviourSupportPlan}</p>
                  </div>
                </div>
              )}

              {/* Palliative Care Plan (if applicable) */}
              {resident.clinical?.palliativeCarePlan && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-300 p-6 shadow-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <Heart className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-bold text-purple-900">Palliative Care Plan</h3>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-purple-200">
                    <p className="text-gray-800 leading-relaxed">{resident.clinical.palliativeCarePlan}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Assessments Tab (consolidated: Assessments + Quality + AN-ACC + Care Minutes) */}
          {activeTab === 'assessments' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold mb-2">Clinical Assessments</h2>
                <p className="opacity-90">Comprehensive assessments and evaluations for {profileData.personalInfo.name}</p>
              </div>

              <AssessmentLibraryPanel
                recordedAssessments={mergedAssessments}
                residentName={profileData.personalInfo?.name || ''}
                residentId={resident.id}
                context="resident"
                onQuickFormSaved={handleQuickFormSaved}
              />

              <MandatoryAssessmentCoverageGrid recordedAssessments={mergedAssessments} />

              <AssessmentTrendCharts mode="resident" residentId={resident.id} />

              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" aria-hidden />
                Recorded assessments (demo)
              </h3>

              <div className="grid grid-cols-1 gap-6">
                {mergedAssessments.map((assessment, index) => (
                  <div 
                    key={assessment.id} 
                    className="group relative bg-gradient-to-br from-white via-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:border-indigo-300"
                    style={{
                      animationDelay: `${index * 150}ms`,
                      animation: 'fadeInUp 0.8s ease-out forwards'
                    }}
                  >
                    {/* Header with Icon */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <ClipboardList className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-1">{assessment.name}</h3>
                          <p className="text-sm text-indigo-600 font-medium">{assessment.type}</p>
                          <p className="text-sm text-gray-500">Assessed by: {assessment.assessor}</p>
                          <p className="text-xs text-gray-400">{assessment.date}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className={`px-4 py-2 rounded-full text-sm font-bold shadow-lg ${
                          assessment.status === 'Completed' 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                            : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                        }`}>
                          {assessment.status}
                        </span>
                        {assessment.score && (
                          <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600">{assessment.score}</div>
                            <div className="text-xs text-gray-500">/{assessment.maxScore}</div>
            </div>
          )}
        </div>
      </div>
                    
                    {/* Content Sections */}
                    <div className="space-y-6">
                      <div className="bg-white rounded-xl p-4 border border-indigo-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                          Findings
                        </h4>
                        <p className="text-gray-700 leading-relaxed">{assessment.findings}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          Results
                        </h4>
                        <p className="text-gray-700 leading-relaxed">{assessment.results}</p>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-green-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Recommendations
                        </h4>
                        <p className="text-gray-700 leading-relaxed">{assessment.recommendations}</p>
                      </div>
                      
                      {assessment.nextReview && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                              <Calendar className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-bold text-blue-900">Next Review</p>
                              <p className="text-blue-700">{assessment.nextReview}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Hover Effect Overlay */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-500 pointer-events-none"></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Appointments Tab — enhanced with specialist cross-reference */}
          {activeTab === 'appointments' && (() => {
            const appts = profileData.appointments || [];
            const specialists = resident.supportNetwork?.specialists || [];
            const upcoming = appts.filter(a => a.status === 'Scheduled');
            const completed = appts.filter(a => a.status !== 'Scheduled');
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-green-800 via-teal-600 to-emerald-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-green-100/90">Scheduling</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">Appointments &amp; Consultations</h2>
                <p className="mt-2 text-base text-green-50/90">
                  Upcoming and past appointments for{' '}
                  <span className="font-semibold text-white">{profileData.personalInfo?.name}</span>.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-green-100/80">Total</span><span className="text-lg font-bold">{appts.length}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-green-100/80">Upcoming</span><span className="text-lg font-bold">{upcoming.length}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-green-100/80">Specialists on file</span><span className="text-lg font-bold">{specialists.length}</span></div>
                </div>
              </div>

              {/* Upcoming */}
              {upcoming.length > 0 && (
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-green-600 mb-4">Upcoming</h3>
                  <div className="space-y-3">
                    {upcoming.map((a) => (
                      <div key={a.id} className="rounded-lg border border-green-200 bg-green-50/50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">{a.title}</p>
                            <p className="text-sm text-gray-600">{a.type} · {a.date} at {a.time} · {a.duration} min</p>
                            <p className="text-sm text-gray-500">{a.provider}{a.location ? ` · ${a.location}` : ''}</p>
                          </div>
                          <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">{a.status}</span>
                        </div>
                        {a.preparation && (
                          <div className="mt-2 rounded bg-blue-50 border border-blue-200 p-2 text-xs text-blue-800">
                            <span className="font-semibold">Prep:</span> {a.preparation}
                          </div>
                        )}
                        {a.notes && <p className="text-xs text-gray-600 mt-1">{a.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completed */}
              {completed.length > 0 && (
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Completed</h3>
                  <div className="space-y-2">
                    {completed.map((a) => (
                      <div key={a.id} className="rounded-lg border p-3 flex items-center justify-between hover:bg-gray-50/50">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 text-sm">{a.title}</p>
                          <p className="text-xs text-gray-500">{a.type} · {a.date} · {a.provider}</p>
                          {a.notes && <p className="text-xs text-gray-500 mt-0.5">{a.notes}</p>}
                        </div>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded flex-shrink-0">{a.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {appts.length === 0 && (
                <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-12 text-center">
                  <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                  <p className="text-lg font-semibold text-gray-700">No appointments on file</p>
                  <p className="text-sm text-gray-500 mt-1">Appointments are created when consultations are scheduled with specialists or allied health providers.</p>
                </div>
              )}
            </div>
            );
          })()}

          {/* Documents Tab (consolidated: Documents + Leave + Analytics + Alerts) */}
          {activeTab === 'documents' && (
            <div className="space-y-8">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-amber-700 via-orange-600 to-rose-700 p-8 text-white shadow-2xl">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
                      <FileStack className="h-8 w-8 text-white" />
                    </div>
                    <div className="max-w-2xl">
                      <p className="text-xs font-bold uppercase tracking-widest text-amber-100/90">Record library</p>
                      <h2 className="mt-2 text-3xl font-bold tracking-tight">Documents & reports</h2>
                      <p className="mt-3 text-base leading-relaxed text-amber-50/95">
                        Clinical correspondence, care plans, results, legal registers (agreement, consents, POA, ACD), and uploads for{' '}
                        <span className="font-semibold text-white">{profileData.personalInfo.name}</span>.
                      </p>
                      <p className="mt-3 text-sm text-amber-100/85">
                        Production: versioned files in object storage, OCR metadata, access audit, and linkage to assessments and funding records.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => activateTab('assessments')}
                      className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/35 backdrop-blur hover:bg-white/25"
                    >
                      Linked assessments
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDocSearch('');
                        setDocCategoryFilter('all');
                      }}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-orange-900 shadow hover:bg-orange-50"
                    >
                      Reset filters
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDocUploadModal(true)}
                      className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/35 backdrop-blur hover:bg-white/25 flex items-center gap-1.5"
                    >
                      <Plus className="w-4 h-4" /> Upload Document
                    </button>
                  </div>
                </div>
              </div>

              {resident.documents && typeof resident.documents === 'object' && (
                <div className="rounded-2xl border border-amber-200 bg-white p-6 shadow-md">
                  <div className="mb-4 flex flex-col gap-2 border-b border-amber-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                      <Scale className="h-5 w-5 text-amber-700" />
                      Compliance register (structured references)
                    </h3>
                    <span className="text-xs font-medium text-gray-500">
                      Maps to <code className="rounded bg-gray-100 px-1">resident.documents</code> in the resident model
                    </span>
                  </div>
                  <div className="grid gap-4 text-sm md:grid-cols-2">
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
                      <div className="text-xs font-bold uppercase text-gray-500">Resident agreement</div>
                      <div className="mt-1 font-mono text-gray-900">{resident.documents.residentAgreement || '—'}</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
                      <div className="text-xs font-bold uppercase text-gray-500">Advance care directive</div>
                      <div className="mt-1 font-mono text-gray-900">{resident.documents.advanceCareDirectiveDoc || '—'}</div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 md:col-span-2">
                      <div className="text-xs font-bold uppercase text-gray-500">Consent forms</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(resident.documents.consentForms || []).length > 0
                          ? resident.documents.consentForms.map((id, idx) => (
                              <span key={idx} className="rounded-full bg-white px-3 py-1 font-mono text-xs ring-1 ring-amber-200">
                                {id}
                              </span>
                            ))
                          : <span className="text-gray-500">None listed</span>}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 md:col-span-2">
                      <div className="text-xs font-bold uppercase text-gray-500">POA documents</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(resident.documents.poaDocuments || []).length > 0
                          ? resident.documents.poaDocuments.map((id, idx) => (
                              <span key={idx} className="rounded-full bg-white px-3 py-1 font-mono text-xs ring-1 ring-amber-200">
                                {id}
                              </span>
                            ))
                          : <span className="text-gray-500">None listed</span>}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 md:col-span-2">
                      <div className="text-xs font-bold uppercase text-gray-500">My Health Record</div>
                      <div className="mt-1 text-gray-800">
                        {resident.documents.myHealthRecord ? (
                          <>
                            Linked: {resident.documents.myHealthRecord.connected ? 'Yes' : 'No'} · Consent:{' '}
                            {resident.documents.myHealthRecord.consentGiven ? 'Recorded' : 'Not recorded'}
                            {resident.documents.myHealthRecord.lastSync
                              ? ` · Last sync ${new Date(resident.documents.myHealthRecord.lastSync).toLocaleString()}`
                              : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
                      <div className="text-xs font-bold uppercase text-gray-500">External referrals</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(resident.documents.externalReferrals || []).length > 0
                          ? resident.documents.externalReferrals.map((id, idx) => (
                              <span key={idx} className="rounded-full bg-white px-3 py-1 font-mono text-xs ring-1 ring-amber-200">
                                {id}
                              </span>
                            ))
                          : <span className="text-gray-500">None</span>}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
                      <div className="text-xs font-bold uppercase text-gray-500">Transfer summaries</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(resident.documents.transferSummaries || []).length > 0
                          ? resident.documents.transferSummaries.map((id, idx) => (
                              <span key={idx} className="rounded-full bg-white px-3 py-1 font-mono text-xs ring-1 ring-amber-200">
                                {id}
                              </span>
                            ))
                          : <span className="text-gray-500">None</span>}
                      </div>
                    </div>
                    <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 md:col-span-2">
                      <div className="text-xs font-bold uppercase text-gray-500">Photo gallery</div>
                      <div className="mt-1 text-gray-600">
                        {(resident.documents.photoGallery || []).length} consent-scoped image(s) on file (demo)
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-md">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="relative min-w-[220px] flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={docSearch}
                      onChange={(e) => setDocSearch(e.target.value)}
                      placeholder="Search library by title, type, category…"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-sm focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-200"
                      aria-label="Search documents"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['all', 'clinical', 'legal', 'family', 'general'].map((id) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setDocCategoryFilter(id)}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition ${
                          docCategoryFilter === id
                            ? 'bg-orange-600 text-white shadow'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {id === 'all' ? 'All' : id === 'clinical' ? 'Clinical' : id === 'legal' ? 'Legal' : id === 'family' ? 'Family' : 'General'}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Showing {filteredFileLibrary.length} of {fileLibraryDocuments.length} items
                </p>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {filteredFileLibrary.map((doc) => {
                  const bucket = docCategoryBucket(doc.category);
                  const accent =
                    bucket === 'clinical'
                      ? 'from-emerald-50 ring-emerald-100 border-emerald-200'
                      : bucket === 'legal'
                        ? 'from-slate-50 ring-slate-200 border-slate-200'
                        : bucket === 'family'
                          ? 'from-rose-50 ring-rose-100 border-rose-200'
                          : 'from-gray-50 ring-gray-100 border-gray-200';
                  return (
                    <div
                      key={doc.id || doc.name}
                      className={`flex h-full flex-col rounded-2xl border-2 bg-gradient-to-br to-white p-5 shadow-sm ring-1 ${accent}`}
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-500/90 text-white shadow">
                            <FolderOpen className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base font-bold leading-snug text-gray-900">{doc.name}</h3>
                            <p className="text-xs font-semibold text-orange-700">{doc.type}</p>
                            <p className="mt-1 text-xs text-gray-500">
                              {doc.date} · {doc.size}
                            </p>
                          </div>
                        </div>
                        <span className="shrink-0 rounded-full bg-orange-600 px-2.5 py-1 text-[10px] font-bold uppercase text-white">
                          {doc.status}
                        </span>
                      </div>
                      <p className="mb-4 flex-1 text-sm leading-relaxed text-gray-700">{doc.description}</p>
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
                        <span className="font-medium">{doc.uploadedBy}</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">{doc.category}</span>
                      </div>
                      <div className="mt-auto flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewDocument(buildDocumentViewerPayload(doc))}
                          className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 py-2 text-sm font-semibold text-white shadow hover:from-blue-600 hover:to-cyan-700"
                        >
                          <Eye className="h-3.5 w-3.5" /> View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadDocument(buildDocumentViewerPayload(doc))}
                          className="flex flex-1 items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-gray-600 to-gray-700 py-2 text-sm font-semibold text-white shadow hover:from-gray-700 hover:to-gray-800"
                        >
                          <Download className="h-3.5 w-3.5" /> Save
                        </button>
                      </div>
                    </div>
                  );
                })}
                {filteredFileLibrary.length === 0 && (
                  <div className="col-span-full rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 py-16 text-center text-gray-500">
                    <FileText className="mx-auto mb-3 h-14 w-14 text-gray-300" />
                    <p className="text-lg font-semibold text-gray-700">No documents match your filters</p>
                    <p className="mt-1 text-sm">Clear search or choose &quot;All&quot; to see the full library.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onboarding Workflow — task-based admission & settling-in */}
          {activeTab === 'onboarding' && (
            <OnboardingWorkflow
              residentId={resident.id}
              facilityId={state.selectedFacility}
              admissionDateIso={
                resident.admissionDate ||
                resident.admission?.admissionDate ||
                undefined
              }
              residentData={{
                residentName: profileData.personalInfo?.name || resident.name,
                facilityName: state.selectedFacility,
                roomNumber: resident.room || '',
                admissionDate: resident.admissionDate || '',
              }}
            />
          )}

          {/* CRM & Compliance (Phase 0 — live modal) */}
          {activeTab === 'crm' && (
            <CrmComplianceProfileSection
              residentId={resident.id}
              residentName={profileData.personalInfo?.name || resident.name}
              facilityId={state.selectedFacility}
              admissionDateIso={
                resident.admissionDate ||
                resident.admission?.admissionDate ||
                undefined
              }
              enquiryDateIso={resident.enquiryDate || resident.crm?.enquiryDate || undefined}
            />
          )}

          {/* Dietary & Nutrition Tab — enhanced */}
          {activeTab === 'dietary' && (() => {
            const d = resident.dietary || {};
            const c = resident.clinical || {};
            const bmiVal = clinicalPanel.computedBmi || c.bmi;
            const recentWeights = clinicalPanel.recentWeights || [];
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-amber-700 via-orange-600 to-red-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-100/90">Nutrition management</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">Dietary &amp; Nutrition</h2>
                <p className="mt-2 text-base text-amber-50/90">
                  IDDSI-compliant dietary management, weight monitoring, and nutritional risk for{' '}
                  <span className="font-semibold text-white">{resident.demographics?.preferredName || profileData.personalInfo?.name}</span>.
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-4">
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-amber-100/80">Diet Type</span><span className="text-sm font-bold">{d.dietType || '—'}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-amber-100/80">IDDSI Food</span><span className="text-sm font-bold">{d.iddsiFoodLevel ? `${d.iddsiFoodLevel} — ${getIDDSIFoodLabel(d.iddsiFoodLevel)}` : '—'}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-amber-100/80">IDDSI Fluid</span><span className="text-sm font-bold">{d.iddsiFluidLevel ? `${d.iddsiFluidLevel} — ${getIDDSIFluidLabel(d.iddsiFluidLevel)}` : '—'}</span></div>
                  <div className="rounded-xl bg-black/20 px-4 py-3"><span className="block text-xs text-amber-100/80">Nutrition Risk</span><span className={`text-sm font-bold ${d.nutritionalRiskScore?.riskLevel === 'High' ? 'text-red-200' : ''}`}>{d.nutritionalRiskScore?.riskLevel || 'Not assessed'}</span></div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Diet & IDDSI Classification */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Diet Classification</h3>
                  <dl className="space-y-3 text-sm">
                    {[
                      ['Diet Type', d.dietType],
                      ['Portion Size', d.portionSize],
                      ['IDDSI Food Level', d.iddsiFoodLevel ? `${d.iddsiFoodLevel} — ${getIDDSIFoodLabel(d.iddsiFoodLevel)}` : null],
                      ['IDDSI Fluid Level', d.iddsiFluidLevel ? `${d.iddsiFluidLevel} — ${getIDDSIFluidLabel(d.iddsiFluidLevel)}` : null],
                      ['Mealtime Assistance', d.mealtimeAssistance],
                    ].map(([label, val]) => (
                      <div key={label} className="flex justify-between items-baseline border-b border-gray-100 pb-2 last:border-0">
                        <dt className="text-gray-500 text-xs">{label}</dt>
                        <dd className="font-semibold text-gray-800 text-right">{val || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                  {d.fluidRestriction?.restricted && (
                    <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span className="font-bold text-red-700 text-sm">Fluid Restriction: {d.fluidRestriction.dailyLimitMl}ml/day</span>
                      </div>
                    </div>
                  )}
                  {d.enteralFeeding?.feedType && (
                    <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm">
                      <span className="font-bold text-blue-700">Enteral Feeding:</span> {d.enteralFeeding.feedType} at {d.enteralFeeding.rateMlHr}ml/hr
                      {d.enteralFeeding.schedule && <span className="text-xs text-gray-500 block mt-0.5">{d.enteralFeeding.schedule}</span>}
                    </div>
                  )}
                </div>

                {/* Nutritional Status & Weight */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Weight &amp; Nutritional Status</h3>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="rounded-lg bg-amber-50 p-3 text-center">
                      <p className="text-lg font-bold text-amber-800">{c.weightKg ? `${c.weightKg} kg` : '—'}</p>
                      <p className="text-[10px] text-gray-500">Current Weight</p>
                    </div>
                    <div className="rounded-lg bg-amber-50 p-3 text-center">
                      <p className="text-lg font-bold text-amber-800">{c.heightCm ? `${c.heightCm} cm` : '—'}</p>
                      <p className="text-[10px] text-gray-500">Height</p>
                    </div>
                    <div className={`rounded-lg p-3 text-center ${bmiVal && bmiVal < 18.5 ? 'bg-red-50' : bmiVal && bmiVal > 30 ? 'bg-orange-50' : 'bg-green-50'}`}>
                      <p className={`text-lg font-bold ${bmiVal && bmiVal < 18.5 ? 'text-red-700' : bmiVal && bmiVal > 30 ? 'text-orange-700' : 'text-green-700'}`}>{bmiVal || '—'}</p>
                      <p className="text-[10px] text-gray-500">BMI</p>
                    </div>
                  </div>
                  {d.nutritionalRiskScore?.score != null && (
                    <div className={`rounded-lg p-3 mb-3 ${
                      d.nutritionalRiskScore.riskLevel === 'High' ? 'bg-red-50 border border-red-200' :
                      d.nutritionalRiskScore.riskLevel === 'Medium' ? 'bg-yellow-50 border border-yellow-200' :
                      'bg-green-50 border border-green-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">{d.nutritionalRiskScore.tool || 'Nutrition'} Score</span>
                        <span className={`font-bold text-sm ${
                          d.nutritionalRiskScore.riskLevel === 'High' ? 'text-red-700' : d.nutritionalRiskScore.riskLevel === 'Medium' ? 'text-yellow-700' : 'text-green-700'
                        }`}>{d.nutritionalRiskScore.score} — {d.nutritionalRiskScore.riskLevel}</span>
                      </div>
                      {d.nutritionalRiskScore.assessmentDate && <p className="text-[10px] text-gray-400 mt-1">Assessed: {d.nutritionalRiskScore.assessmentDate}</p>}
                    </div>
                  )}
                  {recentWeights.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 mb-2">Recent Weight History</h4>
                      <div className="space-y-1">
                        {recentWeights.slice(0, 4).map((w, i) => (
                          <div key={i} className="flex justify-between text-xs border-b border-gray-100 pb-1">
                            <span className="text-gray-500">{w.date}</span>
                            <span className="font-semibold">{w.weightKg} kg</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Preferences & Allergies */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Preferences &amp; Allergies</h3>
                  {(d.dietaryPreferences || []).length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-gray-500 mb-2">Dietary Preferences</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {d.dietaryPreferences.map((p, i) => (
                          <span key={i} className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(d.foodAllergies || []).length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-xs font-semibold text-red-500 mb-2">Food Allergies</h4>
                      <div className="space-y-2">
                        {d.foodAllergies.map((a, i) => (
                          <div key={i} className={`rounded-lg p-2.5 border ${a.severity === 'Severe' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-sm">{a.allergen}</span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${a.severity === 'Severe' ? 'bg-red-200 text-red-800' : 'bg-orange-200 text-orange-800'}`}>{a.severity}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">{a.reaction}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(d.dietaryHistory || []).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 mb-2">Diet Changes</h4>
                      <div className="space-y-1">
                        {d.dietaryHistory.slice(0, 3).map((h, i) => (
                          <div key={i} className="text-xs border-b border-gray-100 pb-1">
                            <span className="text-gray-500">{h.date}:</span> <span className="font-medium">{h.change || h.note || h}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })()}

          {/* My Life Story Tab — enhanced */}
          {activeTab === 'life-story' && (() => {
            const pl = resident.personalLife || {};
            const dr = pl.dailyRoutinePreferences || {};
            const hobbies = pl.hobbiesInterests || [];
            const dates = pl.importantDates || [];
            const prefName = resident.demographics?.preferredName || profileData.personalInfo?.name;
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-rose-700 via-pink-600 to-fuchsia-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-rose-100/90">Person-centred care</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">My Life Story</h2>
                <p className="mt-2 text-base text-rose-50/90">
                  Life history, preferences, routines, and what matters most to{' '}
                  <span className="font-semibold text-white">{prefName}</span>.
                </p>
              </div>

              {/* Life History Narrative */}
              {pl.lifeHistory && (
                <div className="rounded-xl border bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Life History</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">{pl.lifeHistory}</p>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Background & Interests */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Background</h3>
                  <dl className="space-y-3 text-sm">
                    {pl.occupationHistory && (
                      <div><dt className="text-xs text-gray-400">Occupation History</dt><dd className="font-medium text-gray-800">{pl.occupationHistory}</dd></div>
                    )}
                    {hobbies.length > 0 && (
                      <div>
                        <dt className="text-xs text-gray-400 mb-1.5">Hobbies &amp; Interests</dt>
                        <dd className="flex flex-wrap gap-1.5">
                          {hobbies.map((h, i) => <span key={i} className="px-2.5 py-1 bg-rose-100 text-rose-800 rounded-full text-xs font-medium">{h}</span>)}
                        </dd>
                      </div>
                    )}
                    {pl.musicPreferences && (
                      <div className="rounded-lg bg-pink-50 border border-pink-200 p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Music className="w-3.5 h-3.5 text-pink-600" />
                          <span className="text-xs font-semibold text-pink-700">Music</span>
                        </div>
                        <p className="text-sm text-gray-800">{pl.musicPreferences}</p>
                      </div>
                    )}
                    {pl.petsAnimals && (
                      <div><dt className="text-xs text-gray-400">Pets / Animals</dt><dd className="font-medium text-gray-800">{pl.petsAnimals}</dd></div>
                    )}
                  </dl>
                </div>

                {/* Daily Routine */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Daily Routine</h3>
                  <dl className="space-y-3 text-sm">
                    {[
                      ['Wake Time', dr.wakeTime],
                      ['Shower', dr.showerPreference],
                      ['Meal Preferences', dr.mealPreferences],
                      ['Bedtime', dr.bedtime],
                    ].filter(([, v]) => v).map(([label, val]) => (
                      <div key={label} className="flex justify-between items-baseline border-b border-gray-100 pb-2 last:border-0">
                        <dt className="text-gray-500 text-xs">{label}</dt>
                        <dd className="font-semibold text-gray-800 text-right">{val}</dd>
                      </div>
                    ))}
                    {pl.socialPreferences && (
                      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                        <span className="text-xs font-semibold text-blue-700 block mb-0.5">Social Preferences</span>
                        <p className="text-sm text-gray-800">{pl.socialPreferences}</p>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Comfort & Important Dates */}
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Comfort &amp; Dates</h3>
                  {pl.comfortItems && (
                    <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Heart className="w-3.5 h-3.5 text-yellow-600" />
                        <span className="text-xs font-semibold text-yellow-700">Comfort Items</span>
                      </div>
                      <p className="text-sm text-gray-800">{pl.comfortItems}</p>
                    </div>
                  )}
                  {dates.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 mb-2">Important Dates</h4>
                      <div className="space-y-2">
                        {dates.map((d, i) => (
                          <div key={i} className="flex items-center gap-3 rounded-lg bg-rose-50 border border-rose-200 p-2.5">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-rose-200 flex items-center justify-center">
                              <Calendar className="w-4 h-4 text-rose-700" />
                            </div>
                            <div>
                              <p className="font-bold text-rose-800 text-sm">{d.date}</p>
                              <p className="text-xs text-gray-600">{d.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!pl.comfortItems && dates.length === 0 && (
                    <p className="text-sm text-gray-500">No comfort items or important dates on file.</p>
                  )}
                </div>
              </div>

              {/* Cultural context cross-reference */}
              {(resident.demographics?.culturalBackground || resident.demographics?.religion) && (
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Cultural Context</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {resident.demographics.culturalBackground && <div><span className="text-xs text-gray-400 block">Cultural Background</span><span className="font-semibold">{resident.demographics.culturalBackground}</span></div>}
                    {resident.demographics.religion && <div><span className="text-xs text-gray-400 block">Religion</span><span className="font-semibold">{resident.demographics.religion}</span></div>}
                    {resident.demographics.primaryLanguage && <div><span className="text-xs text-gray-400 block">Primary Language</span><span className="font-semibold">{resident.demographics.primaryLanguage}</span></div>}
                    {resident.demographics.countryOfBirth && <div><span className="text-xs text-gray-400 block">Country of Birth</span><span className="font-semibold">{resident.demographics.countryOfBirth}</span></div>}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Leave & Movements Tab — Production-Ready */}
          {activeTab === 'leave' && (() => {
            const ld = leaveData || {};
            const history = ld.leaveHistory || [];
            const transfers = ld.hospitalTransfers || [];
            const bh = ld.bedHold || {};
            const status = ld.currentLeaveStatus || 'In Facility';
            const isOnLeave = status !== 'In Facility';
            const activeLeave = history.find((l) => l.returnStatus === 'On Leave');
            const activeHospital = transfers.find((t) => t.status === 'In Hospital');
            const stats = leaveStats || {};
            const evacPriority = ld.evacuationPriority;

            return (
            <div className="space-y-6">
              {/* Header */}
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-800 via-cyan-600 to-blue-700 p-8 text-white shadow-2xl">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-widest text-cyan-100/90">Resident movements</p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight">Leave &amp; Movements</h2>
                    <p className="mt-3 text-base leading-relaxed text-cyan-50/95">
                      Leave records, hospital transfers, bed holds, and evacuation priority for{' '}
                      <span className="font-semibold text-white">{profileData.personalInfo?.name}</span>.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={isOnLeave}
                      onClick={() => {
                        setLeaveFormErrors([]);
                        setLeaveFormData({ type: 'Social Leave', startDate: new Date().toISOString().split('T')[0], endDate: '', expectedReturnDate: '', destination: '', notes: '', contactWhileOnLeave: '', authorisedBy: '', activateBedHold: false });
                        setShowLeaveModal(true);
                      }}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-cyan-900 shadow hover:bg-cyan-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      + Record Leave
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setLeaveFormErrors([]);
                        setTransferFormData({ dateOut: new Date().toISOString().split('T')[0], hospital: '', reason: '', referringDoctor: '', ambulanceRequired: false, familyNotified: false, expectedReturn: '' });
                        setShowTransferModal(true);
                      }}
                      className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/35 backdrop-blur hover:bg-white/25"
                    >
                      + Hospital Transfer
                    </button>
                  </div>
                </div>
              </div>

              {/* Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Current Status</p>
                  <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold ${
                    status === 'In Facility' ? 'bg-green-100 text-green-800' :
                    status.includes('Hospital') ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>{status}</span>
                  {isOnLeave && activeLeave && (
                    <p className="text-xs text-gray-500 mt-2">Since {activeLeave.startDate} — {activeLeave.destination || 'Unknown destination'}</p>
                  )}
                </div>

                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Evacuation Priority</p>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-3 py-1.5 rounded-full text-sm font-bold ${
                      evacPriority === 'Priority 1' ? 'bg-red-100 text-red-800' :
                      evacPriority === 'Priority 2' ? 'bg-orange-100 text-orange-800' :
                      evacPriority === 'Priority 3' ? 'bg-yellow-100 text-yellow-800' :
                      evacPriority === 'Priority 4' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>{evacPriority || 'Not Set'}</span>
                    <select
                      className="text-xs border rounded px-2 py-1 text-gray-700"
                      value={evacPriority || ''}
                      onChange={(e) => handleUpdateEvacPriority(e.target.value)}
                    >
                      <option value="" disabled>Change…</option>
                      <option value="Priority 1">P1 — Bed-bound</option>
                      <option value="Priority 2">P2 — Wheelchair</option>
                      <option value="Priority 3">P3 — Assisted walking</option>
                      <option value="Priority 4">P4 — Independent</option>
                    </select>
                  </div>
                  {evacPriority === 'Priority 1' && <p className="text-xs text-red-600 mt-1 font-medium">Requires stretcher evacuation</p>}
                </div>

                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Bed Hold</p>
                  {bh.active ? (
                    <div>
                      <span className="inline-block px-3 py-1.5 rounded-full text-sm font-bold bg-amber-100 text-amber-800">Active</span>
                      <p className="text-xs text-gray-600 mt-2">Since {bh.startDate}{bh.expectedReturn ? ` • Expected return: ${bh.expectedReturn}` : ''}</p>
                      <p className="text-xs text-gray-500">{bh.reason}</p>
                      <button type="button" onClick={handleReleaseBedHold} className="mt-2 text-xs text-red-600 hover:text-red-800 font-semibold underline">Release Bed Hold</button>
                    </div>
                  ) : (
                    <div>
                      <span className="inline-block px-3 py-1.5 rounded-full text-sm font-bold bg-gray-100 text-gray-600">None</span>
                      <button
                        type="button"
                        onClick={() => setShowBedHoldModal(true)}
                        className="block mt-2 text-xs text-blue-600 hover:text-blue-800 font-semibold underline"
                      >
                        Activate Bed Hold
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Year Summary</p>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-blue-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-blue-700">{stats.leaveDaysThisYear || 0}</p>
                      <p className="text-[10px] text-gray-500">Leave Days</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-blue-700">{stats.totalHospitalTransfers || 0}</p>
                      <p className="text-[10px] text-gray-500">Hosp. Transfers</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Leave / Transfer Alert */}
              {isOnLeave && (
                <div className={`border-l-4 ${status.includes('Hospital') ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50'} rounded-lg p-4 shadow-sm`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className={`w-5 h-5 ${status.includes('Hospital') ? 'text-red-600' : 'text-amber-600'}`} />
                      <div>
                        <p className={`font-bold text-sm ${status.includes('Hospital') ? 'text-red-800' : 'text-amber-800'}`}>
                          Resident is currently {status.includes('Hospital') ? 'in hospital' : 'on leave'}
                        </p>
                        {activeLeave && <p className="text-xs text-gray-600 mt-0.5">Since {activeLeave.startDate} at {activeLeave.destination || '—'}</p>}
                        {activeHospital && <p className="text-xs text-gray-600 mt-0.5">Since {activeHospital.dateOut} at {activeHospital.hospital}</p>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeLeave) setActiveLeaveRecord(activeLeave);
                        if (activeHospital) setActiveTransferRecord(activeHospital);
                        setReturnFormData({ returnDate: new Date().toISOString().split('T')[0], returnNotes: '', clinicalChangesOnReturn: '', dischargeSummaryReceived: false, medicationChanges: '', followUpRequired: '' });
                        setLeaveFormErrors([]);
                        setShowReturnModal(true);
                      }}
                      className="px-4 py-2 bg-white rounded-lg text-sm font-semibold border shadow-sm hover:bg-gray-50"
                    >
                      Process Return
                    </button>
                  </div>
                </div>
              )}

              {/* Leave History */}
              <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b bg-gray-50">
                  <h3 className="text-base font-bold text-gray-900">Leave History</h3>
                  <span className="text-xs text-gray-500 font-medium">{history.length} record{history.length !== 1 ? 's' : ''}</span>
                </div>
                {history.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">No leave records yet. Click <strong>Record Leave</strong> to add one.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50/50">
                          <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-600">Dates</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-600">Destination</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-600">Notes</th>
                          <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((l) => (
                          <tr key={l.id} className="border-b hover:bg-gray-50/50 transition-colors">
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                l.type === 'Hospital Leave' ? 'bg-red-100 text-red-700' :
                                l.type === 'Social Leave' ? 'bg-blue-100 text-blue-700' :
                                l.type === 'Respite Leave' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>{l.type}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {l.startDate}{l.endDate ? ` → ${l.endDate}` : ' → ongoing'}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{l.destination || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                l.returnStatus === 'Returned' ? 'bg-green-100 text-green-700' :
                                l.returnStatus === 'On Leave' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{l.returnStatus}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate" title={l.notes}>{l.notes || '—'}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {l.returnStatus === 'On Leave' && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveLeaveRecord(l);
                                      setActiveTransferRecord(null);
                                      setReturnFormData({ returnDate: new Date().toISOString().split('T')[0], returnNotes: '', clinicalChangesOnReturn: '', dischargeSummaryReceived: false, medicationChanges: '', followUpRequired: '' });
                                      setLeaveFormErrors([]);
                                      setShowReturnModal(true);
                                    }}
                                    className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 font-medium"
                                  >
                                    Return
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDeleteLeave(l.id)}
                                  className="px-2 py-1 text-xs text-red-600 hover:text-red-800 font-medium"
                                  title="Delete record"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Hospital Transfers */}
              <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b bg-gray-50">
                  <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                    <Building className="w-4 h-4 text-red-500" />
                    Hospital Transfers
                  </h3>
                  <span className="text-xs text-gray-500 font-medium">{transfers.length} transfer{transfers.length !== 1 ? 's' : ''}</span>
                </div>
                {transfers.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">No hospital transfers on record.</div>
                ) : (
                  <div className="divide-y">
                    {transfers.map((t) => (
                      <div key={t.id} className="p-5 hover:bg-gray-50/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-gray-900">{t.hospital}</span>
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                t.status === 'In Hospital' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                              }`}>{t.status || (t.dateReturned ? 'Returned' : 'In Hospital')}</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600 mt-2">
                              <div><span className="text-gray-400">Out:</span> {t.dateOut}</div>
                              <div><span className="text-gray-400">Returned:</span> {t.dateReturned || '—'}</div>
                              <div><span className="text-gray-400">Doctor:</span> {t.referringDoctor || '—'}</div>
                              <div className="flex gap-3">
                                {t.ambulanceRequired && <span className="text-red-600 font-medium">Ambulance</span>}
                                {t.familyNotified && <span className="text-blue-600 font-medium">Family notified</span>}
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 mt-2"><span className="text-gray-400">Reason:</span> {t.reason}</p>
                            {t.clinicalChangesOnReturn && (
                              <p className="text-sm text-gray-700 mt-1"><span className="text-gray-400">Changes on return:</span> {t.clinicalChangesOnReturn}</p>
                            )}
                            {t.medicationChanges && (
                              <p className="text-sm text-gray-700 mt-1"><span className="text-gray-400">Medication changes:</span> {t.medicationChanges}</p>
                            )}
                          </div>
                          {(t.status === 'In Hospital' || (!t.dateReturned && !t.status)) && (
                            <button
                              type="button"
                              onClick={() => {
                                setActiveTransferRecord(t);
                                setActiveLeaveRecord(null);
                                setReturnFormData({ returnDate: new Date().toISOString().split('T')[0], returnNotes: '', clinicalChangesOnReturn: '', dischargeSummaryReceived: false, medicationChanges: '', followUpRequired: '' });
                                setLeaveFormErrors([]);
                                setShowReturnModal(true);
                              }}
                              className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 font-semibold whitespace-nowrap"
                            >
                              Process Return
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Record Leave Modal ────────────────────────────────── */}
              {showLeaveModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setShowLeaveModal(false)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="px-6 py-5 border-b bg-gradient-to-r from-cyan-600 to-blue-600 rounded-t-2xl">
                      <h3 className="text-lg font-bold text-white">Record Leave</h3>
                      <p className="text-cyan-100 text-sm">Record social, hospital, respite, or transition leave</p>
                    </div>
                    <div className="p-6 space-y-4">
                      {leaveFormErrors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          {leaveFormErrors.map((e, i) => <p key={i} className="text-sm text-red-700">{e}</p>)}
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Leave Type *</label>
                        <select className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.type} onChange={(e) => setLeaveFormData(p => ({ ...p, type: e.target.value }))}>
                          <option>Social Leave</option>
                          <option>Hospital Leave</option>
                          <option>Respite Leave</option>
                          <option>Transition Leave</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date *</label>
                          <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.startDate} onChange={(e) => setLeaveFormData(p => ({ ...p, startDate: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Return</label>
                          <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.expectedReturnDate} onChange={(e) => setLeaveFormData(p => ({ ...p, expectedReturnDate: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Destination</label>
                        <input type="text" placeholder="e.g. Daughter's home, The Alfred Hospital" className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.destination} onChange={(e) => setLeaveFormData(p => ({ ...p, destination: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Contact While on Leave</label>
                        <input type="text" placeholder="Phone number or contact person" className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.contactWhileOnLeave} onChange={(e) => setLeaveFormData(p => ({ ...p, contactWhileOnLeave: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Authorised By</label>
                        <input type="text" placeholder="Staff member name" className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.authorisedBy} onChange={(e) => setLeaveFormData(p => ({ ...p, authorisedBy: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
                        <textarea rows={3} placeholder="Additional details…" className="w-full border rounded-lg px-3 py-2 text-sm" value={leaveFormData.notes} onChange={(e) => setLeaveFormData(p => ({ ...p, notes: e.target.value }))} />
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={leaveFormData.activateBedHold} onChange={(e) => setLeaveFormData(p => ({ ...p, activateBedHold: e.target.checked }))} className="rounded" />
                        <span className="font-medium text-gray-700">Activate bed hold during leave</span>
                      </label>
                    </div>
                    <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                      <button type="button" onClick={() => setShowLeaveModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg hover:bg-gray-100">Cancel</button>
                      <button type="button" onClick={handleRecordLeave} className="px-4 py-2 text-sm font-semibold text-white bg-cyan-600 rounded-lg hover:bg-cyan-700">Record Leave</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Hospital Transfer Modal ──────────────────────────── */}
              {showTransferModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setShowTransferModal(false)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="px-6 py-5 border-b bg-gradient-to-r from-red-600 to-rose-600 rounded-t-2xl">
                      <h3 className="text-lg font-bold text-white">Record Hospital Transfer</h3>
                      <p className="text-red-100 text-sm">Document transfer to external hospital</p>
                    </div>
                    <div className="p-6 space-y-4">
                      {leaveFormErrors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          {leaveFormErrors.map((e, i) => <p key={i} className="text-sm text-red-700">{e}</p>)}
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Hospital Name *</label>
                        <input type="text" placeholder="e.g. The Alfred Hospital" className="w-full border rounded-lg px-3 py-2 text-sm" value={transferFormData.hospital} onChange={(e) => setTransferFormData(p => ({ ...p, hospital: e.target.value }))} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Transfer Date *</label>
                          <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={transferFormData.dateOut} onChange={(e) => setTransferFormData(p => ({ ...p, dateOut: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Return</label>
                          <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={transferFormData.expectedReturn} onChange={(e) => setTransferFormData(p => ({ ...p, expectedReturn: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Reason for Transfer *</label>
                        <textarea rows={2} placeholder="Clinical reason for hospital transfer" className="w-full border rounded-lg px-3 py-2 text-sm" value={transferFormData.reason} onChange={(e) => setTransferFormData(p => ({ ...p, reason: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Referring Doctor</label>
                        <input type="text" placeholder="Doctor name" className="w-full border rounded-lg px-3 py-2 text-sm" value={transferFormData.referringDoctor} onChange={(e) => setTransferFormData(p => ({ ...p, referringDoctor: e.target.value }))} />
                      </div>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={transferFormData.ambulanceRequired} onChange={(e) => setTransferFormData(p => ({ ...p, ambulanceRequired: e.target.checked }))} className="rounded" />
                          <span className="font-medium text-gray-700">Ambulance required</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={transferFormData.familyNotified} onChange={(e) => setTransferFormData(p => ({ ...p, familyNotified: e.target.checked }))} className="rounded" />
                          <span className="font-medium text-gray-700">Family notified</span>
                        </label>
                      </div>
                    </div>
                    <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                      <button type="button" onClick={() => setShowTransferModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg hover:bg-gray-100">Cancel</button>
                      <button type="button" onClick={handleRecordTransfer} className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700">Record Transfer</button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Return / Discharge Modal ─────────────────────────── */}
              {showReturnModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => { setShowReturnModal(false); setActiveLeaveRecord(null); setActiveTransferRecord(null); }}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="px-6 py-5 border-b bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-2xl">
                      <h3 className="text-lg font-bold text-white">Process Return</h3>
                      <p className="text-green-100 text-sm">
                        {activeTransferRecord ? `Return from ${activeTransferRecord.hospital}` : activeLeaveRecord ? `Return from ${activeLeaveRecord.type}` : 'Process resident return'}
                      </p>
                    </div>
                    <div className="p-6 space-y-4">
                      {leaveFormErrors.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          {leaveFormErrors.map((e, i) => <p key={i} className="text-sm text-red-700">{e}</p>)}
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Return Date *</label>
                        <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm" value={returnFormData.returnDate} onChange={(e) => setReturnFormData(p => ({ ...p, returnDate: e.target.value }))} />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Clinical Changes on Return</label>
                        <textarea rows={3} placeholder="Document any clinical changes observed" className="w-full border rounded-lg px-3 py-2 text-sm" value={returnFormData.clinicalChangesOnReturn} onChange={(e) => setReturnFormData(p => ({ ...p, clinicalChangesOnReturn: e.target.value }))} />
                      </div>
                      {activeTransferRecord && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Medication Changes</label>
                            <textarea rows={2} placeholder="Any medication changes from hospital" className="w-full border rounded-lg px-3 py-2 text-sm" value={returnFormData.medicationChanges} onChange={(e) => setReturnFormData(p => ({ ...p, medicationChanges: e.target.value }))} />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Follow-up Required</label>
                            <input type="text" placeholder="e.g. Follow-up with cardiologist in 2 weeks" className="w-full border rounded-lg px-3 py-2 text-sm" value={returnFormData.followUpRequired} onChange={(e) => setReturnFormData(p => ({ ...p, followUpRequired: e.target.value }))} />
                          </div>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={returnFormData.dischargeSummaryReceived} onChange={(e) => setReturnFormData(p => ({ ...p, dischargeSummaryReceived: e.target.checked }))} className="rounded" />
                            <span className="font-medium text-gray-700">Discharge summary received</span>
                          </label>
                        </>
                      )}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Return Notes</label>
                        <textarea rows={2} placeholder="Additional notes" className="w-full border rounded-lg px-3 py-2 text-sm" value={returnFormData.returnNotes} onChange={(e) => setReturnFormData(p => ({ ...p, returnNotes: e.target.value }))} />
                      </div>
                    </div>
                    <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                      <button type="button" onClick={() => { setShowReturnModal(false); setActiveLeaveRecord(null); setActiveTransferRecord(null); }} className="px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg hover:bg-gray-100">Cancel</button>
                      <button
                        type="button"
                        onClick={activeTransferRecord ? handleReturnFromHospital : handleReturnFromLeave}
                        className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700"
                      >
                        Confirm Return
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Bed Hold Modal ────────────────────────────────────── */}
              {showBedHoldModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setShowBedHoldModal(false)}>
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                    <div className="px-6 py-5 border-b">
                      <h3 className="text-lg font-bold text-gray-900">Activate Bed Hold</h3>
                    </div>
                    <form
                      className="p-6 space-y-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const fd = new FormData(e.target);
                        handleActivateBedHold({
                          startDate: fd.get('startDate'),
                          expectedReturn: fd.get('expectedReturn') || null,
                          reason: fd.get('reason') || 'Manual',
                        });
                      }}
                    >
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                        <input type="date" name="startDate" defaultValue={new Date().toISOString().split('T')[0]} className="w-full border rounded-lg px-3 py-2 text-sm" required />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Expected Return</label>
                        <input type="date" name="expectedReturn" className="w-full border rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Reason</label>
                        <input type="text" name="reason" placeholder="e.g. Family visit, Hospital stay" className="w-full border rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={() => setShowBedHoldModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg hover:bg-gray-100">Cancel</button>
                        <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700">Activate</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Quality & Compliance Tab */}
          {activeTab === 'quality' && (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-600 to-teal-700 p-8 text-white shadow-2xl">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-100/90">Monitoring &amp; accountability</p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight">Quality &amp; compliance</h2>
                    <p className="mt-3 text-base leading-relaxed text-emerald-50/95">
                      NQIP-style indicators, SIRS, incidents, narrative documentation registers, and care minutes context for{' '}
                      <span className="font-semibold text-white">{profileData.personalInfo.name}</span>.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isAgedCare && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('funding')}
                        className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/35 backdrop-blur hover:bg-white/25"
                      >
                        Funding &amp; AN-ACC
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => activateTab('assessments')}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-emerald-900 shadow hover:bg-emerald-50"
                    >
                      Assessments
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-amber-200 bg-white p-6 shadow-md">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                    <Star className="h-4 w-4 text-amber-500" />
                    Star ratings (resident slice)
                  </h3>
                  {resident.starRatings &&
                  ['overallRating', 'experienceScore', 'complianceScore', 'staffingScore', 'qualityScore'].some(
                    (k) => resident.starRatings[k] != null
                  ) ? (
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        ['Overall', resident.starRatings.overallRating],
                        ['Experience', resident.starRatings.experienceScore],
                        ['Compliance', resident.starRatings.complianceScore],
                        ['Staffing', resident.starRatings.staffingScore],
                        ['Quality', resident.starRatings.qualityScore]
                      ].map(([label, val]) =>
                        val != null ? (
                          <div key={label} className="rounded-lg bg-amber-50/80 px-3 py-2">
                            <dt className="text-xs text-gray-500">{label}</dt>
                            <dd className="text-lg font-bold text-amber-900">{String(val)}</dd>
                          </div>
                        ) : null
                      )}
                    </dl>
                  ) : (
                    <p className="text-sm leading-relaxed text-gray-500">
                      Mock data keeps star rating fields null (facility-level program). This panel will populate when the API binds quarterly contribution scores.
                    </p>
                  )}
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-white p-6 shadow-md">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                    <Clock className="h-4 w-4 text-emerald-600" />
                    Care minutes (today)
                  </h3>
                  {loadingCareMinutes ? (
                    <p className="text-sm text-gray-500">Loading care minutes…</p>
                  ) : careMinutesData ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-wrap gap-4">
                        <div>
                          <span className="text-gray-500">Direct care</span>
                          <div className="text-2xl font-bold text-emerald-800">{careMinutesData.totalMinutes ?? 0}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">RN minutes</span>
                          <div className="text-2xl font-bold text-emerald-800">{careMinutesData.rnMinutes ?? 0}</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        Status:{' '}
                        <span className="font-semibold">
                          {careMinutesData.compliant && careMinutesData.rnCompliant ? 'Within targets' : 'Review required'}
                        </span>
                        {' · '}
                        Targets ~{careMinutesData.anaccAdjustedTarget || careMinutesData.targetMinutes || '—'} direct /{' '}
                        {careMinutesData.targetRNMinutes || '—'} RN
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No care minutes row returned for this resident and date.</p>
                  )}
                </div>
              </div>

              {resident.qualityCompliance?.nqipData && (
                <div className="bg-white rounded-xl border-2 border-emerald-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">NQIP Indicators (This Quarter)</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-700">{resident.qualityCompliance.nqipData.pressureInjuries}</div>
                      <div className="text-xs text-gray-500 mt-1">Pressure Injuries</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-700">{resident.qualityCompliance.nqipData.falls}</div>
                      <div className="text-xs text-gray-500 mt-1">Falls</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-700">{resident.qualityCompliance.nqipData.fallsWithInjury}</div>
                      <div className="text-xs text-gray-500 mt-1">Falls with Injury</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className={`text-2xl font-bold ${resident.qualityCompliance.nqipData.unplannedWeightLoss ? 'text-red-700' : 'text-green-700'}`}>{resident.qualityCompliance.nqipData.unplannedWeightLoss ? 'Yes' : 'No'}</div>
                      <div className="text-xs text-gray-500 mt-1">Unplanned Weight Loss</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className={`text-2xl font-bold ${resident.qualityCompliance.nqipData.polypharmacy ? 'text-orange-700' : 'text-green-700'}`}>{resident.qualityCompliance.nqipData.polypharmacy ? 'Yes' : 'No'}</div>
                      <div className="text-xs text-gray-500 mt-1">Polypharmacy (9+)</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className={`text-2xl font-bold ${resident.qualityCompliance.nqipData.antipsychotics ? 'text-red-700' : 'text-green-700'}`}>{resident.qualityCompliance.nqipData.antipsychotics ? 'Yes' : 'No'}</div>
                      <div className="text-xs text-gray-500 mt-1">Antipsychotics</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className={`text-2xl font-bold ${resident.qualityCompliance.nqipData.physicalRestraint ? 'text-red-700' : 'text-green-700'}`}>{resident.qualityCompliance.nqipData.physicalRestraint ? 'Yes' : 'No'}</div>
                      <div className="text-xs text-gray-500 mt-1">Physical Restraint</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md">
                <h3 className="mb-3 text-lg font-bold text-gray-900">Progress notes</h3>
                {(resident.qualityCompliance?.progressNotes || []).length > 0 ? (
                  <ul className="space-y-3">
                    {resident.qualityCompliance.progressNotes.map((n, i) => (
                      <li key={i} className="rounded-lg border border-slate-100 bg-slate-50/80 p-4 text-sm">
                        <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                          <span>{n.date || n.recordedAt || '—'}</span>
                          <span>{n.author || n.recordedBy || ''}</span>
                        </div>
                        <p className="mt-2 text-gray-800">{n.summary || n.text || n.note || JSON.stringify(n)}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm leading-relaxed text-gray-500">
                    No progress notes in the demo payload. Production: chronological clinical narrative with author, time, and visibility rules.
                  </p>
                )}
              </div>

              <div className="rounded-2xl border border-indigo-200 bg-white p-6 shadow-md">
                <h3 className="mb-3 text-lg font-bold text-gray-900">Clinical handover</h3>
                {(resident.qualityCompliance?.clinicalHandoverNotes || []).length > 0 ? (
                  <ul className="space-y-3">
                    {resident.qualityCompliance.clinicalHandoverNotes.map((n, i) => (
                      <li key={i} className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-4 text-sm">
                        <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
                          <span>{n.shift || n.date || '—'}</span>
                          <span>{n.author || ''}</span>
                        </div>
                        <p className="mt-2 text-gray-800">{n.summary || n.content || JSON.stringify(n)}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No handover rows in demo data — ISBAR handovers and shift ledger would appear here.</p>
                )}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-orange-200 bg-white p-6 shadow-md">
                  <h3 className="mb-3 text-lg font-bold text-gray-900">Complaints &amp; feedback</h3>
                  {(resident.qualityCompliance?.complaintsFeedback || []).length > 0 ? (
                    <ul className="space-y-2 text-sm">
                      {resident.qualityCompliance.complaintsFeedback.map((c, i) => (
                        <li key={i} className="rounded-lg border border-orange-100 bg-orange-50/60 p-3">
                          <span className="font-semibold">{c.date || '—'}</span> — {c.summary || c.description || JSON.stringify(c)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No complaints or formal feedback logged for this resident in mock data.</p>
                  )}
                </div>
                <div className="rounded-2xl border border-teal-200 bg-white p-6 shadow-md">
                  <h3 className="mb-3 text-lg font-bold text-gray-900">Monthly care statement</h3>
                  {resident.qualityCompliance?.monthlyCareStatement ? (
                    <pre className="max-h-48 overflow-auto rounded-lg bg-teal-50/80 p-3 text-xs text-gray-800">
                      {typeof resident.qualityCompliance.monthlyCareStatement === 'string'
                        ? resident.qualityCompliance.monthlyCareStatement
                        : JSON.stringify(resident.qualityCompliance.monthlyCareStatement, null, 2)}
                    </pre>
                  ) : (
                    <p className="text-sm text-gray-500">Not issued in demo — pricing-period statements and fee breakdowns attach here.</p>
                  )}
                </div>
              </div>

              {(resident.qualityCompliance?.careMinutesLog || []).length > 0 && (
                <div className="rounded-2xl border border-emerald-200 bg-white p-6 shadow-md">
                  <h3 className="mb-3 text-lg font-bold text-gray-900">Care minutes log (sample)</h3>
                  <ul className="space-y-2 text-sm">
                    {resident.qualityCompliance.careMinutesLog.map((row, i) => (
                      <li key={i} className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2 font-mono text-xs">
                        {typeof row === 'string' ? row : JSON.stringify(row)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {resident.qualityCompliance?.starRatingContribution != null && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-6 shadow-md">
                  <h3 className="mb-2 text-lg font-bold text-gray-900">Star rating contribution detail</h3>
                  <pre className="overflow-auto text-xs text-gray-800">
                    {typeof resident.qualityCompliance.starRatingContribution === 'string'
                      ? resident.qualityCompliance.starRatingContribution
                      : JSON.stringify(resident.qualityCompliance.starRatingContribution, null, 2)}
                  </pre>
                </div>
              )}

              {residentSIRS.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-red-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">SIRS Notifications</h3>
                  <div className="space-y-3">{residentSIRS.map((s, i) => (
                    <div key={i} className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <div className="flex justify-between"><span className="font-bold text-red-800">{s.type}</span><span className="text-sm text-gray-500">{s.date}</span></div>
                      <div className="text-sm mt-1">{s.description}</div>
                    </div>
                  ))}</div>
                </div>
              )}

              {residentQualityIncidents.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-orange-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Incident Reports</h3>
                  <div className="space-y-3">{residentQualityIncidents.map((inc, i) => (
                    <div key={i} className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <div className="flex justify-between"><span className="font-bold">{inc.type}</span><span className="text-sm">{inc.date}</span></div>
                      <div className="text-sm mt-1">{inc.description}</div>
                      <span className={`text-xs px-2 py-0.5 rounded mt-2 inline-block ${inc.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{inc.status}</span>
                    </div>
                  ))}</div>
                </div>
              )}
            </div>
          )}

          {/* AN-ACC & Funding Tab */}
          {isAgedCare && activeTab === 'funding' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-700 to-emerald-600 text-white p-8 rounded-2xl shadow-2xl">
                <h2 className="text-3xl font-bold">AN-ACC & Funding</h2>
                <p className="text-green-100 text-lg">Classification, subsidies, accommodation, and fee structure</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl border-2 border-green-200 p-6 shadow-lg text-center">
                  <div className="text-sm text-gray-500 mb-1">AN-ACC Class</div>
                  <div className="text-5xl font-bold text-green-700">{resident.funding?.anaccClass || resident.anacc?.class || '—'}</div>
                  <div className="text-sm text-gray-500 mt-2">Classified {resident.funding?.anaccClassificationDate || '—'}</div>
                  {resident.funding?.anaccReassessmentDue && <div className="text-xs text-orange-600 mt-1">Reassessment due: {resident.funding.anaccReassessmentDue}</div>}
                </div>
                <div className="bg-white rounded-xl border-2 border-green-200 p-6 shadow-lg text-center">
                  <div className="text-sm text-gray-500 mb-1">Daily Government Subsidy</div>
                  <div className="text-4xl font-bold text-green-700">${((resident.funding?.baseCareTariff || 0) + (resident.funding?.anaccSubsidyDaily || 0) + (resident.funding?.supplements || []).reduce((s, x) => s + (x.dailyRate || 0), 0)).toFixed(2)}</div>
                  <div className="text-xs text-gray-500 mt-2">Base: ${resident.funding?.baseCareTariff?.toFixed(2) || '0.00'} + Variable: ${resident.funding?.anaccSubsidyDaily?.toFixed(2) || '0.00'}</div>
                </div>
                <div className="bg-white rounded-xl border-2 border-green-200 p-6 shadow-lg text-center">
                  <div className="text-sm text-gray-500 mb-1">Daily Resident Fees</div>
                  <div className="text-4xl font-bold text-blue-700">${((resident.funding?.basicDailyFee || 0) + (resident.funding?.incomeTestedCareFee || 0) + (resident.funding?.dapDailyRate || 0)).toFixed(2)}</div>
                  <div className="text-xs text-gray-500 mt-2">BDF: ${resident.funding?.basicDailyFee?.toFixed(2) || '0.00'} + ITCF: ${resident.funding?.incomeTestedCareFee?.toFixed(2) || '0.00'}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border-2 border-green-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Accommodation</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-500">Payment Type:</span> <span className="font-bold">{resident.funding?.accommodationPaymentType || '—'}</span></div>
                    {resident.funding?.radAmountAgreed && <div><span className="text-gray-500">RAD Agreed:</span> <span className="font-medium">${resident.funding.radAmountAgreed.toLocaleString()}</span></div>}
                    {resident.funding?.radBalance && <div><span className="text-gray-500">RAD Balance:</span> <span className="font-medium">${resident.funding.radBalance.toLocaleString()}</span></div>}
                    {resident.funding?.dapDailyRate && <div><span className="text-gray-500">DAP Daily Rate:</span> <span className="font-medium">${resident.funding.dapDailyRate.toFixed(2)}</span></div>}
                    <div><span className="text-gray-500">Means Testing:</span> <span className="font-medium">{resident.funding?.meansTestingStatus || '—'}</span></div>
                    <div><span className="text-gray-500">Agreement Date:</span> <span className="font-medium">{resident.funding?.accommodationAgreementDate || '—'}</span></div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border-2 border-green-200 p-6 shadow-lg">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Supplements</h3>
                  {(resident.funding?.supplements || []).length > 0 ? (
                    <div className="space-y-2">{resident.funding.supplements.map((s, i) => (
                      <div key={i} className="flex justify-between items-center bg-green-50 p-3 rounded-lg">
                        <span className="font-medium">{s.type}</span>
                        <span className="font-bold text-green-700">${s.dailyRate?.toFixed(2)}/day</span>
                      </div>
                    ))}</div>
                  ) : <p className="text-gray-500 text-sm">No supplements currently active</p>}
                  {resident.funding?.extraServicesAgreement?.enabled && (
                    <div className="mt-4 bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <span className="font-bold text-purple-700">Extra Services:</span>
                      <p className="text-sm mt-1">{resident.funding.extraServicesAgreement.details}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Activities Tab — data-driven from resident model */}
          {activeTab === 'activities' && (() => {
            const pl = resident.personalLife || {};
            const hobbies = pl.hobbiesInterests || [];
            const wellbeing = resident.assessments?.wellbeingAssessments || [];
            const scheduledActivities = profileData.activities?.scheduled || [];
            const completedActivities = profileData.activities?.completed || [];
            return (
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-purple-800 via-pink-600 to-fuchsia-700 p-8 text-white shadow-2xl">
                <p className="text-xs font-bold uppercase tracking-widest text-purple-100/90">Engagement &amp; wellbeing</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">Activities &amp; Engagement</h2>
                <p className="mt-2 text-base text-purple-50/90">
                  Activity preferences, participation tracking, and social engagement for{' '}
                  <span className="font-semibold text-white">{profileData.personalInfo?.name}</span>.
                </p>
              </div>

              {/* Interest-Based Activity Matching */}
              <div className="rounded-xl border bg-white p-5 shadow-sm">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4">Activity Preferences &amp; Interests</h3>
                {hobbies.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {hobbies.map((h, i) => (
                      <span key={i} className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium border border-purple-200">{h}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">No registered interests. Add via the Personal Life tab.</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {pl.musicPreferences && (
                    <div className="rounded-lg bg-pink-50 border border-pink-200 p-3">
                      <Music className="w-4 h-4 text-pink-600 mb-1" />
                      <p className="text-xs text-gray-500">Music</p>
                      <p className="text-sm font-medium text-gray-800 line-clamp-2">{pl.musicPreferences}</p>
                    </div>
                  )}
                  {pl.socialPreferences && (
                    <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                      <Users className="w-4 h-4 text-blue-600 mb-1" />
                      <p className="text-xs text-gray-500">Social</p>
                      <p className="text-sm font-medium text-gray-800 line-clamp-2">{pl.socialPreferences}</p>
                    </div>
                  )}
                  {resident.demographics?.dailyRoutinePreferences?.mealPreferences && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                      <Utensils className="w-4 h-4 text-amber-600 mb-1" />
                      <p className="text-xs text-gray-500">Meals</p>
                      <p className="text-sm font-medium text-gray-800 line-clamp-2">{resident.demographics.dailyRoutinePreferences.mealPreferences}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Scheduled Activities */}
              <div className="rounded-xl border bg-white p-5 shadow-sm">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-500" /> Scheduled Activities
                </h3>
                {scheduledActivities.length > 0 ? (
                  <div className="space-y-3">
                    {scheduledActivities.slice(0, 8).map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50/50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm">{a.name}</p>
                          <p className="text-xs text-gray-500">{a.date} · {a.time} · {a.location}{a.facilitator ? ` · ${a.facilitator}` : ''}</p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0 ml-3">
                          {a.category && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded">{a.category}</span>}
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">{a.status || 'Scheduled'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No scheduled activities. Activities are matched based on the resident's registered interests and care plan goals.</p>
                )}
              </div>

              {/* Completed / Recent */}
              {completedActivities.length > 0 && (
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" /> Recent Activities
                  </h3>
                  <div className="space-y-3">
                    {completedActivities.slice(0, 6).map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border p-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm">{a.name}</p>
                          <p className="text-xs text-gray-500">{a.date} · {a.location}</p>
                          {a.notes && <p className="text-xs text-gray-600 mt-0.5 italic">&ldquo;{a.notes}&rdquo;</p>}
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0 ml-3">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">Completed</span>
                          {a.rating && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded">{a.rating}/5</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Wellbeing Assessments */}
              {wellbeing.length > 0 && (
                <div className="rounded-xl border bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">Wellbeing Assessments</h3>
                  <div className="space-y-2">
                    {wellbeing.map((w, i) => (
                      <div key={i} className="rounded-lg bg-green-50 border border-green-200 p-3 flex justify-between text-sm">
                        <span className="font-medium">{w.type || w.tool || 'Assessment'}</span>
                        <span className="text-gray-500">{w.date || '—'}{w.score != null ? ` — Score: ${w.score}` : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Alerts & Automation Tab */}
          {activeTab === 'alerts' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold mb-2">Alerts & Automation</h2>
                <p className="opacity-90">Critical alerts, automated workflows, and safety monitoring for {profileData.personalInfo.name}</p>
              </div>

              {/* Critical Alerts */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-red-600">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Critical Alerts
                </h3>
                <div className="space-y-4">
                  {profileData.medications?.current?.filter(med => 
                    med.interactions?.some(interaction => interaction.severity === 'High') ||
                    med.status === 'Overdue' ||
                    med.status === 'Due Soon'
                  ).map((medication, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-red-800">{medication.medication}</h4>
                          <p className="text-sm text-red-600">
                            {medication.interactions?.find(i => i.severity === 'High') ? 
                              'High-risk drug interaction detected' : 
                              `Status: ${medication.status}`
                            }
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-red-200 text-red-800 text-xs font-medium rounded-full">
                          {medication.interactions?.find(i => i.severity === 'High') ? 'HIGH RISK' : 'ALERT'}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {mergedAssessments?.filter(assessment => 
                    assessment.nextDue && new Date(assessment.nextDue) < new Date()
                  ).map((assessment, index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-orange-800">{assessment.type}</h4>
                          <p className="text-sm text-orange-600">Overdue since {new Date(assessment.nextDue).toLocaleDateString()}</p>
                        </div>
                        <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-medium rounded-full">
                          OVERDUE
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Automated Workflows */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-600">
                  <Settings className="h-5 w-5 mr-2" />
                  Automated Workflows
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Medication Reminders</h4>
                    <p className="text-sm text-blue-600 mb-3">Automated alerts for medication administration</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-blue-600">Status: Active</span>
                      <button className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors">
                        Configure
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2">Fall Risk Monitoring</h4>
                    <p className="text-sm text-green-600 mb-3">Continuous monitoring for fall risk indicators</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-green-600">Status: Active</span>
                      <button className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors">
                        Configure
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">Assessment Scheduling</h4>
                    <p className="text-sm text-purple-600 mb-3">Automatic scheduling of routine assessments</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-purple-600">Status: Active</span>
                      <button className="px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors">
                        Configure
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Emergency Alerts</h4>
                    <p className="text-sm text-orange-600 mb-3">Immediate notifications for critical situations</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-orange-600">Status: Active</span>
                      <button className="px-3 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition-colors">
                        Configure
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Safety Monitoring */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-green-600">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Safety Monitoring
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">24/7</div>
                    <div className="text-sm text-green-600">Continuous Monitoring</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">5</div>
                    <div className="text-sm text-blue-600">Active Sensors</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">99.8%</div>
                    <div className="text-sm text-purple-600">Uptime</div>
                  </div>
                </div>
              </div>

              {/* Alert History */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-700">
                  <Clock className="h-5 w-5 mr-2" />
                  Recent Alert History
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Medication administered on time</p>
                        <p className="text-xs text-gray-500">2 hours ago</p>
                      </div>
                    </div>
                    <span className="text-xs text-green-600 font-medium">RESOLVED</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Assessment reminder sent</p>
                        <p className="text-xs text-gray-500">4 hours ago</p>
                      </div>
                    </div>
                    <span className="text-xs text-yellow-600 font-medium">PENDING</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Fall risk assessment completed</p>
                        <p className="text-xs text-gray-500">1 day ago</p>
                      </div>
                    </div>
                    <span className="text-xs text-blue-600 font-medium">COMPLETED</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold mb-2">Health Analytics & Trends</h2>
                <p className="opacity-90">Comprehensive health metrics and trends for {profileData.personalInfo.name}</p>
              </div>

              {/* Health Metrics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Health Status</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Overall Health</span>
                      <span className="font-semibold text-green-600">{profileData.analytics.healthMetrics.overallHealth}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fall Risk</span>
                      <span className="font-semibold text-yellow-600">{profileData.analytics.healthMetrics.fallRisk}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mobility Score</span>
                      <span className="font-semibold text-blue-600">{profileData.analytics.healthMetrics.mobilityScore}/10</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cognitive Score</span>
                      <span className="font-semibold text-purple-600">{profileData.analytics.healthMetrics.cognitiveScore}/30</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Engagement</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Social Activities</span>
                      <span className="font-semibold text-green-600">{profileData.analytics.engagement.socialActivities}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Medication Compliance</span>
                      <span className="font-semibold text-blue-600">{profileData.analytics.engagement.medicationCompliance}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Care Plan Adherence</span>
                      <span className="font-semibold text-purple-600">{profileData.analytics.engagement.carePlanAdherence}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Activity Participation</span>
                      <span className="font-semibold text-orange-600">{profileData.analytics.engagement.activityParticipation}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Trends</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weight</span>
                      <span className={`font-semibold ${profileData.analytics.trends.weightStable ? 'text-green-600' : 'text-red-600'}`}>
                        {profileData.analytics.trends.weightStable ? 'Stable' : 'Changing'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Blood Pressure</span>
                      <span className={`font-semibold ${profileData.analytics.trends.bloodPressureImproving ? 'text-green-600' : 'text-red-600'}`}>
                        {profileData.analytics.trends.bloodPressureImproving ? 'Improving' : 'Stable'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cognitive</span>
                      <span className="font-semibold text-yellow-600">{profileData.analytics.trends.cognitiveDecline}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Social Engagement</span>
                      <span className="font-semibold text-green-600">{profileData.analytics.trends.socialEngagement}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Health Trends Charts Placeholder */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">Health Trends Over Time</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <h4 className="font-medium text-gray-900 mb-2">Weight Trend</h4>
                    <div className="text-2xl font-bold text-blue-600">68 kg</div>
                    <div className="text-sm text-gray-600">Stable over 3 months</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <h4 className="font-medium text-gray-900 mb-2">Blood Pressure</h4>
                    <div className="text-2xl font-bold text-green-600">135/80</div>
                    <div className="text-sm text-gray-600">Improving trend</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aged Care-Specific Tabs */}
          {isAgedCare && supportsANACC && activeTab === 'anacc-assessment' && (
            <div className="space-y-6">
              <ANACCAssessment residentId={resident.id} />
            </div>
          )}

          {/* Care Minutes Tab - Aged Care Only */}
          {isAgedCare && supportsANACC && activeTab === 'care-minutes' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold mb-2">Care Minutes</h2>
                <p className="opacity-90">Care minutes compliance tracking for {profileData.personalInfo?.name || 'Resident'}</p>
              </div>

              {/* Today's Care Minutes */}
              {loadingCareMinutes ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading care minutes data...</p>
                </div>
              ) : careMinutesData ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-600" />
                      Today's Care Minutes ({careMinutesData.date || new Date().toISOString().split('T')[0]})
                    </h3>
                    <div className="flex items-center gap-2">
                      {careMinutesData.compliant && careMinutesData.rnCompliant ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                          ✓ Compliant
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                          ⚠ Non-Compliant
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Total Minutes</p>
                      <p className={`text-3xl font-bold ${careMinutesData.compliant ? 'text-green-600' : 'text-red-600'}`}>
                        {careMinutesData.totalMinutes || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Target: {careMinutesData.anaccAdjustedTarget || careMinutesData.targetMinutes || 215}</p>
                      {careMinutesData.complianceGap > 0 && (
                        <p className="text-xs text-red-600 font-medium mt-1">⚠ Gap: {careMinutesData.complianceGap} min</p>
                      )}
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">RN Minutes</p>
                      <p className={`text-3xl font-bold ${careMinutesData.rnCompliant ? 'text-green-600' : 'text-red-600'}`}>
                        {careMinutesData.rnMinutes || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Target: {careMinutesData.targetRNMinutes || 44}</p>
                      {careMinutesData.rnComplianceGap > 0 && (
                        <p className="text-xs text-red-600 font-medium mt-1">⚠ Gap: {careMinutesData.rnComplianceGap} min</p>
                      )}
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">AN-ACC Class</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {careMinutesData.anaccClass || resident?.anaccClass || resident?.anacc?.class || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Classification</p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1">Compliance Rate</p>
                      <p className={`text-3xl font-bold ${careMinutesData.compliant && careMinutesData.rnCompliant ? 'text-green-600' : 'text-red-600'}`}>
                        {careMinutesData.compliant && careMinutesData.rnCompliant ? '100%' : 
                         careMinutesData.compliant ? '50%' : '0%'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Overall Status</p>
                    </div>
                  </div>

                  {/* Breakdown by Role */}
                  {careMinutesData.minutes && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold mb-3 text-gray-800">Breakdown by Role</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <div className="text-xs text-gray-600">Registered Nurse (RN)</div>
                          <div className="text-lg font-semibold text-gray-900">{careMinutesData.minutes?.rn || 0} min</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600">Enrolled Nurse (EN)</div>
                          <div className="text-lg font-semibold text-gray-900">{careMinutesData.minutes?.en || 0} min</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600">Personal Care Assistant</div>
                          <div className="text-lg font-semibold text-gray-900">{careMinutesData.minutes?.pca || 0} min</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600">Allied Health</div>
                          <div className="text-lg font-semibold text-gray-900">{careMinutesData.minutes?.allied || 0} min</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No care minutes data available for today</p>
                    <button
                      onClick={async () => {
                        setLoadingCareMinutes(true);
                        try {
                          const today = new Date().toISOString().split('T')[0];
                          const cmResult = await CareMinutesService.getByResidentAndDate(resident.id, today);
                          if (cmResult.success && cmResult.data) {
                            setCareMinutesData(cmResult.data);
                          }
                        } catch (error) {
                          console.error('Error loading care minutes:', error);
                        } finally {
                          setLoadingCareMinutes(false);
                        }
                      }}
                      disabled={loadingCareMinutes}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
                    >
                      {loadingCareMinutes ? 'Loading...' : 'Load Care Minutes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Historical Data */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Historical Data
                </h3>
                <p className="text-sm text-gray-600 mb-4">View monthly and quarterly summaries in the Quality → Care Minutes dashboard</p>
                <button
                  onClick={() => {
                    actions.setActiveTab('quality');
                    sessionStorage.setItem('qualitySubTab', 'care-minutes');
                    window.dispatchEvent(new CustomEvent('setQualitySubTab', { detail: 'care-minutes' }));
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold"
                >
                  View Full Dashboard →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Medication Edit Modal - Controlled Inputs */}
      {showEditModal && editingMedication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Edit Medication</h3>
                <button onClick={() => { setShowEditModal(false); setEditMedFormData(null); }} className="text-white hover:text-gray-200 transition-colors">✕</button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Medication Name</label>
                  <input type="text" value={(editMedFormData || editingMedication).medication || ''} onChange={(e) => setEditMedFormData((p) => ({ ...(p || editingMedication), medication: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dosage</label>
                  <input type="text" value={(editMedFormData || editingMedication).dosage || ''} onChange={(e) => setEditMedFormData((p) => ({ ...(p || editingMedication), dosage: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                  <input type="text" value={(editMedFormData || editingMedication).frequency || ''} onChange={(e) => setEditMedFormData((p) => ({ ...(p || editingMedication), frequency: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
                  <textarea value={(editMedFormData || editingMedication).instructions || ''} onChange={(e) => setEditMedFormData((p) => ({ ...(p || editingMedication), instructions: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button onClick={() => { setShowEditModal(false); setEditMedFormData(null); }} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button onClick={() => handleSaveMedication(editingMedication)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Medication to Resident Modal */}
      {showAddMedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-5 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Add Medication to {resident?.name}</h3>
              <button onClick={() => setShowAddMedModal(false)} className="text-white hover:text-gray-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="block text-sm font-medium mb-1">Medication *</label><input type="text" value={addMedForm.medication} onChange={(e) => setAddMedForm(p => ({...p, medication: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Metformin 500mg" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Dosage</label><input type="text" value={addMedForm.dosage} onChange={(e) => setAddMedForm(p => ({...p, dosage: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">Frequency</label><input type="text" value={addMedForm.frequency} onChange={(e) => setAddMedForm(p => ({...p, frequency: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Route</label><select value={addMedForm.route} onChange={(e) => setAddMedForm(p => ({...p, route: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="oral">Oral</option><option value="injection">Injection</option><option value="topical">Topical</option><option value="inhalation">Inhalation</option></select></div>
                <div><label className="block text-sm font-medium mb-1">Prescriber</label><input type="text" value={addMedForm.prescriber} onChange={(e) => setAddMedForm(p => ({...p, prescriber: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Start Date</label><input type="date" value={addMedForm.startDate} onChange={(e) => setAddMedForm(p => ({...p, startDate: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Instructions</label><textarea value={addMedForm.instructions} onChange={(e) => setAddMedForm(p => ({...p, instructions: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" rows={2} /></div>
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button onClick={() => setShowAddMedModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleAddMedToResident} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Add Medication</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discontinue Medication Modal */}
      {showDiscontinueMedModal && discontinueMedTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-5 rounded-t-xl">
              <h3 className="text-lg font-bold">Discontinue Medication</h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-gray-700">Are you sure you want to discontinue <strong>{discontinueMedTarget.medication}</strong>?</p>
              <div><label className="block text-sm font-medium mb-1">Reason *</label><textarea value={discontinueReason} onChange={(e) => setDiscontinueReason(e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} placeholder="Enter reason for discontinuation..." /></div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowDiscontinueMedModal(false); setDiscontinueMedTarget(null); setDiscontinueReason(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleDiscontinueMed} disabled={!discontinueReason} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Discontinue</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-5 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Upload Document</h3>
              <button onClick={() => setShowDocUploadModal(false)} className="text-white hover:text-gray-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="block text-sm font-medium mb-1">Document Name *</label><input type="text" value={docUploadForm.name} onChange={(e) => setDocUploadForm(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Care Plan Review" /></div>
              <div><label className="block text-sm font-medium mb-1">Type</label><select value={docUploadForm.type} onChange={(e) => setDocUploadForm(p => ({...p, type: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="Clinical">Clinical</option><option value="Legal">Legal</option><option value="Administrative">Administrative</option><option value="Financial">Financial</option></select></div>
              <div><label className="block text-sm font-medium mb-1">Category</label><input type="text" value={docUploadForm.category} onChange={(e) => setDocUploadForm(p => ({...p, category: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Description</label><textarea value={docUploadForm.description} onChange={(e) => setDocUploadForm(p => ({...p, description: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" rows={2} /></div>
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button onClick={() => setShowDocUploadModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleDocUpload} disabled={!docUploadForm.name} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">Upload</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Edit Profile: {resident?.name}</h3>
              <button onClick={() => setShowEditProfileModal(false)} className="text-white hover:text-gray-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium mb-1">Name</label><input type="text" defaultValue={resident?.name} onChange={(e) => setEditProfileData(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Room</label><input type="text" defaultValue={resident?.room} onChange={(e) => setEditProfileData(p => ({...p, room: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Ward</label><input type="text" defaultValue={resident?.ward} onChange={(e) => setEditProfileData(p => ({...p, ward: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">Care Level</label><select defaultValue={resident?.careLevel} onChange={(e) => setEditProfileData(p => ({...p, careLevel: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option></select></div>
              <div><label className="block text-sm font-medium mb-1">Medicare</label><input type="text" defaultValue={resident?.medicare} onChange={(e) => setEditProfileData(p => ({...p, medicare: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-medium mb-1">GP Name</label><input type="text" defaultValue={resident?.gp?.name || ''} onChange={(e) => setEditProfileData(p => ({...p, gpName: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2"><label className="block text-sm font-medium mb-1">Allergies (comma-separated)</label><input type="text" defaultValue={(resident?.allergies || []).join(', ')} onChange={(e) => setEditProfileData(p => ({...p, allergies: e.target.value.split(',').map(a => a.trim())}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2"><label className="block text-sm font-medium mb-1">Dietary Restrictions (comma-separated)</label><input type="text" defaultValue={(resident?.dietaryRestrictions || []).join(', ')} onChange={(e) => setEditProfileData(p => ({...p, dietaryRestrictions: e.target.value.split(',').map(d => d.trim())}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2 flex justify-end gap-3 pt-3 border-t">
                <button onClick={() => setShowEditProfileModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleEditProfileSubmit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save Profile</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Medication Refill Modal */}
      {showRefillModal && editingMedication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-violet-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Process Refill</h3>
                <button 
                  onClick={() => setShowRefillModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">{editingMedication.medication}</h4>
                  <p className="text-sm text-purple-700">Dosage: {editingMedication.dosage}</p>
                  <p className="text-sm text-purple-700">Refills Left: {editingMedication.refillsLeft}/{editingMedication.totalRefills}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quantity to Refill</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="30">30 days supply</option>
                    <option value="60">60 days supply</option>
                    <option value="90">90 days supply</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pharmacy</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="main">Main Pharmacy</option>
                    <option value="external">External Pharmacy</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button 
                  onClick={() => setShowRefillModal(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleProcessRefill(editingMedication)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Process Refill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Medication Schedule Modal */}
      {showScheduleModal && editingMedication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Schedule Medication</h3>
                <button 
                  onClick={() => setShowScheduleModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">{editingMedication.medication}</h4>
                  <p className="text-sm text-green-700">Dosage: {editingMedication.dosage}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                    <input 
                      type="time" 
                      defaultValue="08:00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                      <option value="daily">Daily</option>
                      <option value="twice">Twice Daily</option>
                      <option value="three">Three Times Daily</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reminder Settings</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" defaultChecked className="mr-2" />
                      <span className="text-sm">Send reminder 15 minutes before</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" defaultChecked className="mr-2" />
                      <span className="text-sm">Send reminder if missed</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button 
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleScheduleMedication(editingMedication, {})}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Save Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Medication Report Modal */}
      {showReportModal && editingMedication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Generate Report</h3>
                <button 
                  onClick={() => setShowReportModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-2">{editingMedication.medication}</h4>
                  <p className="text-sm text-orange-700">Dosage: {editingMedication.dosage}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <option value="compliance">Compliance Report</option>
                    <option value="side_effects">Side Effects Report</option>
                    <option value="interactions">Drug Interactions Report</option>
                    <option value="effectiveness">Effectiveness Report</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <div className="grid grid-cols-2 gap-4">
                    <input 
                      type="date" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <input 
                      type="date" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Include Data</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="checkbox" defaultChecked className="mr-2" />
                      <span className="text-sm">Medication adherence</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" defaultChecked className="mr-2" />
                      <span className="text-sm">Side effects</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" defaultChecked className="mr-2" />
                      <span className="text-sm">Vital signs</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button 
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleReportMedication(editingMedication)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Residents = () => {
  const { state, dispatch } = useAppContext();
  const actions = useAppActions();
  const { 
    selectedFacility, 
    selectedWard, 
    searchQuery, 
    currentUser, 
    deviceInfo 
  } = state;
  const { optimization } = deviceInfo;
  
  // Get facility features for conditional UI
  const { isNDIS, isAgedCare, facilityConfig } = useFacilityFeatures();
  const { showToast } = useToast();

  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedResident, setSelectedResident] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSelectResident = useCallback((resident) => {
    setProfileLoading(true);
    setSelectedResident(resident);
    setTimeout(() => setProfileLoading(false), 300);
  }, []);

  const [showAddResidentModal, setShowAddResidentModal] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [dischargeTarget, setDischargeTarget] = useState(null);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeDate, setDischargeDate] = useState(new Date().toISOString().split('T')[0]);
  const [addResidentForm, setAddResidentForm] = useState({
    name: '', dateOfBirth: '', room: '', ward: 'ward_a', facility: 'sunshine',
    medicare: '', careLevel: 'Low', gender: '', fundingType: 'AN-ACC',
    allergies: '', dietaryRestrictions: '', emergencyContactName: '', emergencyContactPhone: '',
  });
  const handleAddResidentSubmit = async () => {
    const result = await ResidentService.create({
      name: addResidentForm.name,
      dateOfBirth: addResidentForm.dateOfBirth,
      room: addResidentForm.room,
      ward: addResidentForm.ward,
      facility: addResidentForm.facility,
      medicare: addResidentForm.medicare,
      careLevel: addResidentForm.careLevel,
      gender: addResidentForm.gender,
      fundingType: addResidentForm.fundingType,
      allergies: addResidentForm.allergies ? addResidentForm.allergies.split(',').map(a => a.trim()) : [],
      dietaryRestrictions: addResidentForm.dietaryRestrictions ? addResidentForm.dietaryRestrictions.split(',').map(d => d.trim()) : [],
      emergencyContact: {
        name: addResidentForm.emergencyContactName,
        phone: addResidentForm.emergencyContactPhone,
      },
      photo: '👵', status: 'active',
    });
    if (result.success) {
      dispatch({ type: ACTIONS.ADD_RESIDENT, payload: result.data });
      setShowAddResidentModal(false);
      setAddResidentForm({ name: '', dateOfBirth: '', room: '', ward: 'ward_a', facility: 'sunshine', medicare: '', careLevel: 'Low', gender: '', fundingType: 'AN-ACC', allergies: '', dietaryRestrictions: '', emergencyContactName: '', emergencyContactPhone: '' });
      showToast('Resident admitted successfully', 'success');
    } else {
      showToast('Error: ' + (result.errors?.join(', ') || result.error), 'error');
    }
  };

  const handleDischargeResident = async () => {
    if (!dischargeTarget) return;
    const result = await ResidentService.discharge(dischargeTarget.id, dischargeReason, dischargeDate);
    if (result.success) {
      dispatch({ type: ACTIONS.REMOVE_RESIDENT, payload: dischargeTarget.id });
      setShowDischargeModal(false);
      setDischargeTarget(null);
      setDischargeReason('');
      showToast('Resident discharged successfully', 'success');
    } else {
      showToast('Error: ' + result.error, 'error');
    }
  };

  const residents = getFilteredResidents(selectedFacility, selectedWard, searchQuery, currentUser);

  const getCareLevelColor = (level) => {
    switch (level) {
      case 'Critical': return 'text-red-600 bg-red-100';
      case 'High': return 'text-orange-600 bg-orange-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getFallRiskColor = (risk) => {
    switch (risk) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderResidentCard = (resident, index) => {
    // Check if this is an NDIS participant
    const isNDISParticipant = !!resident.ndisParticipantId;
    
    // Get comprehensive profile data for this resident
    const profileData = ResidentProfileExportModule.generateResidentProfileData(resident);
    const persistedAssess = getPersistedAssessmentCards(resident.id);
    const baseAssess = profileData.assessments || [];
    const assessmentCount =
      baseAssess.length +
      persistedAssess.filter((p) => p?.id && !baseAssess.some((b) => b.id === p.id)).length;
    
    // Calculate real statistics
    const medicationCount = profileData.medications?.current?.length || 0;
    const appointmentCount = profileData.appointments?.length || 0;
    const documentCount = profileData.documents?.length || 0;
    
    // Aged Care specific data - only for aged care residents
    let careMinutesToday = null;
    let anaccClass = null;
    let isCareMinutesCompliant = true;
    let residentSIRS = [];
    let hasActiveSIRS = false;
    
    if (!isNDISParticipant) {
      // Get care minutes data for today (Aged Care only)
      const today = new Date().toISOString().split('T')[0];
      careMinutesToday = careMinutesData.find(cm => 
        cm.residentId === resident.id && cm.date === today
      );
      
      // Get AN-ACC class from care minutes or resident (Aged Care only)
      anaccClass = careMinutesToday?.anaccClass || resident.anaccClass || null;
      isCareMinutesCompliant = careMinutesToday?.compliant && careMinutesToday?.rnCompliant;
      
      // Get SIRS incidents for this resident (Aged Care only)
      residentSIRS = sirsData.filter(s => s.residentId === resident.id);
      hasActiveSIRS = residentSIRS.some(s => s.status === 'Under Investigation');
    }
    
    // Get quality incidents (both NDIS and Aged Care)
    const residentQualityIncidents = qualityIncidents.filter(i => 
      i.resident.toLowerCase().includes(resident.name.toLowerCase())
    );
    
    // Check for alerts and warnings
    const hasHighRiskMedications = profileData.medications?.current?.some(med => 
      med.interactions?.some(interaction => interaction.severity === 'High')
    ) || false;
    
    const hasUpcomingAppointments = profileData.appointments?.some(apt => 
      new Date(apt.date) > new Date() && new Date(apt.date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    ) || false;
    
    const hasOverdueAssessments = profileData.assessments?.some(assessment => 
      new Date(assessment.nextDue) < new Date()
    ) || false;
    
    const hasMedicationAlerts = profileData.medications?.current?.some(med => 
      med.status === 'Overdue' || med.status === 'Due Soon'
    ) || false;

    // Critical alerts - different for NDIS vs Aged Care
    const hasCriticalAlerts = isNDISParticipant 
      ? residentQualityIncidents.length > 0 || hasMedicationAlerts
      : hasActiveSIRS || !isCareMinutesCompliant || residentQualityIncidents.length > 0;

    return (
      <div 
        key={resident.id} 
        className="group relative bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-2xl border-2 border-blue-200 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer min-h-[450px] flex flex-col overflow-hidden"
        style={{
          animationDelay: `${index * 150}ms`,
          animation: 'fadeInUp 0.8s ease-out forwards'
        }}
      >
        {/* Alert Badge - Enhanced */}
        {(hasCriticalAlerts || hasHighRiskMedications || hasOverdueAssessments || hasMedicationAlerts) && (
          <div className="absolute top-3 right-3 z-10">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg"></div>
          </div>
        )}

        {/* Resident Avatar & Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
            {resident.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 text-lg">{resident.name}</h3>
            <p className="text-sm text-gray-600">{resident.room} • Age {resident.age}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Tooltip content="Assessed care complexity level for staffing and funding" position="bottom">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                  {resident.careLevel || 'Medium'} Care
                </span>
              </Tooltip>
              {anaccClass && (
                <Tooltip content="Australian National Aged Care Classification — determines government funding tier" position="bottom">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                    AN-ACC {anaccClass}
                  </span>
                </Tooltip>
              )}
              {resident.mmseScore && (
                <Tooltip content="Mini-Mental State Examination — cognitive function score (0-30, higher is better)" position="bottom">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                    MMSE: {resident.mmseScore}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      
        {/* Compliance & Quality Status - Only show for Aged Care */}
        {!isNDISParticipant && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {/* Care Minutes Compliance - Clickable */}
            {careMinutesToday ? (
            <div 
              onClick={(e) => {
                e.stopPropagation();
                // Navigate to Quality tab and set care-minutes sub-tab
                actions.setActiveTab('quality');
                // Store the sub-tab to open in sessionStorage
                sessionStorage.setItem('qualitySubTab', 'care-minutes');
                // Trigger a custom event for Quality component to listen
                window.dispatchEvent(new CustomEvent('setQualitySubTab', { detail: 'care-minutes' }));
              }}
              className={`p-3 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all ${
                isCareMinutesCompliant 
                  ? 'bg-green-50 border-green-200 hover:border-green-300' 
                  : 'bg-red-50 border-red-200 hover:border-red-300'
              }`}
              title="Click to view Care Minutes Dashboard"
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock className={`w-4 h-4 ${isCareMinutesCompliant ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-xs font-semibold text-gray-700">Care Minutes</span>
              </div>
              <div className={`text-lg font-bold ${isCareMinutesCompliant ? 'text-green-600' : 'text-red-600'}`}>
                {careMinutesToday.totalMinutes} min
              </div>
              <div className="text-xs text-gray-600">
                {isCareMinutesCompliant ? (
                  <span className="text-green-700 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Compliant
                  </span>
                ) : (
                  <span className="text-red-700">⚠ Gap: {careMinutesToday.complianceGap} min</span>
                )}
              </div>
            </div>
          ) : (
            <div className="p-3 rounded-lg border-2 border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-semibold text-gray-500">Care Minutes</span>
              </div>
              <div className="text-sm text-gray-500">No data today</div>
            </div>
          )}

          {/* AN-ACC Class */}
          {anaccClass ? (
            <div className="p-3 rounded-lg border-2 border-purple-200 bg-purple-50">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-semibold text-gray-700">AN-ACC Class</span>
              </div>
              <div className="text-lg font-bold text-purple-600">{anaccClass}</div>
              <div className="text-xs text-gray-600">Active Classification</div>
            </div>
          ) : (
            <div className="p-3 rounded-lg border-2 border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-semibold text-gray-500">AN-ACC</span>
              </div>
              <div className="text-sm text-gray-500">Not assessed</div>
            </div>
          )}
          </div>
        )}

        {/* Quick Stats with Real Data - FORCED ONE ROW */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
          <div className="text-center p-2 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow" style={{ minWidth: '0' }}>
            <div className="text-lg font-bold text-blue-600">{medicationCount}</div>
            <div className="text-xs text-gray-600">Medications</div>
            {hasMedicationAlerts && (
              <div className="text-xs text-red-600 font-medium mt-1">⚠️ Alerts</div>
            )}
          </div>
          <div className="text-center p-2 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow" style={{ minWidth: '0' }}>
            <div className="text-lg font-bold text-green-600">{assessmentCount}</div>
            <div className="text-xs text-gray-600">Assessments</div>
            {hasOverdueAssessments && (
              <div className="text-xs text-red-600 font-medium mt-1">⚠️ Overdue</div>
            )}
          </div>
          <div className="text-center p-2 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow" style={{ minWidth: '0' }}>
            <div className="text-lg font-bold text-purple-600">{appointmentCount}</div>
            <div className="text-xs text-gray-600">Appointments</div>
            {hasUpcomingAppointments && (
              <div className="text-xs text-blue-600 font-medium mt-1">📅 Upcoming</div>
            )}
          </div>
          <div className="text-center p-2 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow" style={{ minWidth: '0' }}>
            <div className="text-lg font-bold text-orange-600">{documentCount}</div>
            <div className="text-xs text-gray-600">Documents</div>
          </div>
        </div>

        {/* Critical Alerts Section - Enhanced */}
        {hasCriticalAlerts && (
          <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-bold text-red-800">Critical Compliance Alerts</span>
            </div>
            <div className="space-y-1">
              {!isNDISParticipant && hasActiveSIRS && (
                <Tooltip content="Serious Incident Response Scheme — mandatory reporting to the Aged Care Quality and Safety Commission" position="right">
                  <div className="text-xs text-red-700 font-semibold cursor-help">
                    ⚠️ Active SIRS Investigation ({residentSIRS.filter(s => s.status === 'Under Investigation').length})
                  </div>
                </Tooltip>
              )}
              {!isNDISParticipant && !isCareMinutesCompliant && careMinutesToday && (
                <Tooltip content="Government-mandated minimum care minutes not met — review staffing allocation" position="right">
                  <div className="text-xs text-red-700 font-semibold cursor-help">
                    ⚠️ Care Minutes Non-Compliant (Gap: {careMinutesToday.complianceGap} min, RN Gap: {careMinutesToday.rnComplianceGap} min)
                  </div>
                </Tooltip>
              )}
              {residentQualityIncidents.length > 0 && (
                <div className="text-xs text-red-700 font-semibold">
                  ⚠️ Quality Incidents ({residentQualityIncidents.length})
                </div>
              )}
            </div>
          </div>
        )}

        {/* SIRS Status - Only for Aged Care */}
        {!isNDISParticipant && residentSIRS.length > 0 && (
          <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <Tooltip content="Serious Incident Response Scheme — mandatory reporting of serious incidents to the Aged Care Quality and Safety Commission" position="right">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-orange-600" />
                  <span className="text-xs font-semibold text-orange-800">SIRS Incidents</span>
                </div>
              </Tooltip>
              <span className="text-xs font-bold text-orange-600">{residentSIRS.length}</span>
            </div>
            {residentSIRS.slice(0, 1).map(incident => (
              <div key={incident.id} className="text-xs text-orange-700">
                {incident.incidentType} - {incident.status}
                {incident.daysRemaining !== null && incident.daysRemaining >= 0 && (
                  <span className="ml-2">({incident.daysRemaining} days left)</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Primary Diagnosis from Profile Data */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">Primary Diagnosis</p>
          <div className="flex flex-wrap gap-1">
            {profileData.medicalHistory?.primaryDiagnoses?.slice(0, 2).map((condition, idx) => (
              <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                {condition.condition}
              </span>
            ))}
            {profileData.medicalHistory?.primaryDiagnoses?.length > 2 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                +{profileData.medicalHistory.primaryDiagnoses.length - 2}
              </span>
            )}
          </div>
        </div>

        {/* Alerts and Warnings Section */}
        {(hasHighRiskMedications || hasOverdueAssessments || hasMedicationAlerts) && !hasCriticalAlerts && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">Alerts & Warnings</span>
            </div>
            <div className="space-y-1">
              {hasHighRiskMedications && (
                <div className="text-xs text-yellow-700">⚠️ High-risk medication interactions</div>
              )}
              {hasOverdueAssessments && (
                <div className="text-xs text-yellow-700">⚠️ Overdue assessments</div>
              )}
              {hasMedicationAlerts && (
                <div className="text-xs text-yellow-700">⚠️ Medication alerts</div>
              )}
            </div>
          </div>
        )}

        {/* Fall Risk */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">Fall Risk</p>
          {!isNDISParticipant && (
            <Tooltip content="Falls risk assessment rating — determines supervision level and environmental precautions" position="top">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getFallRiskColor(resident.fallRisk || 'Low')}`}>
                {resident.fallRisk || 'Low'} Risk
              </span>
            </Tooltip>
          )}
          {isNDISParticipant && resident.disability && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {resident.disability}
            </span>
          )}
        </div>

        {/* Care Plan Review Status */}
        {profileData.carePlan?.reviewDate && new Date(profileData.carePlan.reviewDate) < new Date() && (
          <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-xs font-medium text-yellow-800">Care Plans - Need review</span>
            </div>
          </div>
        )}

        {/* Emergency Contact */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">Emergency Contact</p>
          <p className="text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-200">
            {profileData.personalInfo?.emergencyContact?.name || 'N/A'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-auto">
          <button 
            onClick={() => handleSelectResident(resident)}
            className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            View Profile
          </button>
          <Tooltip content="Open resident profile for editing" position="top">
            <button onClick={() => handleSelectResident(resident)} className="p-2 bg-green-100 text-green-600 rounded-xl hover:bg-green-200 transition-colors">
              <Edit3 className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip content="Discharge resident from facility" position="top">
            <button onClick={() => { setDischargeTarget(resident); setShowDischargeModal(true); }} className="p-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>

        {/* Hover Effect Overlay */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 to-purple-500/0 group-hover:from-blue-500/5 group-hover:to-purple-500/5 transition-all duration-300 pointer-events-none"></div>
      </div>
    );
  };

  const renderResidentRow = (resident) => {
    const isNDISParticipant = !!resident.ndisParticipantId;
    
    return (
      <div key={resident.id} className="bg-white rounded-lg shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-800">{resident.name}</h3>
              <p className="text-xs text-gray-600">{resident.room} • Age {resident.age}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isNDISParticipant && resident.ndisParticipantId && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {resident.ndisParticipantId}
              </span>
            )}
            {!isNDISParticipant && (
              <Tooltip content="Assessed care complexity level for staffing and funding" position="bottom">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCareLevelColor(resident.careLevel || 'Medium')}`}>
                  {resident.careLevel || 'Medium'}
                </span>
              </Tooltip>
            )}
            {isNDISParticipant && resident.ndisParticipantId && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {resident.ndisParticipantId}
              </span>
            )}
            {!isNDISParticipant && (
              <Tooltip content="Falls risk assessment rating — determines supervision and precautions" position="bottom">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFallRiskColor(resident.fallRisk || 'Low')}`}>
                  {resident.fallRisk || 'Low'}
                </span>
              </Tooltip>
            )}
            {isNDISParticipant && resident.disability && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                {resident.disability}
              </span>
            )}
            {!isNDISParticipant && (
              <Tooltip content="Mini-Mental State Examination score" position="bottom">
                <span className="text-xs text-gray-600">MMSE: {resident.mmseScore || 'N/A'}</span>
              </Tooltip>
            )}
            <Tooltip content="View full resident profile" position="left">
              <button 
                onClick={() => handleSelectResident(resident)}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              >
                <Eye className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  const renderResidentsList = () => (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search residents by name, room, or condition..."
            value={searchQuery}
            onChange={(e) => actions.setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm"
          />
        </div>
        <select
          value={selectedWard}
          onChange={(e) => actions.setSelectedWard(e.target.value)}
            className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm font-medium"
        >
          <option value="all">All Wards</option>
          <option value="ward_a">Ward A</option>
          <option value="ward_b">Ward B</option>
          <option value="ward_c">Ward C</option>
        </select>
        <div className="flex space-x-2">
          <Tooltip content="Grid view — card-based layout" position="bottom">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-3 rounded-xl transition-all duration-300 ${
                viewMode === 'grid' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                  : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-blue-300'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
          </Tooltip>
          <Tooltip content="List view — compact table layout" position="bottom">
            <button
              onClick={() => setViewMode('list')}
              className={`p-3 rounded-xl transition-all duration-300 ${
                viewMode === 'list' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                  : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-blue-300'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </Tooltip>
          </div>
        </div>
      </div>

      {/* Residents Display */}
      {isLoading ? (
        <TableSkeleton rows={8} columns={6} />
      ) : residents.length === 0 && (searchQuery || selectedWard !== 'all') ? (
        <EmptyState
          icon={Search}
          title="No residents found"
          description="Try adjusting your search or filters"
        />
      ) : residents.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No residents yet"
          description="Add your first resident to get started"
          action={
            <button
              onClick={() => setShowAddResidentModal(true)}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Resident
            </button>
          }
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {residents.map((resident, index) => renderResidentCard(resident, index))}
        </div>
      ) : (
        <div className="space-y-2">
          {residents.map(renderResidentRow)}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Heart className="w-8 h-8" />
                {isNDIS ? 'Participant Management' : 'Resident Management'}
              </h1>
              <p className="text-blue-100 text-base mt-2">
                {isNDIS ? 'Comprehensive NDIS participant profiles and support management' : 'Comprehensive resident profiles and care management'}
              </p>
              {facilityConfig && (
                <div className="mt-2 flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    isNDIS ? 'bg-green-200 text-green-800' : 
                    isAgedCare ? 'bg-purple-200 text-purple-800' : 
                    'bg-gray-200 text-gray-800'
                  }`}>
                    <Tag className="w-3 h-3 inline mr-1" />
                    {isNDIS ? 'NDIS' : isAgedCare ? 'Aged Care' : 'General'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAddResidentModal(true)}
              className="px-4 py-2 bg-white text-blue-700 rounded-xl hover:bg-blue-50 font-semibold shadow flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add {isNDIS ? 'Participant' : 'Resident'}
            </button>
            <div className="text-right">
              <div className="text-2xl font-bold">{residents.length}</div>
              <div className="text-blue-100 text-sm">
                Active {isNDIS ? 'Participants' : 'Residents'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {selectedResident ? (
        profileLoading ? (
          <ResidentProfileSkeleton />
        ) : (
          <EnhancedResidentProfile
            resident={selectedResident}
            actions={actions}
            onClose={() => setSelectedResident(null)}
          />
        )
      ) : (
        renderResidentsList()
      )}

      {/* Add Resident / Admission Modal */}
      {showAddResidentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl flex justify-between items-center">
              <h2 className="text-xl font-bold">New Resident Admission</h2>
              <button onClick={() => setShowAddResidentModal(false)} className="text-white hover:text-gray-200"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label><input type="text" value={addResidentForm.name} onChange={(e) => setAddResidentForm(p => ({...p, name: e.target.value}))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="First Last" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label><input type="date" value={addResidentForm.dateOfBirth} onChange={(e) => setAddResidentForm(p => ({...p, dateOfBirth: e.target.value}))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Gender</label><select value={addResidentForm.gender} onChange={(e) => setAddResidentForm(p => ({...p, gender: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="">Select...</option><option value="Female">Female</option><option value="Male">Male</option><option value="Other">Other</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Room *</label><input type="text" value={addResidentForm.room} onChange={(e) => setAddResidentForm(p => ({...p, room: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. A101" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Ward</label><select value={addResidentForm.ward} onChange={(e) => setAddResidentForm(p => ({...p, ward: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="ward_a">Ward A</option><option value="ward_b">Ward B</option><option value="ward_c">Ward C</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Facility</label><select value={addResidentForm.facility} onChange={(e) => setAddResidentForm(p => ({...p, facility: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="sunshine">Sunshine Aged Care</option><option value="riverside">Riverside Aged Care</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Medicare Number</label><input type="text" value={addResidentForm.medicare} onChange={(e) => setAddResidentForm(p => ({...p, medicare: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Care Level</label><select value={addResidentForm.careLevel} onChange={(e) => setAddResidentForm(p => ({...p, careLevel: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option><option value="Critical">Critical</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Funding Type</label><select value={addResidentForm.fundingType} onChange={(e) => setAddResidentForm(p => ({...p, fundingType: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="AN-ACC">AN-ACC</option><option value="NDIS">NDIS</option><option value="Private">Private</option></select></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Allergies (comma-separated)</label><input type="text" value={addResidentForm.allergies} onChange={(e) => setAddResidentForm(p => ({...p, allergies: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Penicillin, Shellfish" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Dietary Restrictions (comma-separated)</label><input type="text" value={addResidentForm.dietaryRestrictions} onChange={(e) => setAddResidentForm(p => ({...p, dietaryRestrictions: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Diabetic Diet, Low Sodium" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name</label><input type="text" value={addResidentForm.emergencyContactName} onChange={(e) => setAddResidentForm(p => ({...p, emergencyContactName: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone</label><input type="text" value={addResidentForm.emergencyContactPhone} onChange={(e) => setAddResidentForm(p => ({...p, emergencyContactPhone: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button onClick={() => setShowAddResidentModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleAddResidentSubmit} disabled={!addResidentForm.name || !addResidentForm.room} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"><Plus className="w-4 h-4" /> Admit Resident</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discharge Confirmation Modal */}
      {showDischargeModal && dischargeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-5 rounded-t-xl">
              <h3 className="text-lg font-bold">Discharge Resident</h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-gray-700">Discharge <strong>{dischargeTarget.name}</strong> from {dischargeTarget.room}?</p>
              <div><label className="block text-sm font-medium mb-1">Reason *</label><textarea value={dischargeReason} onChange={(e) => setDischargeReason(e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows={3} placeholder="Enter discharge reason..." /></div>
              <div><label className="block text-sm font-medium mb-1">Effective Date</label><input type="date" value={dischargeDate} onChange={(e) => setDischargeDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowDischargeModal(false); setDischargeTarget(null); setDischargeReason(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleDischargeResident} disabled={!dischargeReason} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Discharge</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Residents;

